/**
 * mailx client entry point.
 * Wires together all UI components and WebSocket connection.
 */

import { initFolderTree, refreshFolderTree, updateFolderCounts, setFolderSynced, getFolderSynced, setOutboxTotal, highlightFolder } from "./components/folder-tree.js";
import { initMessageList, loadMessages, loadUnifiedInbox, loadSearchResults, reloadCurrentFolder, clearSearchMode, setLiveFilter, getSelectedMessages, markBodiesCached, getCurrentFocused, releaseFocus, removeMessagesAndReconcile, setRowFlagged, scrollFocusedIntoView, refreshPriorityIndex, revealMessage, getCurrentView, selectAllVisible, exitMultiSelect, getDateBasis, setDateBasis } from "./components/message-list.js";
import { seenOf, flaggedOf, draftOf, setSeen, setFlagged } from "@bobfrankston/mailx-types";
import { initTabs, setActiveView as setActiveTabView, openTab, type ViewTab } from "./components/tabs.js";
import { showMessage, getCurrentMessage, initViewer, popOutCurrentMessage, popOutToWindow, printCurrentMessage, toggleFullscreenPreview, showPreviewBodyMenu, wrapHtmlBody } from "./components/message-viewer.js";
import { connectWebSocket, onWsEvent, triggerSync, syncAccount, reauthenticate, getAccounts, getFolders, deleteMessage, deleteMessages, undeleteMessage, restartServer, getSyncPending, getVersion, getSettings, saveSettings, getAutocompleteSettings, saveAutocompleteSettings, repairAccounts, updateFlags, markAsSpamMessages, logClientEvent, sendMessage as apiSendMessage, subscribeStore, cancelServerSearch, installConsoleCapture, getAttachment } from "./lib/api-client.js";
import * as messageState from "./lib/message-state.js";

// FIRST THING. Captures every console.log/warn/error + window errors +
// unhandled promise rejections to the daemon log via logClientEvent, so
// debugging client-side bugs never requires opening devtools — search
// rmfmail-YYYY-MM-DD.log for "console.error" or "window.error" instead.
installConsoleCapture();

// ── Main-thread stall watchdog ──
// Bob 2026-06-22: "trying to scroll does nothing, as if the UI thread is
// locked"; the UI must always be responsive. A blocked event loop is
// otherwise invisible — the symptom shows up in the UI but never names its
// cause. This watchdog re-arms a 1s timer and reports the slip whenever the
// callback fires late by more than a threshold: a 1s timer that fires at
// 1.0s is healthy; one that fires at 4.2s means the main thread was wedged
// ~3.2s (long IPC on the loop, giant synchronous render, parse on main).
// The slip duration + wall-clock pinpoints which interaction blew out.
(function installStallWatchdog(): void {
    const EXPECTED_MS = 1000;
    const STALL_MS = 250; // only report slips that a user would feel
    let last = performance.now();
    const tick = (): void => {
        const now = performance.now();
        const slip = now - last - EXPECTED_MS;
        if (slip > STALL_MS) {
            console.warn(`  [stall] main thread blocked ~${slip.toFixed(0)}ms (event loop frozen — UI was unresponsive this long)`);
        }
        last = now;
        setTimeout(tick, EXPECTED_MS);
    };
    setTimeout(tick, EXPECTED_MS);
})();

// ── New message badge (favicon + title) ──
/** The user-visible app name. Single point of change for the rename;
 *  every UI surface that shows "rmfmail" reads from here. Static HTML
 *  uses placeholder text that `propagateAppName` (called immediately
 *  below) overwrites — so the constant is the only place the literal
 *  string lives. */
export const APP_NAME = "rmfmail";

/** Stamp APP_NAME into every static HTML element that should show it.
 *  Runs synchronously at module load — before paint when imports are
 *  fast, with a sub-frame flash of the placeholder otherwise. Add new
 *  surfaces here, never inline literals. */
function propagateAppName(): void {
    const set = (id: string, fn: (v: string) => string): void => {
        const el = document.getElementById(id);
        if (el) el.textContent = fn(APP_NAME);
    };
    document.title = APP_NAME;
    set("startup-status", n => `Starting ${n}…`);
    set("status-version", n => n);
    set("app-version", n => n);
    // About-button label / restart-button hover-titles live in HTML
    // attributes; rewrite the ones that include the name.
    const aboutBtn = document.getElementById("btn-about");
    if (aboutBtn) aboutBtn.title = `Show version and build info`;
    const aboutText = aboutBtn?.textContent || "";
    if (aboutBtn && aboutText.toLowerCase().includes("about")) {
        aboutBtn.textContent = `About ${APP_NAME}...`;
    }
}
propagateAppName();
(window as any).__btick && (window as any).__btick("app.ts module body executing");

// ── App-vs-browser policy: this is a desktop app, not a web page. ──
// Block browser-default accelerators (Reload, Save Page, Print, View
// Source, history navigation, …) and the right-click context menu so
// mailx's keymap is the only way to do anything. F12 is preserved as
// the developer-tools escape hatch (Bob 2026-05-11 explicitly).
//
// Capture-phase listener so we intercept BEFORE any inner widget can
// observe the event — mailx's own keymap re-emits via specific element
// handlers (Ctrl+N compose, Ctrl+R reply, etc.) which were never the
// browser's accelerators in the first place.
(function blockBrowserKeysAndMenu() {
    const isBlockedKey = (e: KeyboardEvent): boolean => {
        // Always-allow: F12 for DevTools, and any single non-modifier key
        // (typing, arrow nav, Tab/Esc/Enter) which is the user typing.
        if (e.key === "F12") return false;
        // Block reload paths: F5, Ctrl+R, Ctrl+Shift+R, Ctrl+F5.
        if (e.key === "F5" || e.key === "F3") return true;
        if ((e.ctrlKey || e.metaKey) && (e.key === "r" || e.key === "R")) return true;
        // Browser accelerators bound to Ctrl-letter combos that mailx
        // doesn't want to forward: S (save page), U (view source),
        // J (downloads), L (focus address bar — n/a in WebView but
        // harmless), G (find next). Note: O is "open file" in browsers —
        // mailx uses Ctrl+O nowhere meaningful, so block. P is NOT here:
        // mailx's own Ctrl+P handler prints the focused letter and
        // preventDefaults the browser dialog itself — listing it here
        // stopPropagation'd the event before that handler could run, which
        // is why the print shortcut was dead (Bob 2026-07-30).
        if (e.ctrlKey || e.metaKey) {
            const k = e.key.toLowerCase();
            if (["s", "u", "j", "l", "g", "o"].includes(k)) return true;
        }
        // Alt+Left / Alt+Right = browser back / forward. No use in mailx.
        if (e.altKey && (e.key === "ArrowLeft" || e.key === "ArrowRight")) return true;
        // Backspace as nav-back when no input is focused — fires on some
        // WebView builds. Only block when target isn't text-editable.
        if (e.key === "Backspace") {
            const t = e.target as HTMLElement | null;
            const editable = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA"
                || (t as any).isContentEditable);
            if (!editable) return true;
        }
        return false;
    };
    document.addEventListener("keydown", (e) => {
        if (isBlockedKey(e)) {
            e.preventDefault();
            e.stopPropagation();
        }
    }, true);
    // Right-click context menu — mailx has its own (showContextMenu) for
    // specific surfaces. Default WebView menu is "Reload / Save As /
    // View Source / Inspect" which doesn't belong in a desktop app.
    // Individual handlers that DO want a context menu must
    // `e.preventDefault()` themselves AFTER showing — this only
    // suppresses the default browser one when nothing else handles.
    document.addEventListener("contextmenu", (e) => {
        // Allow contextmenu to bubble normally so mailx-internal handlers
        // (folder-tree right-click, address-pill right-click, link
        // right-click in preview) can react. We only kill it if it would
        // otherwise show the browser default — i.e., no other handler
        // called preventDefault.
        if (!e.defaultPrevented) e.preventDefault();
    });
})();

let baseTitle = APP_NAME;
let lastSeenCount = 0;
let badgeCount = 0;

function updateBadge(count: number): void {
    badgeCount = count;
    // Update title
    document.title = count > 0 ? `(${count}) ${baseTitle}` : baseTitle;
    // Generate a single badge bitmap used for both the favicon (visible on
    // browser tabs / mobile homescreen) AND the Windows taskbar overlay
    // icon (visible as a Thunderbird-style corner pill on the taskbar
    // button when running via msger). Rendered once, consumed twice.
    const canvas = document.createElement("canvas");
    canvas.width = 32;
    canvas.height = 32;
    const ctx = canvas.getContext("2d")!;
    // Base envelope icon (always drawn — so the favicon is a recognizable
    // mailx icon even at 0 count).
    ctx.fillStyle = "#4a7ccc";
    ctx.fillRect(2, 8, 28, 20);
    ctx.fillStyle = "#6a9cec";
    ctx.beginPath();
    ctx.moveTo(2, 8);
    ctx.lineTo(16, 20);
    ctx.lineTo(30, 8);
    ctx.fill();
    if (count > 0) {
        // Red badge circle with count
        ctx.fillStyle = "#e33";
        ctx.beginPath();
        ctx.arc(24, 8, 8, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#fff";
        ctx.font = "bold 11px sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(count > 99 ? "99+" : String(count), 24, 8);
    }
    // Set as favicon
    let link = document.querySelector("link[rel='icon']") as HTMLLinkElement;
    if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
    }
    const dataUrl = canvas.toDataURL("image/png");
    link.href = dataUrl;

    // Also push to the Windows taskbar overlay via msger's IPC helper —
    // no-op on Linux/Mac. For count=0, clear the overlay so the base icon
    // shows cleanly.
    try {
        // The injected host bridge is `window.mailxapi`, NOT `window.msgapi`.
        // The old `msgapi` reference was always undefined, so the taskbar
        // overlay never fired — the count badge silently did nothing on the
        // taskbar for the whole life of this feature (Bob 2026-05-28 "are you
        // able to show the number of new messages").
        const hostApi: any = (window as any).mailxapi;
        if (hostApi?.setTaskbarOverlay) {
            if (count > 0) {
                // BADGE-ONLY overlay. SetOverlayIcon composites the supplied
                // icon onto the bottom-right corner of the taskbar button —
                // the base envelope icon is already there. Sending the full
                // envelope+badge canvas (favicon) drew a SECOND tiny envelope
                // in the corner on top of the big one (Bob 2026-05-29). Render
                // just the red count pill on a transparent field so only the
                // number is overlaid. Filled circle spans most of the 32×32 so
                // it stays legible after the corner downscale.
                const bc = document.createElement("canvas");
                bc.width = 32; bc.height = 32;
                const bx = bc.getContext("2d")!;
                bx.fillStyle = "#e33";
                bx.beginPath();
                bx.arc(16, 16, 15, 0, Math.PI * 2);
                bx.fill();
                bx.fillStyle = "#fff";
                bx.font = "bold 20px sans-serif";
                bx.textAlign = "center";
                bx.textBaseline = "middle";
                bx.fillText(count > 99 ? "99+" : String(count), 16, 17);
                const b64 = bc.toDataURL("image/png").split(",")[1] || "";
                hostApi.setTaskbarOverlay(b64, `${count} unread`);
            } else {
                hostApi.setTaskbarOverlay("", "");
            }
        }
    } catch { /* host bridge unavailable in browser fallback */ }
}

async function updateNewMessageCount(): Promise<void> {
    try {
        const accounts = await getAccounts();
        // Fan out folder queries in parallel — earlier code awaited each
        // account's `getFolders` in series, so an N-account setup paid N
        // back-to-back IPC round-trips on every count refresh (folderCountsChanged,
        // sync events, IDLE updates).
        const folderLists = await Promise.all(
            accounts.map((acct: any) => getFolders(acct.id).catch(() => [] as any[])),
        );
        let totalUnread = 0;
        for (const folders of folderLists) {
            const inbox = folders.find((f: any) => f.specialUse === "inbox");
            if (inbox) totalUnread += inbox.unreadCount || 0;
        }
        // Rail badge: unread count on the Inbox and Unified-inbox rail buttons.
        // Visible even when those views aren't the active one — part of C33
        // "rail icon badges for unread counts."
        updateRailBadge("rail-inbox", totalUnread);
        updateRailBadge("rail-unified", totalUnread);
        // First load: set baseline
        if (lastSeenCount === 0) { lastSeenCount = totalUnread; updateBadge(0); return; }
        const previousBadge = badgeCount;
        // New messages = increase since last seen
        const newCount = Math.max(0, totalUnread - lastSeenCount);
        updateBadge(newCount);
        // Flash the title when new mail arrives and the window isn't focused.
        // Windows' taskbar mirrors document.title so this acts as a taskbar flash.
        if (newCount > previousBadge && document.visibilityState !== "visible") {
            startTitleFlash();
        }
    } catch { /* offline */ }
}

function updateRailBadge(buttonId: string, count: number): void {
    const btn = document.getElementById(buttonId);
    if (!btn) return;
    let badge = btn.querySelector<HTMLElement>(".rail-badge");
    if (count <= 0) {
        if (badge) badge.remove();
        return;
    }
    if (!badge) {
        badge = document.createElement("span");
        badge.className = "rail-badge";
        btn.appendChild(badge);
    }
    badge.textContent = count > 999 ? "999+" : String(count);
}

// ── Taskbar flash via title alternation ──
let titleFlashTimer: ReturnType<typeof setInterval> | null = null;
let titleFlashPhase = false;

function startTitleFlash(): void {
    stopTitleFlash();
    titleFlashPhase = true;
    titleFlashTimer = setInterval(() => {
        titleFlashPhase = !titleFlashPhase;
        if (titleFlashPhase) {
            document.title = `✉ NEW MAIL (${badgeCount})`;
        } else {
            document.title = badgeCount > 0 ? `(${badgeCount}) ${baseTitle}` : baseTitle;
        }
    }, 1000);
}

function stopTitleFlash(): void {
    if (titleFlashTimer) { clearInterval(titleFlashTimer); titleFlashTimer = null; }
    document.title = badgeCount > 0 ? `(${badgeCount}) ${baseTitle}` : baseTitle;
}

document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") stopTitleFlash();
});
window.addEventListener("focus", stopTitleFlash);

/** Call when user actively views messages — resets the badge */
function markAsSeen(): void {
    getAccounts().then(async (accounts: any[]) => {
        // Parallel folder fetch — see updateNewMessageCount for rationale.
        const folderLists = await Promise.all(
            accounts.map((acct: any) => getFolders(acct.id).catch(() => [] as any[])),
        );
        let total = 0;
        for (const folders of folderLists) {
            const inbox = folders.find((f: any) => f.specialUse === "inbox");
            if (inbox) total += inbox.unreadCount || 0;
        }
        lastSeenCount = total;
        updateBadge(0);
    }).catch(() => {});
}

function setTitle(title: string): void {
    baseTitle = title;
    document.title = badgeCount > 0 ? `(${badgeCount}) ${baseTitle}` : baseTitle;
}

// ── Alert banner ──
const alertBanner = document.getElementById("alert-banner");
const alertText = document.getElementById("alert-text");
const alertDismiss = document.getElementById("alert-dismiss");
const dismissedAlerts = new Set<string>();

let alertAutoDismissTimer: ReturnType<typeof setTimeout> | null = null;
function showAlert(message: string, key?: string, opts?: { sticky?: boolean }): void {
    if (key && dismissedAlerts.has(key)) return;
    if (alertBanner && alertText) {
        alertText.textContent = message;
        alertBanner.hidden = false;
        alertBanner.dataset.key = key || "";
        // Q65: auto-dismiss non-critical banners after 30s; sticky ones
        // (acct-*, ws-error, config-restart) keep showing until user acts.
        if (alertAutoDismissTimer) { clearTimeout(alertAutoDismissTimer); alertAutoDismissTimer = null; }
        const isCritical = !!opts?.sticky
            || (key?.startsWith("acct-"))
            || key === "ws-error"
            || key === "config-restart";
        if (!isCritical) {
            alertAutoDismissTimer = setTimeout(() => {
                if (alertBanner && alertBanner.dataset.key === (key || "")) {
                    alertBanner.hidden = true;
                }
                alertAutoDismissTimer = null;
            }, 30_000);
        }
    }
}

// Non-blocking alert channel for components that can't import showAlert
// directly (message-viewer, etc.). They dispatch a `mailx-alert` CustomEvent
// rather than calling the blocking window.alert().
window.addEventListener("mailx-alert", (e: Event) => {
    const d = (e as CustomEvent).detail || {};
    if (d.message) showAlert(String(d.message), d.key);
});

function hideAlert(opts?: { force?: boolean }): void {
    if (alertBanner) {
        // The "update available" notice is sticky: routine "clear the
        // transient banner" calls (after a successful sync, etc.) must not
        // wipe it — otherwise the next sync, seconds later, hides the
        // update banner and the user never sees it. Only the explicit X
        // (force) clears it.
        if (!opts?.force && alertBanner.dataset.key === "update-available") return;
        const key = alertBanner.dataset.key;
        if (key) dismissedAlerts.add(key);
        alertBanner.hidden = true;
    }
}

alertDismiss?.addEventListener("click", () => hideAlert({ force: true }));

/** Show the alert banner with a "Restart" button wired to the mailxapi
 *  restartDaemon action. Used when a watched config file whose changes
 *  don't apply live (accounts.jsonc) has been modified. */
function showRestartForConfigBanner(): void {
    if (!alertBanner || !alertText) return;
    // Timestamp in the banner so repeated / spurious fires are visually
    // distinguishable (and the user can see when the change actually
    // happened, useful for debugging false triggers).
    const ts = new Date().toLocaleTimeString([], { hour12: false });
    alertText.textContent = `[${ts}] accounts.jsonc changed — restart to apply.`;
    alertBanner.hidden = false;
    alertBanner.dataset.key = "config-restart";
    // Avoid duplicate buttons across repeat changes.
    const existing = alertBanner.querySelector("#alert-restart-btn");
    if (existing) return;
    const btn = document.createElement("button");
    btn.id = "alert-restart-btn";
    btn.textContent = "Restart now";
    btn.style.cssText = "margin-left: 12px; padding: 3px 12px; cursor: pointer;";
    btn.addEventListener("click", async () => {
        btn.disabled = true;
        btn.textContent = "Restarting…";
        try {
            const ipc: any = (window as any).mailxapi;
            if (ipc?.restartDaemon) {
                await ipc.restartDaemon();
                // Service is going down; the WebView should reload shortly
                // when the replacement daemon takes over. Force a reload
                // after a short delay in case the event doesn't arrive.
                setTimeout(() => location.reload(), 2000);
            } else {
                // Non-IPC (server/browser mode) — location reload won't
                // restart the daemon but at least gives the user feedback.
                location.reload();
            }
        } catch (e: any) {
            btn.textContent = `Failed: ${e?.message || e}`;
            btn.disabled = false;
        }
    });
    alertText.after(btn);
}

// ── Wire up components ──

const folderTree = document.getElementById("folder-tree")!;
let currentFolderSpecialUse = "";

// Selection / preview drift is now structurally impossible: the list owns
// focus, the viewer is a passive renderer, and `releaseFocus()` is the
// only way to deselect+clear. The old `mailx-clear-viewer` event and the
// app-level clearViewer() shim are no longer needed.

const folderTitleEl = document.getElementById("ml-folder-title");
let currentFolderName = "";
let currentFolderSyncedAt: number | undefined;

function formatAge(ms: number): string {
    const s = Math.round(ms / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.round(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.round(h / 24)}d ago`;
}

function renderNarrowFolderTitle(): void {
    if (!folderTitleEl) return;
    if (currentFolderSyncedAt) {
        const age = formatAge(Date.now() - currentFolderSyncedAt);
        folderTitleEl.innerHTML = `${currentFolderName}<span class="ml-folder-age"> · ${age}</span>`;
        folderTitleEl.title = `Last synced ${new Date(currentFolderSyncedAt).toLocaleTimeString()}`;
    } else {
        folderTitleEl.textContent = currentFolderName;
        folderTitleEl.title = "";
    }
}

function setNarrowFolderTitle(name: string): void {
    currentFolderName = name;
    currentFolderSyncedAt = getFolderSynced(currentAccountId, currentFolderId);
    renderNarrowFolderTitle();
}

// Tick the "3m ago" text every 30s so it stays truthful without flooding repaints.
setInterval(() => {
    if (currentFolderSyncedAt) renderNarrowFolderTitle();
}, 30_000);

initFolderTree(folderTree, (accountId, folderId, folderName, specialUse) => {
    currentFolderSpecialUse = specialUse;
    currentAccountId = accountId;
    currentFolderId = folderId;
    // Drop search state on folder switch — input alone wasn't enough,
    // searchMode stayed true and the next loadMessages was rerouted.
    if (searchInput) { searchInput.value = ""; updateSearchHighlight(); }
    clearSearchMode();
    // Navigating to a folder / All Inboxes ALSO abandons search — kill any
    // in-flight server sweep so it stops starving the read worker (Bob 2026-06-19).
    cancelServerSearch();
    markAsSeen();
    releaseFocus();
    loadMessages(accountId, folderId, 1, specialUse);
    // Lazy-folder-sync: opening a folder fetches its latest from the server
    // (fire-and-forget). The full all-folders sweep now runs only every 30
    // min, so on-open sync is what keeps a folder you're actually looking at
    // fresh. The folderSynced event refreshes the list when it lands. INBOX
    // is excluded — IDLE + the 5-min quick check already keep it live, and
    // re-syncing it on every visit is wasted work.
    if (specialUse !== "inbox") {
        import("./lib/api-client.js").then(m => m.syncFolderNow?.(accountId, folderId)).catch(() => { /* */ });
    }
    setTitle(`${APP_NAME} - ${folderName}`);
    setNarrowFolderTitle(folderName);
    // Record the navigation in the active tab so a later tab-switch restores
    // this folder. Folder navigation happens IN the current tab.
    setActiveTabView({ kind: "folder", accountId, folderId, specialUse }, folderName);
    document.dispatchEvent(new CustomEvent("mailx-folder-changed", { detail: { accountId, folderId } }));
}, () => {
    // Unified inbox handler
    currentFolderSpecialUse = "inbox";
    // Clear search state — switching folders should drop any active filter.
    // Pre-fix: the search input retained its old value AND search mode
    // stayed active, so "All Inboxes" appeared to be ignoring its own
    // contents (Bob 2026-05-09: "I just switched to all inboxes but the
    // search is still showing even though all the entries are there.").
    if (searchInput) { searchInput.value = ""; updateSearchHighlight(); }
    clearSearchMode();
    // Navigating to a folder / All Inboxes ALSO abandons search — kill any
    // in-flight server sweep so it stops starving the read worker (Bob 2026-06-19).
    cancelServerSearch();
    releaseFocus();
    loadUnifiedInbox();
    setTitle(`${APP_NAME} - All Inboxes`);
    setNarrowFolderTitle("All Inboxes");
    setActiveTabView({ kind: "unified" }, "All Inboxes");
});

// ── View tabs (docs/multi-view.md) ──────────────────────────────────────
// `applyTabView` loads a tab's view into the single three-pane when the user
// switches tabs. It mirrors the folder-tree handlers above but must NOT call
// setActiveTabView (that would be a no-op update, but keeping them separate
// keeps "user navigated" distinct from "tab restored").
function applyTabView(tab: ViewTab): void {
    if (searchInput) { searchInput.value = ""; updateSearchHighlight(); }
    clearSearchMode();
    // Navigating to a folder / All Inboxes ALSO abandons search — kill any
    // in-flight server sweep so it stops starving the read worker (Bob 2026-06-19).
    cancelServerSearch();
    releaseFocus();
    const v = tab.view;
    if (v.kind === "unified") {
        currentFolderSpecialUse = "inbox";
        loadUnifiedInbox();
        setTitle(`${APP_NAME} - All Inboxes`);
        setNarrowFolderTitle("All Inboxes");
    } else if (v.kind === "folder") {
        currentFolderSpecialUse = v.specialUse;
        currentAccountId = v.accountId;
        currentFolderId = v.folderId;
        loadMessages(v.accountId, v.folderId, 1, v.specialUse);
        setTitle(`${APP_NAME} - ${tab.title}`);
        setNarrowFolderTitle(tab.title);
    } else {
        if (searchInput) { searchInput.value = v.query; updateSearchHighlight(); }
        loadSearchResults(v.query, v.scope, v.accountId, v.folderId, v.includeTrash);
        setTitle(`${APP_NAME} - Search`);
        setNarrowFolderTitle(`Search: ${v.query}`);
    }
}
const tabStripEl = document.getElementById("view-tab-strip");
if (tabStripEl) initTabs(tabStripEl, applyTabView);

initMessageList((_accountId, _uid, _folderId) => {
    // The list row's setFocus() already drove the viewer with its own
    // envelope (header + preview paint synchronously, body fetches in
    // background). Calling showMessage here a SECOND time without the
    // envelope wiped the good render and forced a bare "Loading…"
    // placeholder until the body IPC returned — making every click feel
    // like a network round-trip. This callback now only handles the
    // narrow-screen layout switch.
    if (window.innerWidth <= 768) {
        document.getElementById("message-viewer")?.classList.add("narrow-active");
        document.getElementById("message-list")?.classList.add("narrow-hidden");
        // Selecting a message means the user is done with the rail / folder
        // drawers — auto-dismiss either if left open. Without this the rail
        // floats over the message body (the "rail on top of the letter" bug).
        document.querySelector(".icon-rail")?.classList.remove("open");
        document.querySelector(".folder-panel")?.classList.remove("open");
    }
});
initViewer();
(window as any).__btick && (window as any).__btick("init* done — first interactive moment");
requestAnimationFrame(() => (window as any).__btick && (window as any).__btick("first rAF after init"));

// Status bar: show focused message UID/folder for debugging. The list
// fires mailx-focus-changed with the focused envelope (or null). Pure
// observation — this listener never drives the viewer.
document.addEventListener("mailx-focus-changed", (e: any) => {
    const acctEl = document.getElementById("status-accounts");
    if (!acctEl) return;
    const sel = e.detail as { accountId: string; uid: number; folderId: number } | null;
    if (sel) {
        acctEl.textContent = `${sel.accountId}/uid:${sel.uid} folder:${sel.folderId}`;
        acctEl.style.color = "";
    } else {
        acctEl.textContent = "";
    }
});

// Q53: per-account last-sync timestamps surfaced via the status-sync hover.
const lastSyncByAccount: Record<string, number> = {};
function recordAccountSync(accountId: string): void {
    lastSyncByAccount[accountId] = Date.now();
    refreshSyncTooltip();
}
function refreshSyncTooltip(): void {
    const el = document.getElementById("status-sync");
    if (!el) return;
    const accts = Object.keys(lastSyncByAccount).sort();
    if (accts.length === 0) { el.title = ""; return; }
    el.title = "Last sync:\n" + accts.map(a => {
        const ts = lastSyncByAccount[a];
        const d = new Date(ts);
        return `  ${a}: ${d.toLocaleTimeString()}  (${formatAge(Date.now() - ts)})`;
    }).join("\n");
}
// Refresh the tooltip every 30s so the "(12m ago)" stays current even with
// no new sync events.
setInterval(refreshSyncTooltip, 30_000);

// Honest "Synced" indicator. "Synced HH:MM" must mean sync is IDLE, not fire
// on every tick — folderCountsChanged/syncProgress fire repeatedly during a
// catch-up backfill (e.g. after the daemon was offline) while messages are
// STILL arriving, so stamping "Synced" each time misled (Bob 2026-06-06: "it
// says synced but I don't see this afternoon's mail" — they were mid-backfill).
// Any sync activity (re)arms this settle timer; only when activity goes quiet
// for SYNC_SETTLE_MS do we stamp the time. While the storm runs, the handlers
// keep the bar on "Syncing…".
let syncSettleTimer: ReturnType<typeof setTimeout> | null = null;
const SYNC_SETTLE_MS = 4000;
function scheduleSyncedStamp(): void {
    if (syncSettleTimer) clearTimeout(syncSettleTimer);
    syncSettleTimer = setTimeout(() => {
        syncSettleTimer = null;
        const el = document.getElementById("status-sync");
        // Only flip a "Syncing…" display to "Synced" — never clobber an error
        // ("Sync error: …") or a transient action message ("Trashed 1 …").
        if (el && (el.textContent || "").startsWith("Syncing")) {
            el.textContent = `Synced ${new Date().toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false })}`;
        }
    }, SYNC_SETTLE_MS);
}

// C121: connect-phase ticker. The daemon emits connectProgress per IMAP
// connection attempt (socket → handshake → done/failed). While any attempt
// has been in flight longer than CONNECT_SHOW_AFTER_MS, the status bar shows
// "Connecting to <host> (<phase> <purpose>, 3.2s)" with a live elapsed
// counter — so a stuck DNS/TLS/greeting/LOGIN is distinguishable at a glance
// instead of an opaque frozen "Syncing…". Healthy sub-second connects never
// surface (the delay gate kills the flicker).
const activeConnects = new Map<string, { host: string; purpose: string; phase: string; startedAt: number }>();
const CONNECT_SHOW_AFTER_MS = 1500;
let connectTickTimer: ReturnType<typeof setInterval> | null = null;
function handleConnectProgress(event: any): void {
    const key = `${event.accountId}|${event.purpose}`;
    if (event.phase === "done" || event.phase === "failed") {
        activeConnects.delete(key);
        if (activeConnects.size === 0 && connectTickTimer) {
            clearInterval(connectTickTimer);
            connectTickTimer = null;
            // Leave the bar to the normal sync handlers; stamp settle so a
            // lingering "Connecting…" flips to Synced when things go quiet.
            scheduleSyncedStamp();
        }
        return;
    }
    const existing = activeConnects.get(key);
    if (existing) {
        existing.phase = event.phase;
    } else {
        activeConnects.set(key, {
            host: event.host || event.accountId,
            purpose: event.purpose || "",
            phase: event.phase,
            startedAt: Date.now() - (event.elapsedMs || 0),
        });
    }
    if (!connectTickTimer) connectTickTimer = setInterval(renderConnectTick, 100);
}
function renderConnectTick(): void {
    const el = document.getElementById("status-sync");
    if (!el || activeConnects.size === 0) return;
    // Staleness guard: if a done/failed event got lost (daemon restart,
    // dropped IPC line) an entry would tick forever — expire at 10 min,
    // well past any transport timeout.
    const now = Date.now();
    for (const [k, c] of activeConnects) {
        if (now - c.startedAt > 600_000) activeConnects.delete(k);
    }
    // Show the LONGEST-waiting attempt — that's the one worth staring at.
    let worst: { host: string; purpose: string; phase: string; startedAt: number } | null = null;
    for (const c of activeConnects.values()) {
        if (!worst || c.startedAt < worst.startedAt) worst = c;
    }
    if (!worst) return;
    const elapsed = Date.now() - worst.startedAt;
    if (elapsed < CONNECT_SHOW_AFTER_MS) return;
    el.textContent = `Connecting to ${worst.host} (${worst.phase}${worst.purpose ? " " + worst.purpose : ""}, ${(elapsed / 1000).toFixed(1)}s)`;
}

// ── Auto two-line when message list is narrow ──
const messageList = document.getElementById("message-list");
if (messageList) {
    const twoLineThreshold = 600; // px — switch to two-line below this width
    const userTwoLine = localStorage.getItem("mailx-two-line") === "true";
    new ResizeObserver(([entry]) => {
        const narrow = entry.contentRect.width < twoLineThreshold;
        // Auto two-line when narrow, respect user preference when wide
        if (narrow) {
            messageList.classList.add("two-line");
        } else if (!userTwoLine) {
            messageList.classList.remove("two-line");
        }
    }).observe(messageList);
}

// ── Narrow/medium drawer toggles ──
// Hamburger (☰): rail drawer on narrow; on wider tiers the rail is already
// visible so this is a no-op visually (the toggle still fires but the rail
// has no `.open` style to invoke).
// Folder (📁): folder-panel drawer on any tier where it's positioned as an
// overlay (medium + narrow).
document.getElementById("btn-menu")?.addEventListener("click", () => {
    document.querySelector(".icon-rail")?.classList.toggle("open");
    // Rail drawer and folder drawer are mutually exclusive — opening one
    // closes the other so they don't fight for the left edge.
    document.querySelector(".folder-panel")?.classList.remove("open");
});
document.getElementById("btn-folder-toggle")?.addEventListener("click", () => {
    document.querySelector(".folder-panel")?.classList.toggle("open");
    document.querySelector(".icon-rail")?.classList.remove("open");
});

const backToList = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    // If user is in full-screen-viewer mode, the first back tap should exit
    // full-screen and return to the normal narrow split (list + active
    // viewer). It shouldn't also deselect — that would yank the user out two
    // levels in one tap.
    if (document.body.classList.contains("viewer-fullscreen")) {
        document.body.classList.remove("viewer-fullscreen");
        return;
    }
    document.getElementById("message-viewer")?.classList.remove("narrow-active");
    document.getElementById("message-list")?.classList.remove("narrow-hidden");
    // Release focus so the viewer clears. Without this a subsequent sync
    // reload could re-show the same message and re-trigger narrow-active.
    releaseFocus();
};
document.getElementById("btn-back")?.addEventListener("click", backToList);
// Android WebView sometimes drops synthetic clicks after a touchend inside a
// header bar layered above the iframe — handle touchend explicitly too.
document.getElementById("btn-back")?.addEventListener("touchend", backToList);

// ── Fold/unfold (and any window resize) layout reconcile ──
// narrow-active/narrow-hidden are otherwise only set when a message is
// CLICKED while already narrow. Opening a message on the Fold's inner
// screen (wide, two panes) and then folding to the cover screen crossed
// into the narrow layout with neither class set — the viewer vanished and
// there was no back-to-inbox button (Bob 2026-07-03). Reconcile on every
// tier crossing: into narrow with a message open → full-screen viewer +
// back button, exactly as if it had been opened narrow; out of narrow →
// both panes, classes off. matchMedia mirrors layout.css's narrow query
// (768px width OR 600px height) so JS and CSS agree on the tier.
const narrowMq = window.matchMedia("(max-width: 768px), (max-height: 600px)");
narrowMq.addEventListener("change", () => {
    const viewer = document.getElementById("message-viewer");
    const list = document.getElementById("message-list");
    if (narrowMq.matches) {
        if (getCurrentMessage()) {
            viewer?.classList.add("narrow-active");
            list?.classList.add("narrow-hidden");
        }
    } else {
        viewer?.classList.remove("narrow-active");
        list?.classList.remove("narrow-hidden");
    }
});

// Pop-out viewer button — desktop spawns a floating overlay (multiple at
// once), mobile toggles `body.viewer-fullscreen` for full-screen reading.
// Threshold and behavior live in popOutCurrentMessage.
document.getElementById("mv-popout")?.addEventListener("click", () => popOutCurrentMessage());
document.getElementById("mv-popout-window")?.addEventListener("click", () => popOutToWindow());
document.getElementById("btn-print")?.addEventListener("click", () => printCurrentMessage());

// Close folder panel when a folder is selected (narrow mode)
// Also reset narrow navigation: show message list, hide viewer
document.getElementById("folder-tree")?.addEventListener("click", (e) => {
    if (window.innerWidth <= 768 && (e.target as HTMLElement).closest(".ft-folder")) {
        document.querySelector(".folder-panel")?.classList.remove("open");
        document.getElementById("message-viewer")?.classList.remove("narrow-active");
        document.getElementById("message-list")?.classList.remove("narrow-hidden");
    }
});

// Close folder overlay when user clicks outside it (narrow mode OR
// medium-width mode where the folder panel slides in as an overlay).
// Uses capture phase so it beats any child handler that might stopPropagation.
document.addEventListener("pointerdown", (e) => {
    const panel = document.querySelector(".folder-panel");
    if (!panel || !panel.classList.contains("open")) return;
    const target = e.target as HTMLElement;
    // Ignore clicks inside the panel itself and on either toggle button.
    // Without `#btn-folder-toggle` in this list, clicking the folder icon
    // while the panel is open closed it here (capture phase) then the click
    // handler reopened it — net effect: panel stuck open, "doesn't toggle".
    if (target.closest(".folder-panel")
        || target.closest("#btn-menu")
        || target.closest("#btn-folder-toggle")) return;
    // Only auto-dismiss when we're in overlay mode (small or medium screens).
    // On wide screens the panel is a permanent column and the "open" class
    // is irrelevant.
    if (window.innerWidth <= 1100 || window.innerHeight <= 600) {
        panel.classList.remove("open");
    }
}, true);

// Same auto-dismiss for the icon-rail drawer (narrow only — on medium/wide
// the rail is a permanent column and `.open` has no visual effect).
document.addEventListener("pointerdown", (e) => {
    const rail = document.querySelector(".icon-rail");
    if (!rail || !rail.classList.contains("open")) return;
    const target = e.target as HTMLElement;
    if (target.closest(".icon-rail") || target.closest("#btn-menu")) return;
    if (window.innerWidth <= 768) rail.classList.remove("open");
}, true);

// The rail stays open until the user clicks outside it — handled by the
// document-level outside-click handler above. No per-button close: chaining
// rail actions (toggle theme, then check About, then change a setting)
// without having to re-open the rail each time was the explicit ask.

// ── Toolbar actions ──

document.getElementById("btn-sync")?.addEventListener("click", async () => {
    const btn = document.getElementById("btn-sync") as HTMLButtonElement;
    btn.disabled = true;
    btn.classList.add("syncing");
    const statusSync = document.getElementById("status-sync");
    if (statusSync) statusSync.textContent = "Syncing...";

    try {
        await triggerSync();
        // Button stays spinning — WebSocket syncProgress/folderCountsChanged will update UI
        // Set a timeout to re-enable if no WebSocket response
        setTimeout(() => {
            btn.disabled = false;
            btn.classList.remove("syncing");
            refreshFolderTree();
            reloadCurrentFolder();
            if (statusSync && statusSync.textContent === "Syncing...") {
                statusSync.textContent = `Synced ${new Date().toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false })}`;
            }
        }, 30000);
    } catch (e: any) {
        if (statusSync) statusSync.textContent = `Sync error: ${e.message}`;
        btn.disabled = false;
        btn.classList.remove("syncing");
    }
});

// Restart menu dropdown
const restartBtn = document.getElementById("btn-restart");
const restartDropdown = document.getElementById("restart-dropdown");
restartBtn?.addEventListener("click", () => {
    restoreToolbarDropdown("restart-dropdown", "restart-menu");
    if (restartDropdown) restartDropdown.hidden = !restartDropdown.hidden;
});
document.addEventListener("click", (e) => {
    if (restartDropdown && !restartDropdown.hidden && !(e.target as HTMLElement).closest("#restart-menu")) {
        restartDropdown.hidden = true;
    }
});

document.getElementById("btn-restart-quick")?.addEventListener("click", async () => {
    if (restartDropdown) restartDropdown.hidden = true;
    if (isApp) {
        // Android has no daemon — only the WebView. Reload-the-page is the
        // right action there. Desktop IPC mode is a different story below.
        if ((window as any).mailxapi?.platform === "android") {
            const f = document.createElement("iframe");
            f.style.display = "none";
            f.src = "mailxapi://checkUpdate";
            document.body.appendChild(f);
            setTimeout(() => f.remove(), 100);
            location.reload();
            return;
        }
        // Desktop IPC mode: there IS a daemon (the --daemon child of mailx)
        // running mailx-service / mailx-imap / mailx-store. Just calling
        // location.reload() reloads the WebView but the daemon keeps running
        // the old code, so daemon-side changes (sync, store, IPC handlers)
        // don't get picked up. Trigger restartDaemon — it spawns a fresh
        // `mailx` process, hands off the instance.json slot, then gracefully
        // shuts down the current daemon. The UI reloads after a short delay
        // so the new daemon's WebView replaces this one.
        const statusSync = document.getElementById("status-sync");
        if (statusSync) statusSync.textContent = "Restarting...";
        const ipc = (window as any).mailxapi;
        if (ipc?.restartDaemon) {
            try { await ipc.restartDaemon(); } catch { /* daemon shutting down */ }
            setTimeout(() => location.reload(), 2000);
        } else {
            // Older host with no restartDaemon IPC — fall back to UI reload.
            location.reload();
        }
    } else {
        const statusSync = document.getElementById("status-sync");
        if (statusSync) statusSync.textContent = "Restarting...";
        try { await restartServer(); } catch { /* server is shutting down */ }
    }
});

document.getElementById("btn-update")?.addEventListener("click", async () => {
    if (restartDropdown) restartDropdown.hidden = true;
    const statusSync = document.getElementById("status-sync");
    if (statusSync) statusSync.textContent = "Checking for updates...";
    const ipc = (window as any).mailxapi || (window as any).opener?.mailxapi;
    if (ipc?.performUpdate) {
        if (statusSync) statusSync.textContent = "Updating... mailx will restart when done";
        ipc.performUpdate();
    } else if (statusSync) {
        statusSync.textContent = "Update not available in this mode";
    }
});

document.getElementById("btn-rebuild")?.addEventListener("click", async () => {
    if (restartDropdown) restartDropdown.hidden = true;
    if (!confirm("Rebuild local cache?\n\nThis wipes the local database and message store, then re-downloads everything.\nAccounts and settings are preserved.\n\nThis is safe and usually takes just a few minutes.")) return;
    const statusSync = document.getElementById("status-sync");
    if (statusSync) statusSync.textContent = "Rebuilding...";
    try { await restartServer(); } catch { /* restarting */ }
});

document.getElementById("btn-factory-reset")?.addEventListener("click", async () => {
    if (restartDropdown) restartDropdown.hidden = true;
    if (!confirm("Factory reset?\n\nThis deletes ALL data — accounts, settings, messages, cache.\nYou will need to set up your account again.")) return;
    const ipc = (window as any).mailxapi;
    if (ipc?.resetAll) {
        await ipc.resetAll();
    } else {
        // Fallback: clear IndexedDB + localStorage manually
        const dbs = await indexedDB.databases();
        for (const db of dbs) { if (db.name) indexedDB.deleteDatabase(db.name); }
        localStorage.clear();
        location.reload();
    }
});

// ── Compose / Reply / Forward ──

type ComposeMode = "new" | "reply" | "replyAll" | "forward" | "editAsNew";

async function openCompose(mode: ComposeMode, overrideMsg?: any, overrideAccountId?: string): Promise<void> {
    logClientEvent("openCompose-entry", { mode });
    // Opening a compose (Ctrl+N, the Compose button, the rail) must dismiss any
    // open toolbar menu. Click-outside already closes them via the overlay
    // shield, but a keyboard Ctrl+N over an open Settings/View menu left it
    // stuck open behind the new compose (Bob 2026-06-21). Close them here so
    // every compose-open path behaves the same.
    for (const id of ["settings-dropdown", "view-dropdown", "restart-dropdown"]) {
        const dd = document.getElementById(id);
        if (dd && !dd.hidden) hideDropdownHard(dd);
    }
    refreshToolbarOverlayShield();
    // `overrideMsg` lets a detached surface (the message pop-out) compose a
    // reply/forward for ITS message rather than whatever the main viewer has
    // selected. Same `{ message, accountId }` shape getCurrentMessage returns.
    const current = overrideMsg
        ? { message: overrideMsg, accountId: overrideAccountId || currentAccountId }
        : getCurrentMessage();
    // Local-first: if the row is selected we already have its headers in the
    // local DB. Populate the compose form unconditionally; the user can edit
    // anything missing. Don't show "still loading" alerts — the message IS
    // loaded (it's in the list), body is a separate fetch that isn't needed
    // for Reply's headers. Missing fields become empty strings.
    if ((mode === "reply" || mode === "replyAll" || mode === "forward" || mode === "editAsNew") && !current) {
        // Only true blocker: no message selected at all.
        console.warn(`[compose] ${mode} — no message selected`);
        return;
    }
    // Parallel-load: kick off getAccounts AND open the iframe in the same
    // tick. The iframe doesn't need the account list until after its editor
    // bootstraps (200-500 ms for TinyMCE, less for Quill); by then the IPC
    // round-trip has resolved. Earlier code awaited getAccounts FIRST,
    // adding the IPC latency to the perceived Ctrl+N → editor-visible time.
    // We post `compose-init-ready` to the iframe once init is in
    // sessionStorage so compose.ts's IIFE can read synchronously without
    // polling.
    const accountsP = getAccounts();
    const msg = current?.message;
    // Title bar text needs the subject from msg (no IPC dependency) — build
    // it now so the iframe can be opened with the final title and avoid a
    // flash of placeholder text.
    const titlePrefix =
        mode === "reply"    ? "Reply" :
        mode === "replyAll" ? "Reply All" :
        mode === "forward"  ? "Forward" :
        mode === "editAsNew" ? "Edit as new" :
        "Compose";
    const titleSubject = mode === "new" ? "" : (msg?.subject || "");
    const composeTitle = titleSubject ? `${titlePrefix}: ${titleSubject}` : titlePrefix;
    // Default: compose/reply/forward open in their OWN OS window — an
    // overlay covering the message list/preview is the thing Bob wants gone
    // (2026-07-12 "the default on a reply should just be a separate window
    // outside the frame"). Overlay remains: small screens, Android (no
    // popout host), Settings toggle off, or daemon spawn failure.
    const preferWindow = window.innerWidth > 768 && window.innerHeight > 600
        && localStorage.getItem("mailx-compose-in-window") !== "0";
    let frame: HTMLIFrameElement | null = preferWindow ? null : showComposeOverlay(composeTitle);
    // Now finish initialisation off the critical path — editor bootstrap
    // inside the iframe runs concurrently with this await.
    const accounts = await accountsP;
    // For a brand-new compose, default the From to the account whose folder
    // you're currently viewing — pressing Ctrl+N while reading AOL should
    // compose AS aol, not the first/defaultSend account. In the unified "All
    // Inboxes" view there's no single account (currentAccountId === ""), so
    // fall back to the selected message's account, then the first account.
    // Reply/forward keep using the message's own account (you answer from the
    // address that received it; detectReplyFrom refines further).
    const accountId = mode === "new"
        ? (currentAccountId || current?.accountId || accounts[0]?.id || "")
        : (current?.accountId || currentAccountId || accounts[0]?.id || "");
    const rePrefix = /^(re|fwd?):\s*/i;
    const cleanSubject = msg ? msg.subject.replace(rePrefix, "") : "";

    const init: any = {
        mode,
        accountId,
        to: [],
        cc: [],
        subject: "",
        bodyHtml: "",
        inReplyTo: "",
        references: [],
        accounts: accounts.map((a: any) => ({ id: a.id, name: a.name, email: a.email, signature: a.signature, sig: a.sig })),
    };

    // Auto-detect reply From: if the message was delivered to an identity address
    // (an alias on the account's domain, or the explicit `identityDomains` list
    // in accounts.jsonc), reply from that address instead of the account's
    // primary. Always derive identityDomains from the account email's domain
    // when not configured — explicit list was a regression source (users would
    // see Reply pick the wrong From silently when the list was missing).
    const account = accounts.find((a: any) => a.id === accountId);
    const explicitDomains: string[] = (account?.identityDomains || []).map((d: string) => d.toLowerCase());
    const accountDomain = (account?.email || "").split("@")[1]?.toLowerCase();
    // Fold subsumed subdomains. If the list has both `frankston.com` and
    // `bobf.frankston.com`, drop the longer entry — the matcher's
    // `endsWith(".frankston.com")` test already catches it, so listing
    // both is wasted clutter. Same-pair dedup + parent-shadow filter in
    // one pass.
    function foldDomains(list: string[]): string[] {
        const unique = Array.from(new Set(list));
        return unique.filter(d => !unique.some(p => p !== d && d.endsWith(`.${p}`)));
    }
    const identityDomains: string[] = foldDomains(
        explicitDomains.length > 0
            ? explicitDomains
            : (accountDomain ? [accountDomain] : []),
    );
    // Extract the bare email address from a header value that may be a full
    // `"Display Name" <addr@host>` form. detectReplyFrom must return a BARE
    // address because the compose side wraps it as `${accountName} <${addr}>`
    // — passing a full name+addr there produced the nested garbage
    // `Bob Frankston <"mailing list" <multicians@groups.io>>` (Bob 2026-05-28).
    // Delivered-To on mailing-list mail commonly carries a display name.
    function bareAddress(raw: string): string {
        if (!raw) return raw;
        const angle = raw.match(/<([^>]+)>/);
        if (angle) return angle[1].trim();
        return raw.trim().replace(/^"+|"+$/g, "");
    }
    function detectReplyFrom(): string | undefined {
        if (!msg) return undefined;
        // Delivered-To is set by the receiving server — it IS an identity at this
        // account, by definition. Trust it unconditionally when present (after
        // deliveredToPrefix stripping in the service). Fall back to To/Cc only
        // when their domain matches the account's identityDomains, since To/Cc
        // can be set by the sender and aren't authoritative.
        if (msg.deliveredTo) {
            const addr = bareAddress(msg.deliveredTo);
            console.log(`[compose] reply From → ${addr} (Delivered-To, bare of "${msg.deliveredTo}")`);
            return addr;
        }
        if (identityDomains.length === 0) return undefined;
        const candidates: string[] = [
            ...((msg.to || []).map((a: any) => a.address)),
            ...((msg.cc || []).map((a: any) => a.address)),
        ].filter(Boolean).map(bareAddress);
        for (const addr of candidates) {
            const domain = addr.split("@")[1]?.toLowerCase();
            if (domain && identityDomains.some(d => domain === d || domain.endsWith(`.${d}`))) {
                console.log(`[compose] reply From → ${addr} (To/Cc match)`);
                return addr;
            }
        }
        console.log(`[compose] no identity match`);
        return undefined;
    }

    // Trace what we're feeding the reply — `[reply-init]` on the daemon log
    // will show the exact `from`/`to`/`cc` values present on the source
    // message at construction time, so a "To field empty" report can be
    // diagnosed without re-deriving state. Bob 2026-05-13: had empty To on
    // a clear-From mailing list message; need to see whether msg.from was
    // populated or arrived as null.
    if (msg) {
        try {
            const dump = {
                mode,
                accountId,
                hasMsg: true,
                from: msg.from || null,
                toLen: Array.isArray(msg.to) ? msg.to.length : -1,
                ccLen: Array.isArray(msg.cc) ? msg.cc.length : -1,
                deliveredTo: msg.deliveredTo || "",
                identityDomains,
                subject: msg.subject || "",
            };
            const apiClient = (window as any).mailxapi;
            if (apiClient?.logClientEvent) {
                apiClient.logClientEvent("reply-init", dump);
            } else {
                console.log("[reply-init]", dump);
            }
        } catch { /* tracing must never break compose */ }
    } else {
        try {
            (window as any).mailxapi?.logClientEvent?.("reply-init", { mode, accountId, hasMsg: false });
        } catch { /* */ }
    }

    // Defensive: msg.from / msg.to may be missing on rows that arrived before
    // headers finished loading. Don't push undefined into init.to — that
    // bubbles to the compose form as literal "undefined". Empty-out gracefully.
    if (msg && mode === "reply") {
        init.to = msg.from ? [msg.from] : [];
        init.subject = `Re: ${cleanSubject}`;
        init.bodyHtml = quoteBody(msg);
        init.inReplyTo = msg.messageId || "";
        init.references = [...(msg.references || []), msg.messageId].filter(Boolean);
        init.fromAddress = detectReplyFrom();
    } else if (msg && mode === "replyAll") {
        const toList: any[] = msg.from ? [msg.from] : [];
        if (Array.isArray(msg.to)) {
            for (const a of msg.to) {
                if (a?.address && a.address !== msg.from?.address) toList.push(a);
            }
        }
        init.to = toList;
        init.cc = Array.isArray(msg.cc) ? msg.cc : [];
        init.subject = `Re: ${cleanSubject}`;
        init.bodyHtml = quoteBody(msg);
        init.inReplyTo = msg.messageId || "";
        init.references = [...(msg.references || []), msg.messageId].filter(Boolean);
        init.fromAddress = detectReplyFrom();
    } else if (msg && mode === "forward") {
        init.subject = `Fwd: ${cleanSubject}`;
        init.bodyHtml = forwardBody(msg);
        init.fromAddress = detectReplyFrom();
    } else if (msg && mode === "editAsNew") {
        // Edit-as-new clones an existing message into a fresh compose as if
        // the user authored it: original To/Cc/Subject/body, all editable.
        // No `Re:`/`Fwd:` prefix (subject verbatim), no quote/forward wrapper,
        // no In-Reply-To / References (this is NOT a reply — it gets a brand-
        // new Message-ID at send time). Replaces the move-to-Drafts kludge for
        // "tweak this and send it again" (Q152, Bob 2026-05-31).
        init.to = Array.isArray(msg.to) ? msg.to : [];
        init.cc = Array.isArray(msg.cc) ? msg.cc : [];
        init.subject = msg.subject || "";
        init.bodyHtml = editAsNewBody(msg);
        // From: preserve the original sender when it's one of YOUR accounts
        // (resending your own message keeps the same identity); otherwise fall
        // back to received-identity detection. Without this, edit-as-new always
        // defaulted to the primary account — "a different from address" (Bob
        // 2026-06-29).
        const origFrom = msg.from?.address ? bareAddress(msg.from.address) : "";
        const ownFrom = origFrom && accounts.some((acc: any) => bareAddress(acc.email || "") === origFrom) ? origFrom : undefined;
        init.fromAddress = ownFrom || detectReplyFrom();
    }

    // Carry the original message's attachments into forward / edit-as-new so
    // reopening a sent message to edit (or forwarding one) keeps its files —
    // they were being silently dropped (Bob 2026-06-21). Fetch each via
    // getAttachment (the same path the viewer's download uses) and hand compose
    // the base64 it expects. Reply/replyAll intentionally omit attachments.
    if (msg && (mode === "editAsNew" || mode === "forward") && Array.isArray(msg.attachments) && msg.attachments.length) {
        const msgAccountId = current?.accountId || accountId;
        const loaded: { filename: string; mimeType: string; dataBase64: string }[] = [];
        for (const att of msg.attachments) {
            try {
                const data = await getAttachment(msgAccountId, msg.uid, att.id, msg.folderId);
                if (data?.content) {
                    loaded.push({
                        filename: data.filename || att.filename || "attachment",
                        mimeType: data.contentType || att.contentType || "application/octet-stream",
                        dataBase64: data.content,
                    });
                }
            } catch (e: any) {
                console.error(`[compose] ${mode}: failed to load attachment ${att.id}: ${e?.message || e}`);
            }
        }
        if (loaded.length) init.attachments = loaded;
    }


    // Store init data for compose window to pick up. Two parallel paths
    // because sessionStorage propagation to a freshly-loading iframe has
    // intermittently failed under WebView2's custom-protocol host (Bob
    // 2026-05-24: reply-all opened with empty To/Subject; daemon log shows
    // reply-init dump fired correctly but the iframe IIFE saw sessionStorage
    // empty and timed out the wait). postMessage carries the FULL init
    // payload so the iframe can populate even when storage doesn't bridge.
    // sessionStorage remains as the fast-path for iframes that finish
    // loading before postMessage fires.
    if (preferWindow && !frame) {
        try {
            const { popoutCompose } = await import("./lib/api-client.js");
            const r = await popoutCompose(init);
            if (r?.ok) return;
            console.error("[compose] separate-window open failed — overlay fallback:", r?.reason);
        } catch (e: any) {
            console.error("[compose] separate-window open threw — overlay fallback:", e?.message || e);
        }
        frame = showComposeOverlay(composeTitle);
    }

    const initJson = JSON.stringify(init);
    try { sessionStorage.setItem("composeInit", initJson); }
    catch (e: any) { console.error("[compose] sessionStorage.setItem failed:", e?.message || e); }
    // Stash on the parent window so a same-origin iframe can pull it via
    // window.parent (most reliable handoff under WebView2 custom-protocol —
    // sessionStorage and postMessage both fail intermittently). Keyed by
    // a unique token in the iframe URL so concurrent composes don't crosswire.
    const composeKey = "init-" + Math.random().toString(36).slice(2, 10);
    (window as any).__mailxComposeInits = (window as any).__mailxComposeInits || {};
    (window as any).__mailxComposeInits[composeKey] = init;
    // Expose the key on the iframe so its IIFE can fetch the right blob.
    try { (frame as any).dataset.composeKey = composeKey; } catch { /* */ }
    const post = (): void => {
        try { frame?.contentWindow?.postMessage({ type: "compose-init", init, composeKey }, "*"); }
        catch (e: any) { logClientEvent("compose-post-failed", { err: e?.message || String(e) }); }
    };
    logClientEvent("compose-handoff", { hasFrame: !!frame, hasContentWindow: !!frame?.contentWindow, composeKey, bodyBytes: initJson.length });
    post();
    // Iframe may not be loaded yet — re-post on load so the listener exists.
    try { frame?.addEventListener("load", () => { logClientEvent("compose-iframe-loaded", { composeKey }); post(); }); } catch { /* */ }
    // And keep posting every 100ms for up to 3s as a defense-in-depth — if
    // both load and the initial post race the wrong way, the iframe's IIFE
    // will see the payload on its next tick.
    let attempts = 0;
    const heartbeat = setInterval(() => {
        attempts++;
        post();
        if (attempts >= 30) clearInterval(heartbeat);
    }, 100);
}

function showComposeOverlay(title = "Compose"): HTMLIFrameElement {
    const wrapper = document.createElement("div");
    wrapper.className = "compose-overlay";
    // Full-screen on small/short screens, floating on larger
    const isSmall = window.innerWidth <= 768 || window.innerHeight <= 600;
    if (isSmall) {
        wrapper.style.cssText = "position:fixed;inset:0;z-index:1600;display:flex;flex-direction:column;background:#fff;";
    } else {
        // CSS `resize:both` only gives a single lower-right grip. To allow
        // dragging any edge or corner, we omit it here and attach eight
        // manual handles via addComposeResizeHandles() below.
        // Open horizontally centred, near the top — not docked to the
        // lower-right corner (Bob 2026-05-18). The title bar still drags it
        // anywhere; this is just the initial placement.
        wrapper.style.cssText = "position:fixed;top:48px;left:calc((100vw - min(900px,55vw)) / 2);width:min(900px,55vw);height:min(700px,70vh);z-index:1600;border-radius:8px;box-shadow:0 4px 24px rgba(0,0,0,0.3);display:flex;flex-direction:column;overflow:hidden;";
    }

    // Title bar — drag to move; right-side cluster holds discard, popout, close.
    const titleBar = document.createElement("div");
    titleBar.style.cssText = "display:flex;align-items:center;justify-content:space-between;padding:4px 8px;background:#e8ecf0;border-radius:8px 8px 0 0;cursor:move;user-select:none;flex-shrink:0;";
    const titleText = document.createElement("span");
    titleText.textContent = title;
    titleBar.appendChild(titleText);

    const btnCluster = document.createElement("div");
    btnCluster.style.cssText = "display:flex;align-items:center;gap:2px;";
    titleBar.appendChild(btnCluster);

    // Outline SVG icons matched to the rest of the toolbar — 16px, 1.6 stroke,
    // currentColor so hover styles work via plain CSS.
    const SVG_ATTRS = `width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"`;
    const TRASH_SVG = `<svg ${SVG_ATTRS}><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>`;
    const POPOUT_SVG = `<svg ${SVG_ATTRS}><path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg>`;
    const MAXIMIZE_SVG = `<svg ${SVG_ATTRS}><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M4 9h16"/></svg>`;
    const RESTORE_SVG = `<svg ${SVG_ATTRS}><path d="M21 9v-6h-6"/><path d="M3 15v6h6"/><path d="M21 3l-7 7"/><path d="M3 21l7-7"/></svg>`;
    const makeIconBtn = (svg: string, titleStr: string, hoverColor: string): HTMLButtonElement => {
        const b = document.createElement("button");
        b.title = titleStr;
        b.style.cssText = "background:none;border:none;cursor:pointer;color:#666;padding:4px 6px;border-radius:4px;display:inline-flex;align-items:center;justify-content:center;line-height:0;";
        b.innerHTML = svg;
        b.addEventListener("mouseenter", () => b.style.color = hoverColor);
        b.addEventListener("mouseleave", () => b.style.color = "#666");
        return b;
    };

    const discardBtn = makeIconBtn(TRASH_SVG, "Discard draft", "#c00");
    discardBtn.addEventListener("click", () => {
        try {
            const win = frame.contentWindow;
            if (win) win.dispatchEvent(new Event("compose-discard"));
        } catch { /* */ }
    });
    btnCluster.appendChild(discardBtn);

    // True popout — hand the live compose off to its own OS window (native
    // msger window via the daemon; see compose.ts popoutHandoff). Lives in
    // the TITLE BAR with the other frame controls because it's about the
    // frame, not the content (Bob 2026-07-12). The iframe owns the state, so
    // this just pokes it with an event, same pattern as Discard/Close.
    // Hidden on small screens where the overlay is full-screen and a separate
    // OS window makes no sense (and Android has no popout host).
    if (!isSmall) {
        const osPopoutBtn = makeIconBtn(POPOUT_SVG, "Open in separate window", "#000");
        osPopoutBtn.addEventListener("click", () => {
            try {
                const win = frame.contentWindow;
                if (win) win.dispatchEvent(new Event("compose-popout"));
            } catch { /* */ }
        });
        btnCluster.appendChild(osPopoutBtn);
    }

    // Maximize = toggle between the floating overlay and a host-window-filling
    // layout — second click restores the floating geometry.
    const popoutBtn = makeIconBtn(MAXIMIZE_SVG, "Maximize", "#000");
    let maximized = false;
    let savedCss = "";
    popoutBtn.addEventListener("click", () => {
        if (!maximized) {
            savedCss = wrapper.style.cssText;
            wrapper.style.cssText = "position:fixed;inset:0;z-index:1600;display:flex;flex-direction:column;background:#fff;overflow:hidden;";
            popoutBtn.innerHTML = RESTORE_SVG;
            popoutBtn.title = "Restore";
            maximized = true;
        } else {
            wrapper.style.cssText = savedCss;
            popoutBtn.innerHTML = MAXIMIZE_SVG;
            popoutBtn.title = "Maximize";
            maximized = false;
        }
    });
    btnCluster.appendChild(popoutBtn);

    const closeBtn = document.createElement("button");
    closeBtn.textContent = "✕";
    closeBtn.title = "Save draft and close";
    closeBtn.style.cssText = "background:none;border:none;font-size:16px;cursor:pointer;color:#666;padding:2px 6px;border-radius:4px;";
    closeBtn.addEventListener("mouseenter", () => closeBtn.style.color = "#c00");
    closeBtn.addEventListener("mouseleave", () => closeBtn.style.color = "#666");
    closeBtn.addEventListener("click", () => {
        // compose.ts handles the prompt (Save/Discard/Cancel) and then calls
        // window.close() which is redirected to wrapper.remove() at line below.
        // If the user cancels the prompt, closeCompose() is never called and
        // the wrapper stays. Don't force-remove on a timer — that defeats Cancel.
        try {
            const win = frame.contentWindow;
            if (win) win.dispatchEvent(new Event("compose-save-and-close"));
        } catch { /* */ }
    });
    btnCluster.appendChild(closeBtn);

    // Drag to move. While dragging we set pointer-events:none on the iframe
    // so mouse events don't get swallowed by the inner document the moment
    // the cursor crosses into the iframe region. Without that, drag only
    // worked if you stayed on the title bar pixels, which is why it felt
    // broken except at the lower-right (resize grip) corner.
    let dragX = 0, dragY = 0;
    titleBar.addEventListener("mousedown", (e: MouseEvent) => {
        // Don't start a drag (and don't install the full-window drag shield)
        // when the press is on ANY title-bar button — the shield was swallowing
        // the click so Discard / Maximize never fired. Was `=== closeBtn` only,
        // which is why maximize "didn't maximize" (Bob 2026-05-31).
        if (btnCluster.contains(e.target as Node)) return;
        e.preventDefault();
        const rect = wrapper.getBoundingClientRect();
        dragX = e.clientX - rect.left;
        dragY = e.clientY - rect.top;
        // Clamp movement to the viewport so the title bar stays grabbable.
        const clamp = (val: number, min: number, max: number) => Math.max(min, Math.min(max, val));
        const shield = installDragShield("move");
        const onMove = (ev: MouseEvent) => {
            ev.preventDefault();
            const w = wrapper.offsetWidth;
            const h = wrapper.offsetHeight;
            const left = clamp(ev.clientX - dragX, 0, window.innerWidth - 40);
            const top = clamp(ev.clientY - dragY, 0, window.innerHeight - 40);
            wrapper.style.left = `${left}px`;
            wrapper.style.top = `${top}px`;
            wrapper.style.bottom = "auto";
            wrapper.style.right = "auto";
        };
        const onUp = () => {
            shield.remove();
            document.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseup", onUp);
        };
        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
    });

    const frame = document.createElement("iframe");
    frame.src = "compose/compose.html";
    frame.style.cssText = "flex:1;border:none;background:#fff;width:100%;";

    // Close when compose calls window.close()
    frame.addEventListener("load", () => {
        try {
            const win = frame.contentWindow;
            if (win) {
                (win as any).close = () => wrapper.remove();
            }
        } catch { /* cross-origin safety */ }
    });

    // Bring to front on click
    wrapper.addEventListener("mousedown", () => {
        document.querySelectorAll(".compose-overlay").forEach(el => (el as HTMLElement).style.zIndex = "1000");
        wrapper.style.zIndex = "1001";
    });

    wrapper.appendChild(titleBar);
    wrapper.appendChild(frame);
    if (!isSmall) addComposeResizeHandles(wrapper, frame);
    document.body.appendChild(wrapper);
    return frame;
}

/** Drop a transparent full-viewport shield in front of every other element
 *  so mousemove events stay in the document during a drag. Setting
 *  pointer-events:none on the compose iframe alone wasn't enough — the
 *  message-list / preview iframes underneath still captured the cursor
 *  when it crossed their boundaries, freezing the drag at random
 *  midpoints (most visibly at the list↔preview seam). One shield blocks
 *  every iframe at once. Caller removes it on mouseup. */
function installDragShield(cursor: string): HTMLDivElement {
    const shield = document.createElement("div");
    shield.style.cssText = `position:fixed;inset:0;z-index:9999;background:transparent;cursor:${cursor};user-select:none;`;
    document.body.appendChild(shield);
    return shield;
}

/** Attach eight resize grippers (4 edges + 4 corners) to a positioned wrapper.
 *  CSS `resize:both` only supports a single lower-right corner; replacing it
 *  with manual handles is the only way to make any side / corner draggable.
 *  Each handle captures the starting geometry on mousedown, then mousemove
 *  adjusts width/height/left/top in the directions implied by which edge. */
function addComposeResizeHandles(wrapper: HTMLElement, frame: HTMLIFrameElement): void {
    const MIN_W = 320;
    const MIN_H = 200;
    const dirs: { [k: string]: { cursor: string; style: string } } = {
        n:  { cursor: "ns-resize",   style: "top:-3px;left:12px;right:12px;height:6px;" },
        s:  { cursor: "ns-resize",   style: "bottom:-3px;left:12px;right:12px;height:6px;" },
        e:  { cursor: "ew-resize",   style: "right:-3px;top:12px;bottom:12px;width:6px;" },
        w:  { cursor: "ew-resize",   style: "left:-3px;top:12px;bottom:12px;width:6px;" },
        ne: { cursor: "nesw-resize", style: "top:-3px;right:-3px;width:14px;height:14px;" },
        nw: { cursor: "nwse-resize", style: "top:-3px;left:-3px;width:14px;height:14px;" },
        se: { cursor: "nwse-resize", style: "bottom:-3px;right:-3px;width:14px;height:14px;" },
        sw: { cursor: "nesw-resize", style: "bottom:-3px;left:-3px;width:14px;height:14px;" },
    };
    for (const [dir, conf] of Object.entries(dirs)) {
        const h = document.createElement("div");
        h.style.cssText = `position:absolute;z-index:2;background:transparent;cursor:${conf.cursor};${conf.style}`;
        h.addEventListener("mousedown", (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            const rect = wrapper.getBoundingClientRect();
            const startX = e.clientX, startY = e.clientY;
            const startL = rect.left, startT = rect.top;
            const startW = rect.width, startH = rect.height;
            // Pin to top-left so left/top can be adjusted to grow upward/leftward.
            wrapper.style.left = `${startL}px`;
            wrapper.style.top = `${startT}px`;
            wrapper.style.right = "auto";
            wrapper.style.bottom = "auto";
            const shield = installDragShield(conf.cursor);
            const onMove = (ev: MouseEvent) => {
                const dx = ev.clientX - startX;
                const dy = ev.clientY - startY;
                let newW = startW, newH = startH, newL = startL, newT = startT;
                if (dir.includes("e")) newW = Math.max(MIN_W, startW + dx);
                if (dir.includes("w")) { newW = Math.max(MIN_W, startW - dx); newL = startL + (startW - newW); }
                if (dir.includes("s")) newH = Math.max(MIN_H, startH + dy);
                if (dir.includes("n")) { newH = Math.max(MIN_H, startH - dy); newT = startT + (startH - newH); }
                wrapper.style.width = `${newW}px`;
                wrapper.style.height = `${newH}px`;
                wrapper.style.left = `${newL}px`;
                wrapper.style.top = `${newT}px`;
            };
            const onUp = () => {
                shield.remove();
                document.removeEventListener("mousemove", onMove);
                document.removeEventListener("mouseup", onUp);
            };
            document.addEventListener("mousemove", onMove);
            document.addEventListener("mouseup", onUp);
        });
        wrapper.appendChild(h);
    }
}

// Marketing-email layout tables (deeply nested, fixed widths) collapse to
// 30-40px columns inside a phone-width compose pane and wrap text
// character-by-character. Strip styles + flatten tables before quoting.
function sanitizeQuotedBody(msg: any): string {
    // Two-mode quote: plain-text gets a pre-wrap wrapper so line breaks
    // render as breaks; HTML is preserved verbatim modulo a minimal scrub
    // for tags that have no legitimate place inside a quoted reply.
    //
    // We do NOT strip inline styles, classes, or table attributes any
    // more — that earlier aggressive strip destroyed 95% of the original
    // sender's formatting (CSS in email is almost entirely inline; see
    // discussion 2026-05-11 about how every mail client preserves the
    // source HTML). Wide content is now clamped via CSS (.reply *
    // { max-width: 100% } in compose.css) instead of by rewriting the
    // DOM. Tables stay tables, paragraphs stay paragraphs, font sizes
    // and colors survive.
    //
    // Script-class tags (<script>, <style>, <link>, <base>, on*= attrs,
    // javascript: URLs) are belt-and-braces — sanitizeHtml (mailx-types)
    // already strips them at body-store time, so msg.bodyHtml shouldn't
    // contain them. Stripping again here is cheap insurance against a
    // future provider/path that didn't go through that pipeline.
    const isPlainText = !msg.bodyHtml;
    if (isPlainText) {
        // Full HTML escape. Leaving `>` unescaped was tempting for source
        // readability but breaks HTML in edge cases — TinyMCE's normalize-
        // on-paste re-interprets the input, and stray `>` near sequences
        // like `<!--` / `-->` / `<!` in plain-text bodies can be misread
        // by the parser. Per Bob 2026-05-12: "not just ugly, it breaks
        // the HTML." Trivial source-clutter is the lesser evil.
        //
        // CRLF → <br>. `white-space:pre-wrap` alone is not enough: TinyMCE
        // (and other HTML editors) normalize text-node whitespace when
        // setContent ingests the HTML, collapsing `\n` to spaces BEFORE
        // CSS runs. The literal `<br>` survives the normalization, so we
        // get one line break per source line whether the editor preserves
        // raw whitespace or not. pre-wrap stays as belt-and-braces and to
        // keep multi-space runs (alignment, indented quote-markers like
        // `> > >`) visible.
        let escaped = String(msg.bodyText || "")
            .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
            .replace(/\r\n?/g, "\n")
            .replace(/\n/g, "<br>");
        // Linkify bare URLs. Without this, replying to a plain-text email
        // (mailing lists, etc.) drops every URL into the quote as dead
        // text — the editor has nothing to keep clickable, so the sent
        // reply has no working links (Bob 2026-05-22). The regex stops at
        // `<`, so the <br>s inserted above can't be swallowed into an href.
        escaped = escaped.replace(
            /(https?:\/\/[^\s<>"')\]]+)/g,
            '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>',
        );
        return `<div style="white-space:pre-wrap;font-family:inherit;margin:0">${escaped}</div>`;
    }
    let body: string = msg.bodyHtml;
    // Minimal defense-in-depth strip. <style> blocks would leak global
    // CSS into the compose document; <link> / <base> would fetch remote
    // resources; <script> would be inert in contenteditable but the tag
    // would persist into the sent message which is rude.
    body = body.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "");
    body = body.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "");
    body = body.replace(/<link[^>]*>/gi, "");
    body = body.replace(/<base[^>]*>/gi, "");
    body = body.replace(/\s+on\w+="[^"]*"/gi, "");
    body = body.replace(/\s+on\w+='[^']*'/gi, "");
    return body;
}

function quoteBody(msg: any): string {
    const date = new Date(msg.date).toLocaleString();
    const from = msg.from.name ? `${msg.from.name} &lt;${msg.from.address}&gt;` : msg.from.address;
    const body = sanitizeQuotedBody(msg);
    // Lead with an empty paragraph so every editor has a real block for the
    // caret to land in and the user's reply to flow into — bare <br>s aren't
    // a block container, so TinyMCE's caret fell through to the quote (Bob
    // 2026-05-21). The blank-line spacing lives in <br>s AFTER the </p>, not
    // inside it.
    return `<p></p><br><br><div class="reply"><p>On ${date}, ${from} wrote:</p><blockquote>${body}</blockquote></div>`;
}

function editAsNewBody(msg: any): string {
    // The message body verbatim (same plain-text/HTML cleaning a quoted reply
    // gets), with NO attribution line and NO quote/forward wrapper — the user
    // is re-authoring this content, not quoting it. A leading empty paragraph
    // gives the caret a block to land in above the cloned content.
    return `<p></p>${sanitizeQuotedBody(msg)}`;
}

function forwardBody(msg: any): string {
    const date = new Date(msg.date).toLocaleString();
    const from = msg.from.name ? `${msg.from.name} &lt;${msg.from.address}&gt;` : msg.from.address;
    const to = msg.to.map((a: any) => a.name ? `${a.name} &lt;${a.address}&gt;` : a.address).join(", ");
    const body = sanitizeQuotedBody(msg);
    return `<p></p><br><br><div class="reply"><p>---------- Forwarded message ----------<br>From: ${from}<br>Date: ${date}<br>Subject: ${msg.subject}<br>To: ${to}</p>${body}</div>`;
}

// ── Delete with undo ──

interface DeletedMessage {
    accountId: string;
    uid: number;
    folderId: number;
    subject: string;
}

interface MovedBatch {
    messages: { accountId: string; uid: number; sourceFolderId: number }[];
    targetAccountId: string;
    targetFolderId: number;
}

// ── Undo stack ───────────────────────────────────────────────────────────
// Each user-driven destructive/state-changing action pushes a typed UndoOp
// onto `undoStack`; Ctrl+Z pops the top entry and invokes the inverse via
// the existing service IPC. LIFO, no redo (undoing an undo would be too
// hard to reason about — Bob 2026-05-23). Bounded at UNDO_MAX entries and
// each entry has a UNDO_TTL_MS lifetime; expired entries are skipped on pop
// rather than aged out by a timer (simpler than juggling per-entry timers
// when older entries get shifted off the front of the stack).
type UndoFlagEntry = { accountId: string; uid: number; prevFlagged: boolean; folderId?: number };
type UndoOp =
    | { kind: "delete"; at: number; payload: DeletedMessage[] }
    | { kind: "move"; at: number; payload: MovedBatch }
    | { kind: "flag"; at: number; payload: UndoFlagEntry[] };

const undoStack: UndoOp[] = [];
const UNDO_MAX = 50;
const UNDO_TTL_MS = 10 * 60_000;

function pushUndo(op: UndoOp): void {
    undoStack.push(op);
    if (undoStack.length > UNDO_MAX) undoStack.shift();
}
function popUndo(): UndoOp | null {
    const now = Date.now();
    while (undoStack.length > 0) {
        const op = undoStack.pop()!;
        if (now - op.at < UNDO_TTL_MS) return op;
        // Expired — drop silently and try the next one.
    }
    return null;
}

/** Route a "delete the selection" action (Delete key, Ctrl+D, top trash
 *  button) to whichever pane has a selection. Tasks take priority — if
 *  any task rows are selected, delete those; otherwise fall through to
 *  messages. The previous behavior was to always delete messages, which
 *  surprised users who'd just selected a few tasks and hit Delete. */
async function deleteSelection(): Promise<void> {
    try {
        const sidebar = await import("./components/calendar-sidebar.js");
        if (sidebar.getSelectedTaskUuids().length > 0) {
            await sidebar.deleteSelectedTasks();
            return;
        }
    } catch { /* sidebar module not loaded yet — fall through to messages */ }
    await deleteSelectedMessages();
}

async function deleteSelectedMessages(): Promise<void> {
    const selected = getSelectedMessages();

    // Fall back to single message from viewer if nothing selected in list
    if (selected.length === 0) {
        const current = getCurrentMessage();
        if (!current) return;
        selected.push({ accountId: current.accountId, uid: current.message.uid, folderId: current.message.folderId });
    }

    // Deleting a message that is ALREADY in Trash is a PERMANENT expunge, not
    // a move — the service's trashMessage returns "expunged" in that case. So
    // the prompt and the undo promise must change: permanent delete always
    // confirms (even a single message) and can't be undone (Bob 2026-06-12).
    const inTrash = currentFolderSpecialUse === "trash";

    // SAFETY GATE — only for the IRREVERSIBLE action.
    //  - In Trash: delete is permanent → confirm (Bob explicitly wanted this).
    //  - Elsewhere: move to Trash with NO prompt, single or bulk. It's
    //    undoable (Ctrl+Z restores the whole batch) and matches Outlook /
    //    Thunderbird, which don't nag on a move-to-trash. Bob 2026-06-15:
    //    "stop giving me the trash warning message. It is annoying."
    const n = selected.length;
    if (inTrash) {
        if (!confirm(`Permanently delete ${n} message${n === 1 ? "" : "s"} from Trash?\n\nThis cannot be undone.`)) {
            return;
        }
    }

    const statusSync = document.getElementById("status-sync");

    // Optimistic UI: remove from list IMMEDIATELY, then queue the IPC.
    // Old order awaited the daemon round-trip (IPC + DB updates) before
    // the rows disappeared, which felt sluggish on bigger selections or
    // when the IPC was congested. Spam button already worked this way;
    // trash now matches. If the IPC fails, the next folder reload
    // re-populates the row and the catch block surfaces the error.
    const snapshot = [...selected];
    removeMessagesAndReconcile(selected);

    if (inTrash) {
        // Permanent — no undo entry (there's nothing to restore to).
        if (statusSync) statusSync.textContent = `Permanently deleted ${n} message${n === 1 ? "" : "s"}`;
    } else {
        // Undo restores the WHOLE batch, not just the first message — a bulk
        // delete must be fully recoverable via Ctrl+Z (the old single-slot undo
        // left the other N-1 unrecoverable; Bob 2026-06-12).
        pushUndo({ kind: "delete", at: Date.now(), payload: snapshot.map(m => ({ ...m, subject: "" })) });
        if (statusSync) statusSync.textContent = n === 1
            ? `Trashed 1 message (syncing) — Ctrl+Z to undo`
            : `Trashed ${n} messages (syncing) — Ctrl+Z to undo`;
    }

    // Fire-and-forget per local-first: optimistic remove above already
    // updated the UI; the daemon-side trash is sync DB + queued IMAP.
    // An IPC 120s timeout doesn't mean the trash failed — surfacing it
    // as a status-bar error would only mislead. Real errors are still
    // reported by next sync's diagnostics.
    // Carry folderId per message so the daemon trashes the exact (folder, uid),
    // not whatever folder a bare (account, uid) lookup happens to resolve to
    // (Bob 2026-06-06: Del trashed the wrong message). uids/folderIds stay
    // index-aligned per account.
    const byAccount = new Map<string, { uids: number[]; folderIds: number[] }>();
    for (const msg of snapshot) {
        const g = byAccount.get(msg.accountId) || { uids: [], folderIds: [] };
        g.uids.push(msg.uid);
        g.folderIds.push(msg.folderId);
        byAccount.set(msg.accountId, g);
    }
    for (const [accountId, g] of byAccount) {
        deleteMessages(accountId, g.uids, g.folderIds).catch((e: any) => {
            console.error(`Delete failed for ${accountId}: ${e?.message || e}`);
            if (statusSync) statusSync.textContent = `Delete sync issue (${accountId}): ${e?.message || e}`;
        });
    }
}

async function performUndo(): Promise<void> {
    const op = popUndo();
    if (!op) return;
    const statusSync = document.getElementById("status-sync");
    try {
        if (op.kind === "delete") {
            // payload is the full batch (was a single object pre-2026-06-12;
            // tolerate both shapes so an in-flight undo from an old build
            // doesn't throw).
            const msgs = Array.isArray(op.payload) ? op.payload : [op.payload];
            for (const m of msgs) {
                await undeleteMessage(m.accountId, m.uid, m.folderId);
            }
            if (statusSync) statusSync.textContent = msgs.length === 1
                ? "Message restored"
                : `Restored ${msgs.length} messages`;
            // Position at what was just restored — an undo whose effect lands
            // off-screen (or in a folder you've since left) reads as a no-op.
            // Reveal the first of the batch; the rest are in the same view.
            // On a miss revealMessage restores the prior view itself.
            const first = msgs[0];
            if (first?.folderId) {
                await revealMessage(first.accountId, first.folderId, first.uid);
                syncChromeToListView();
                return;
            }
        } else if (op.kind === "move") {
            const { messages } = op.payload;
            const byDest = new Map<string, { accountId: string; folderId: number; uids: number[] }>();
            for (const m of messages) {
                const key = `${m.accountId}:${m.sourceFolderId}`;
                if (!byDest.has(key)) byDest.set(key, { accountId: m.accountId, folderId: m.sourceFolderId, uids: [] });
                byDest.get(key)!.uids.push(m.uid);
            }
            const { moveMessages, moveMessage } = await import("./lib/api-client.js");
            for (const group of byDest.values()) {
                if (group.uids.length === 1) await moveMessage(group.accountId, group.uids[0], group.folderId);
                else await moveMessages(group.accountId, group.uids, group.folderId);
            }
            if (statusSync) statusSync.textContent = `Undid move of ${messages.length} message${messages.length !== 1 ? "s" : ""}`;
            // Same positioning rule as undo-delete: show the message back in
            // its original folder.
            const firstMoved = messages[0];
            if (firstMoved?.sourceFolderId) {
                await revealMessage(firstMoved.accountId, firstMoved.sourceFolderId, firstMoved.uid);
                syncChromeToListView();
                return;
            }
        } else if (op.kind === "flag") {
            const { updateFlags } = await import("./lib/api-client.js");
            // Restore each row's prior \Flagged state. Mutates message-state
            // flags arrays directly via the per-row helper so the list paints
            // immediately; the daemon STORE happens via updateFlags.
            for (const entry of op.payload) {
                const flag = "\\Flagged";
                const flagsAfter = entry.prevFlagged
                    ? [flag] // simplest restore — full set is unknown here
                    : [];
                await updateFlags(entry.accountId, entry.uid, flagsAfter, entry.folderId);
                setRowFlagged(entry.accountId, entry.uid, entry.prevFlagged);
                messageState.updateMessageFlags(entry.accountId, entry.uid, flagsAfter);
            }
            if (statusSync) statusSync.textContent = `Undid flag change on ${op.payload.length} message${op.payload.length !== 1 ? "s" : ""}`;
        }
        reloadCurrentFolder();
    } catch (e: any) {
        console.error(`Undo failed: ${e.message}`);
        if (statusSync) statusSync.textContent = `Undo failed: ${e.message}`;
    }
}

// Listen for the "mailx-moved" custom event emitted by folder-tree's drop
// handler so Ctrl+Z can reverse the most recent move.
document.addEventListener("mailx-moved", (e: any) => {
    pushUndo({ kind: "move", at: Date.now(), payload: e.detail as MovedBatch });
});

// Bulk flag/unflag (Filtered menu, row context menu) emits "mailx-flagged-batch"
// with the per-row prior state so Ctrl+Z restores the whole batch at once.
document.addEventListener("mailx-flagged-batch", (e: any) => {
    const payload = e.detail as UndoFlagEntry[];
    if (Array.isArray(payload) && payload.length) {
        pushUndo({ kind: "flag", at: Date.now(), payload });
    }
});

// ── Right-button drag → Move / Copy menu ───────────────────────────────
// Browser drag-and-drop is left-button only, so a RIGHT-button drag is a
// custom mouse-tracked gesture: right-press a message row, drag onto a folder
// in the tree, release → a menu offers Move (default, bold) or Copy. A plain
// left drag still moves directly; a plain right-CLICK (no movement) still opens
// the normal message menu — only a right-press-AND-drag triggers this.
(() => {
    type RDMsg = { accountId: string; uid: number; folderId: number };
    const SLOP = 6;
    let cand: { msgs: RDMsg[]; x: number; y: number } | null = null;
    let active = false;
    let ghost: HTMLElement | null = null;
    let suppressContext = false;

    // Cursor + ghost + drop-highlight styles (self-contained).
    const style = document.createElement("style");
    style.textContent =
        "body.rmf-rdrag, body.rmf-rdrag * { cursor: grabbing !important; }" +
        ".rmf-rdrag-ghost { position: fixed; z-index: 100000; pointer-events: none;" +
        " background: var(--color-accent, #1a6dd4); color: #fff; padding: 2px 9px;" +
        " border-radius: 4px; font: 12px system-ui; box-shadow: 0 2px 8px rgba(0,0,0,.3); }" +
        ".ft-folder.rmf-rdrag-over { outline: 2px solid var(--color-accent, #1a6dd4); outline-offset: -2px; border-radius: 4px; }";
    document.head.appendChild(style);

    const folderAt = (x: number, y: number): HTMLElement | null =>
        ((document.elementFromPoint(x, y) as HTMLElement | null)?.closest?.(".ft-folder") as HTMLElement | null) || null;
    const clearOver = (): void =>
        document.querySelectorAll(".ft-folder.rmf-rdrag-over").forEach(el => el.classList.remove("rmf-rdrag-over"));
    const cleanup = (): void => {
        active = false; cand = null;
        document.body.classList.remove("rmf-rdrag");
        ghost?.remove(); ghost = null;
        clearOver();
    };

    document.addEventListener("mousedown", (e) => {
        if (e.button !== 2) return;
        const row = (e.target as HTMLElement)?.closest?.(".ml-row") as HTMLElement | null;
        if (!row) return;
        const pressed: RDMsg = {
            accountId: row.dataset.accountId || "",
            uid: Number(row.dataset.uid),
            folderId: Number(row.dataset.folderId),
        };
        if (!pressed.uid) return;
        // Drag the current multi-selection if the pressed row is in it;
        // otherwise just the pressed row.
        let msgs = getSelectedMessages();
        if (!msgs.some(m => m.accountId === pressed.accountId && m.uid === pressed.uid)) msgs = [pressed];
        cand = { msgs, x: e.clientX, y: e.clientY };
        active = false;
    }, true);

    document.addEventListener("mousemove", (e) => {
        if (!cand) return;
        if (!active) {
            if (Math.abs(e.clientX - cand.x) < SLOP && Math.abs(e.clientY - cand.y) < SLOP) return;
            active = true;
            document.body.classList.add("rmf-rdrag");
            ghost = document.createElement("div");
            ghost.className = "rmf-rdrag-ghost";
            ghost.textContent = cand.msgs.length === 1 ? "1 message" : `${cand.msgs.length} messages`;
            document.body.appendChild(ghost);
        }
        if (ghost) { ghost.style.left = `${e.clientX + 14}px`; ghost.style.top = `${e.clientY + 8}px`; }
        clearOver();
        folderAt(e.clientX, e.clientY)?.classList.add("rmf-rdrag-over");
    }, true);

    document.addEventListener("mouseup", (e) => {
        if (!cand) return;
        const wasActive = active;
        const msgs = cand.msgs;
        const folder = wasActive ? folderAt(e.clientX, e.clientY) : null;
        const mx = e.clientX, my = e.clientY;
        cleanup();
        if (!wasActive) return;          // a right-click, not a drag
        suppressContext = true;          // eat the contextmenu that fires next
        if (!folder) return;             // released off any folder
        e.preventDefault(); e.stopPropagation();
        const targetAccount = folder.dataset.accountId || "";
        const targetFolderId = Number(folder.dataset.folderId);
        const targetName = folder.querySelector(".ft-folder-name")?.textContent?.trim() || "folder";
        void showRightDragMenu(mx, my, msgs, targetAccount, targetFolderId, targetName);
    }, true);

    // Suppress the folder/message context menu that would otherwise pop on the
    // right-button release after a drag.
    document.addEventListener("contextmenu", (e) => {
        if (suppressContext) { e.preventDefault(); e.stopPropagation(); suppressContext = false; }
    }, true);

    async function showRightDragMenu(x: number, y: number, msgs: RDMsg[], targetAccount: string, targetFolderId: number, targetName: string): Promise<void> {
        const { showContextMenu } = await import("./components/context-menu.js");
        const n = msgs.length;
        const label = n === 1 ? "message" : `${n} messages`;
        showContextMenu(x, y, [
            { label: `Move ${label} here`, emphasized: true, action: () => void runRightDragOp(msgs, targetAccount, targetFolderId, targetName, "move") },
            { label: `Copy ${label} here`, action: () => void runRightDragOp(msgs, targetAccount, targetFolderId, targetName, "copy") },
        ]);
    }

    async function runRightDragOp(msgs: RDMsg[], targetAccount: string, targetFolderId: number, targetName: string, op: "move" | "copy"): Promise<void> {
        const status = document.getElementById("status-sync");
        const api = await import("./lib/api-client.js");
        const byAccount = new Map<string, { uids: number[]; folderIds: number[] }>();
        for (const m of msgs) {
            const g = byAccount.get(m.accountId) || { uids: [], folderIds: [] };
            g.uids.push(m.uid); g.folderIds.push(m.folderId);
            byAccount.set(m.accountId, g);
        }
        if (op === "move") {
            // Optimistic remove + undo, mirroring the left-drag drop handler.
            removeMessagesAndReconcile(msgs);
            document.dispatchEvent(new CustomEvent("mailx-moved", {
                detail: { messages: msgs.map(m => ({ accountId: m.accountId, uid: m.uid, sourceFolderId: m.folderId })) },
            }));
            for (const [src, g] of byAccount) {
                const targetAcct = src === targetAccount ? undefined : targetAccount;
                api.moveMessages(src, g.uids, targetFolderId, g.folderIds, targetAcct)?.catch?.((err: any) => {
                    if (status) status.textContent = `Move failed: ${err?.message || err}`;
                });
            }
            if (status) status.textContent = `Moved ${msgs.length} to ${targetName} — Ctrl+Z to undo`;
        } else {
            let skippedXacct = false;
            for (const [src, g] of byAccount) {
                if (src !== targetAccount) { skippedXacct = true; continue; }  // copy is same-account for now
                api.copyMessages(src, g.uids, g.folderIds, targetFolderId)?.catch?.((err: any) => {
                    if (status) status.textContent = `Copy failed: ${err?.message || err}`;
                });
            }
            if (status) status.textContent = skippedXacct
                ? `Copy across accounts isn't supported yet`
                : `Copying ${msgs.length} to ${targetName}…`;
        }
    }
})();

document.getElementById("btn-delete")?.addEventListener("click", deleteSelection);
// Same handlers also bound to the top-toolbar icons so delete/spam work
// regardless of whether a message is open in the viewer. Useful for quick
// triage from a list-only view. Top trash button uses deleteSelection so
// it follows the user's task selection when present (otherwise messages).
document.getElementById("btn-tb-delete")?.addEventListener("click", deleteSelection);
document.getElementById("btn-tb-spam")?.addEventListener("click", spamSelectedMessages);

// ── Flag toggle ──
/** Sync the toolbar Flag button (glyph + gold colour) to the viewed
 *  message's flagged state, so it mirrors the message-list row star. */
function updateFlagButton(): void {
    const btn = document.getElementById("btn-flag");
    if (!btn) return;
    const sel = getCurrentFocused();
    const yes = !!sel && flaggedOf(sel);
    btn.classList.toggle("flagged", yes);
    btn.textContent = yes ? "★" : "☆";
}
document.addEventListener("mailx-message-shown", updateFlagButton);

/** Bulk-flag the current message-list selection (toolbar ★). If every
 *  selected row is already flagged, unflag them all; otherwise flag any
 *  unflagged rows. Pushes a single undo entry capturing each row's prior
 *  state so Ctrl+Z restores the exact mix. Falls back to the viewer's
 *  focused message when no list selection exists. */
async function bulkFlagSelected(): Promise<void> {
    const selected = getSelectedMessages();
    if (selected.length === 0) {
        const cur = getCurrentMessage();
        if (!cur) return;
        selected.push({ accountId: cur.accountId, uid: cur.message.uid, folderId: cur.message.folderId });
    }
    const statusSync = document.getElementById("status-sync");
    // Pull current message objects from state so we know each row's existing
    // flags + flagged state. Anything not in current state is silently skipped.
    const messages = messageState.getMessages();
    const byKey = new Map<string, any>();
    for (const m of messages) byKey.set(`${m.accountId}:${m.uid}`, m);
    const rows = selected
        .map(s => byKey.get(`${s.accountId}:${s.uid}`))
        .filter(Boolean) as any[];
    if (rows.length === 0) return;
    const anyUnflagged = rows.some(r => !flaggedOf(r));
    const targetFlagged = anyUnflagged; // mixed or all-unflagged → flag; all-flagged → unflag
    const undoPayload: UndoFlagEntry[] = rows.map(r => ({
        accountId: r.accountId, uid: r.uid, prevFlagged: flaggedOf(r), folderId: (r as any).folderId,
    }));
    const { updateFlags } = await import("./lib/api-client.js");
    for (const r of rows) {
        if (flaggedOf(r) === targetFlagged) continue;
        setFlagged(r, targetFlagged);
        setRowFlagged(r.accountId, r.uid, targetFlagged);
        messageState.updateMessageFlags(r.accountId, r.uid, r.flags);
        try { await updateFlags(r.accountId, r.uid, r.flags, (r as any).folderId); }
        catch (e: any) { console.error(`Bulk flag failed for ${r.accountId}/${r.uid}: ${e?.message || e}`); }
    }
    pushUndo({ kind: "flag", at: Date.now(), payload: undoPayload });
    updateFlagButton();
    if (statusSync) {
        const verb = targetFlagged ? "Flagged" : "Unflagged";
        statusSync.textContent = `${verb} ${rows.length} message${rows.length !== 1 ? "s" : ""} — Ctrl+Z to undo`;
    }
}
document.getElementById("btn-tb-flag")?.addEventListener("click", bulkFlagSelected);

document.getElementById("btn-flag")?.addEventListener("click", async () => {
    const sel = getCurrentFocused();
    if (!sel) return;
    const wasFlagged = flaggedOf(sel);
    setFlagged(sel, !wasFlagged);
    updateFlagButton();
    try {
        await updateFlags(sel.accountId, sel.uid, sel.flags, (sel as any).folderId);
        messageState.updateMessageFlags(sel.accountId, sel.uid, sel.flags);
        // Row owns its own DOM \u2014 go through the row object so class + star
        // update atomically and the list/preview stay in sync.
        setRowFlagged(sel.accountId, sel.uid, !wasFlagged);
    } catch (e: unknown) {
        // Revert local state on failure so visual + data stay consistent.
        setFlagged(sel, wasFlagged);
        updateFlagButton();
        console.error(`Flag toggle failed: ${(e as Error).message}`);
    }
});

async function spamSelectedMessages(): Promise<void> {
    console.log("[spam] click — finding selection");
    const selected = getSelectedMessages();
    if (selected.length === 0) {
        const current = getCurrentMessage();
        if (!current) {
            console.warn("[spam] no message selected and none in viewer — nothing to do");
            alert("No message selected. Click a message first, then the spam button.");
            return;
        }
        selected.push({ accountId: current.accountId, uid: current.message.uid, folderId: current.message.folderId });
    }
    console.log(`[spam] marking ${selected.length} message(s):`, selected);
    const statusSync = document.getElementById("status-sync");
    // Optimistic: remove from list immediately so the user sees action happen.
    // If the IPC fails, put them back. This matches local-first — the server
    // sync is a background detail, the user's action should feel instant.
    const snapshot = [...selected];
    removeMessagesAndReconcile(selected);
    // Fire-and-forget per local-first: the optimistic remove above has
    // already updated the UI; the service-side move is sync DB + queued
    // IMAP. An IPC 120s timeout here doesn't mean the move failed — it
    // means the response is stuck behind a long-running prior op (e.g.
    // simpleParser blocking the event loop). The local commit and server
    // sync still happen. Surfacing it as a failure with an alert lies to
    // the user; the next folder reload reconciles either way.
    // Collect per-message source folderId alongside the uid. IMAP UIDs are
    // per-folder, so the service must be told which folder each uid lives in —
    // a bare (account, uid) lookup can resolve to a same-numbered uid in
    // another folder, move the wrong message (or no-op), and leave the real
    // one in INBOX so it reappears on the next list refresh.
    const byAccount = new Map<string, { uids: number[]; folderIds: number[] }>();
    for (const msg of snapshot) {
        const g = byAccount.get(msg.accountId) || { uids: [], folderIds: [] };
        g.uids.push(msg.uid);
        g.folderIds.push(msg.folderId);
        byAccount.set(msg.accountId, g);
    }
    if (statusSync) statusSync.textContent = `Spam: ${snapshot.length} queued — pending server sync`;
    for (const [accountId, { uids, folderIds }] of byAccount) {
        markAsSpamMessages(accountId, uids, folderIds)
            .then(result => {
                console.log(`[spam] ${accountId}: moved ${result?.moved ?? uids.length} to folderId=${result?.targetFolderId}`);
            })
            .catch(e => {
                console.error(`[spam] ${accountId} failed:`, e);
                if (statusSync) statusSync.textContent = `Spam sync issue (${accountId}): ${e?.message || e}`;
            });
    }
}

document.getElementById("btn-spam")?.addEventListener("click", spamSelectedMessages);

/** Show/hide the Spam button based on whether the current account has "spam" configured. */
async function refreshSpamButtonVisibility(): Promise<void> {
    const btn = document.getElementById("btn-spam") as HTMLButtonElement | null;
    if (!btn) return;
    const current = getCurrentMessage();
    const accountId = current?.accountId || currentAccountId;
    if (!accountId) { btn.hidden = true; return; }
    try {
        const accounts = await getAccounts();
        const acct = accounts.find((a: any) => a.id === accountId);
        btn.hidden = !acct?.spam;
    } catch { btn.hidden = true; }
}

document.addEventListener("mailx-message-shown", refreshSpamButtonVisibility);
document.addEventListener("mailx-folder-changed", refreshSpamButtonVisibility);

// Q100 placeholder — append a row to ~/.mailx/spam.csv for later analysis.
// No folder move, no flag change, no auto-delete. Button is always visible
// (no configuration required; unlike btn-spam which needs a junk folder).
document.getElementById("btn-spam-report")?.addEventListener("click", async () => {
    const current = getCurrentMessage();
    const msg = current?.message;
    const accountId = current?.accountId;
    if (!msg || !accountId) return;
    const btn = document.getElementById("btn-spam-report") as HTMLButtonElement;
    const originalLabel = btn.textContent;
    btn.disabled = true;
    btn.textContent = "…";
    try {
        const { recordSpamReport } = await import("./lib/api-client.js");
        await recordSpamReport(accountId, msg.uid, msg.folderId);
        btn.textContent = "✓";
        const status = document.getElementById("status-sync");
        if (status) status.textContent = "Logged to ~/.rmfmail/spam.csv";
        setTimeout(() => { btn.textContent = originalLabel; btn.disabled = false; }, 1500);
    } catch (e: any) {
        btn.textContent = "✗";
        const status = document.getElementById("status-sync");
        if (status) status.textContent = `Spam log failed: ${e?.message || e}`;
        setTimeout(() => { btn.textContent = originalLabel; btn.disabled = false; }, 2500);
    }
});

document.getElementById("btn-compose")?.addEventListener("click", () => openCompose("new"));

document.getElementById("btn-mark-unread")?.addEventListener("click", () => {
    // Toggle \Seen on the currently-focused message. Mirrors the R
    // keyboard shortcut and the right-click "Mark unread" menu item, but
    // as a visible toolbar button so users discover the behavior.
    const sel = getCurrentFocused();
    if (!sel) return;
    const wasSeen = seenOf(sel);
    setSeen(sel, !wasSeen);
    updateFlags(sel.accountId, sel.uid, sel.flags, (sel as any).folderId).then(() => {
        messageState.updateMessageFlags(sel.accountId, sel.uid, sel.flags);
        const row = document.querySelector(`.ml-row[data-uid="${sel.uid}"][data-account-id="${sel.accountId}"]`) as HTMLElement;
        if (row) row.classList.toggle("unread", wasSeen);
    }).catch(() => { setSeen(sel, wasSeen); });
});

document.getElementById("btn-reply")?.addEventListener("click", () => openCompose("reply"));
document.getElementById("btn-reply-all")?.addEventListener("click", () => openCompose("replyAll"));
document.getElementById("btn-forward")?.addEventListener("click", () => openCompose("forward"));
document.getElementById("btn-edit-as-new")?.addEventListener("click", () => openCompose("editAsNew"));

// ── Icon rail wiring ──
// Rail is the always-visible vertical bar on the far left (Thunderbird/Dovecot
// style). Mostly mirrors toolbar/menu actions for one-click access; calendar /
// tasks / contacts buttons are placeholders until those features ship.
document.getElementById("rail-compose")?.addEventListener("click", () => openCompose("new"));
document.getElementById("rail-inbox")?.addEventListener("click", () => {
    // Trigger the existing folder-tree click on the first inbox folder.
    const inbox = document.querySelector('.folder-tree .folder-item[data-special-use="inbox"]') as HTMLElement | null;
    inbox?.click();
});
document.getElementById("rail-unified")?.addEventListener("click", () => {
    const unified = document.querySelector('.folder-tree .all-inboxes') as HTMLElement | null
        || document.getElementById("ft-all-inboxes");
    unified?.click();
});
document.getElementById("rail-contacts")?.addEventListener("click", async () => {
    const { openAddressBook } = await import("./components/address-book.js");
    openAddressBook();
    setRailActive("rail-contacts");
});
// Q114 decided 2026-04-24: full-screen calendar/tasks modals are
// temporarily retired — the right-docked sidebar (calendar-sidebar.ts)
// owns both views. Rail buttons now just reveal the sidebar. Files kept
// (`calendar.ts`, `tasks.ts`) for potential revival; not imported.
document.getElementById("rail-calendar")?.addEventListener("click", async () => {
    const { showCalendarSidebar } = await import("./components/calendar-sidebar.js");
    await showCalendarSidebar();
    // Flip the View-menu checkbox so the on-state stays coherent across paths.
    const optSidebar = document.getElementById("opt-calendar-sidebar") as HTMLInputElement | null;
    if (optSidebar) optSidebar.checked = true;
    setRailActive("rail-calendar");
});
document.getElementById("rail-tasks")?.addEventListener("click", async () => {
    const { showCalendarSidebar } = await import("./components/calendar-sidebar.js");
    await showCalendarSidebar();
    // Scroll the sidebar to the tasks section if possible.
    document.getElementById("cal-side-tasks")?.scrollIntoView({ block: "start", behavior: "smooth" });
    const optSidebar = document.getElementById("opt-calendar-sidebar") as HTMLInputElement | null;
    if (optSidebar) optSidebar.checked = true;
    setRailActive("rail-tasks");
});
/** Open a toolbar dropdown (View / Settings) anchored to a rail icon.
 *
 *  In wide mode the toolbar buttons own the menu and do their own toggle.
 *  In narrow mode the toolbar is `display:none`, so the dropdown — which
 *  lives as a child of `.tb-menu` inside the toolbar — is invisible no
 *  matter what `hidden` is set to. Forwarding the rail click to the
 *  toolbar button toggled `hidden` but the user still saw nothing, hence
 *  the "setup icon does nothing" bug report.
 *
 *  The fix: when a rail icon opens the menu, reparent the dropdown to
 *  `<body>` (so the toolbar's display:none can't hide it), switch to
 *  `position: fixed` anchored to the icon, and let the existing menu
 *  handlers fire normally — same dropdown DOM, same listeners, same
 *  content. We never put the dropdown back: the toolbar handler also
 *  works on a body-attached dropdown (it's just toggling .hidden), and
 *  re-parenting on every toggle would defeat any focus state inside. */
function openMenuFromRail(dropdownId: string, _anchor: HTMLElement): void {
    const dd = document.getElementById(dropdownId);
    if (!dd) return;
    if (!dd.hidden) { dd.hidden = true; return; }
    // Close sibling rail-opened menus so two don't stack.
    for (const id of ["settings-dropdown", "view-dropdown", "restart-dropdown"]) {
        const other = document.getElementById(id);
        if (other && other !== dd) other.hidden = true;
    }
    if (dd.parentElement?.classList.contains("tb-menu")) {
        document.body.appendChild(dd);
    }
    // Uniform behavior across desktop and Android: render rail-opened menus
    // as a centered modal with a dimmed backdrop. Anchor-based positioning
    // was inconsistent (worked on Android narrow but not Mac/desktop wide;
    // bottom-rail icons sit at the viewport floor and the menu spilled off
    // or landed under chrome). A centered card is always visible, always
    // dismissible by tapping the backdrop, and the same on every platform.
    dd.style.cssText = "";
    dd.style.position = "fixed";
    dd.style.top = "50%";
    dd.style.left = "50%";
    dd.style.transform = "translate(-50%, -50%)";
    dd.style.maxHeight = "85vh";
    dd.style.maxWidth = "92vw";
    dd.style.overflowY = "auto";
    dd.style.boxShadow = "0 8px 32px rgba(0,0,0,0.4)";
    dd.style.zIndex = "10000";
    dd.hidden = false;
    ensureRailMenuBackdrop();
}

/** Restore a body-parented rail-opened dropdown back to its toolbar parent
 *  and clear inline positioning, so the toolbar button's plain
 *  `dd.hidden = !dd.hidden` toggle renders the dropdown in its usual
 *  position-absolute place. Without this, the toolbar dropdown opens at
 *  the rail-modal's centered position (or somewhere else stale) the first
 *  time a user mixes rail-open and toolbar-open in the same session. */
function restoreToolbarDropdown(ddId: string, tbMenuId: string): void {
    const dd = document.getElementById(ddId);
    const tb = document.getElementById(tbMenuId);
    if (!dd || !tb) return;
    if (dd.parentElement === document.body) {
        tb.appendChild(dd);
        dd.style.cssText = "";
    }
}

/** Narrow-tier rail menus need a tap-anywhere-to-close backdrop because the
 *  rail itself stays open under the modal. The document outside-click handler
 *  hides the dropdown but doesn't visually convey "modal" — the backdrop
 *  dims the page, makes the modal look like one, and gives a single
 *  unambiguous click target for dismissal. Auto-removes when the dropdown
 *  hides (we observe the [hidden] flip via MutationObserver). */
function ensureRailMenuBackdrop(): void {
    if (document.getElementById("rail-menu-backdrop")) return;
    const bd = document.createElement("div");
    bd.id = "rail-menu-backdrop";
    bd.style.cssText = "position:fixed;inset:0;background:rgba(0,0,0,0.4);z-index:9999;";
    bd.addEventListener("click", () => {
        for (const id of ["settings-dropdown", "view-dropdown", "restart-dropdown"]) {
            const dd = document.getElementById(id);
            if (dd && !dd.hidden) dd.hidden = true;
        }
        bd.remove();
    });
    document.body.appendChild(bd);
    // Self-clean when no rail menu is open
    const cleanup = () => {
        const anyOpen = ["settings-dropdown", "view-dropdown", "restart-dropdown"]
            .some(id => { const el = document.getElementById(id); return el && !el.hidden; });
        if (!anyOpen) bd.remove();
    };
    const obs = new MutationObserver(cleanup);
    for (const id of ["settings-dropdown", "view-dropdown", "restart-dropdown"]) {
        const el = document.getElementById(id);
        if (el) obs.observe(el, { attributes: true, attributeFilter: ["hidden"] });
    }
}

document.getElementById("rail-settings")?.addEventListener("click", (e) => {
    e.stopPropagation();
    openMenuFromRail("settings-dropdown", e.currentTarget as HTMLElement);
});
document.getElementById("rail-view")?.addEventListener("click", (e) => {
    e.stopPropagation();
    openMenuFromRail("view-dropdown", e.currentTarget as HTMLElement);
});
document.getElementById("rail-help")?.addEventListener("click", () => {
    document.getElementById("btn-about")?.click();
});
// rail-theme button removed 2026-05-04 — theme picker lives in the Settings
// dropdown (collapsed into a single "Theme: <current> ▸" item with a popout).
// Removing the dedicated rail icon was the user's call; keeping the radios in
// Settings is enough since the picker is rare-use.

function applyTheme(theme: "system" | "light" | "dark"): void {
    document.documentElement.setAttribute("data-theme", theme);
    try { localStorage.setItem("mailx-theme", theme); } catch { /* private mode */ }
    // Reflect in the Settings menu radios so the two paths stay in sync.
    const radio = document.getElementById(`opt-theme-${theme}`) as HTMLInputElement | null;
    if (radio) radio.checked = true;
    // Update the submenu's "current" badge so the user sees the choice
    // without expanding.
    const cur = document.getElementById("theme-submenu-current");
    if (cur) cur.textContent = theme.charAt(0).toUpperCase() + theme.slice(1);
}

/** Set the document root font-size in px. The CSS uses rem-based sizes, so
 *  changing the root scales the entire UI proportionally. Persisted to
 *  localStorage and to preferences.jsonc.ui.fontSize (the latter syncs
 *  across devices via GDrive). The pref already existed in the schema
 *  with a 15px default — it just wasn't wired up. */
function applyFontSize(px: number): void {
    const clamped = Math.max(10, Math.min(24, px));
    document.documentElement.style.fontSize = `${clamped}px`;
    try { localStorage.setItem("mailx-font-size", String(clamped)); } catch { /* private mode */ }
}

// Restore saved font size from localStorage on startup. preferences.jsonc
// is the cloud-synced source of truth but reading it requires async IPC;
// localStorage gives us a synchronous, instant first paint at the right
// size. The async path can update later if the cloud value differs.
(() => {
    const saved = (() => {
        try { return parseInt(localStorage.getItem("mailx-font-size") || "15", 10); } catch { return 15; }
    })();
    applyFontSize(Number.isFinite(saved) ? saved : 15);
})();

// Restore saved theme + wire the Settings radios. Defaults to "system".
(() => {
    const saved = (() => { try { return localStorage.getItem("mailx-theme") || "system"; } catch { return "system"; } })();
    applyTheme(saved as "system" | "light" | "dark");
    for (const t of ["system", "light", "dark"] as const) {
        document.getElementById(`opt-theme-${t}`)?.addEventListener("change", (e) => {
            if ((e.target as HTMLInputElement).checked) applyTheme(t);
        });
    }
    // Wire the submenu toggle. Click to expand; click outside / on a radio closes.
    const togBtn = document.getElementById("theme-submenu-toggle");
    const popout = document.getElementById("theme-submenu-popout");
    if (togBtn && popout) {
        togBtn.addEventListener("click", (e) => {
            e.stopPropagation();
            const open = popout.hidden;
            popout.hidden = !open;
            togBtn.setAttribute("aria-expanded", String(open));
        });
        // Close on radio change so the menu doesn't linger after the choice.
        popout.querySelectorAll<HTMLInputElement>('input[type="radio"]').forEach(r => {
            r.addEventListener("change", () => {
                popout.hidden = true;
                togBtn.setAttribute("aria-expanded", "false");
            });
        });
    }
})();
// Highlight the current rail target. For now just inbox is the default; once
// calendar/tasks ship, update this on view change.
function setRailActive(id: string): void {
    document.querySelectorAll(".rail-btn[data-active]").forEach(el => el.removeAttribute("data-active"));
    document.getElementById(id)?.setAttribute("data-active", "true");
}
document.addEventListener("mailx-folder-changed", () => setRailActive("rail-inbox"));

// Context menu events from message-list right-click
document.addEventListener("mailx-compose", ((e: CustomEvent) => {
    if (e.detail.mode === "draft" && sessionStorage.getItem("composeInit")) {
        // Draft already stored by viewer — just show overlay
        showComposeOverlay();
    } else {
        openCompose(e.detail.mode);
    }
}) as EventListener);
document.addEventListener("mailx-delete", () => deleteSelection());

// Share / mailto intents (Android-side: MainActivity routes share-sheet
// invocations and mailto: links here. Desktop has no equivalent today —
// MSIX / Windows share-target requires packaging changes we deferred).
// Payload: { action, text, subject, mailto }. Mailto URLs are parsed
// (RFC 2368: mailto:to?subject=...&body=...&cc=...&bcc=...) and used to
// pre-fill compose. Plain shares (text/url) drop into the body / subject.
document.addEventListener("mailx-share-intent", ((e: CustomEvent) => {
    const detail = e.detail || {};
    const init: any = { mode: "new", to: [], cc: [], bcc: [], subject: "", bodyHtml: "" };
    if (detail.mailto) {
        try {
            const url = new URL(detail.mailto);
            const to = decodeURIComponent(url.pathname).split(",").map((s: string) => s.trim()).filter(Boolean);
            init.to = to;
            const sp = url.searchParams;
            const subj = sp.get("subject"); if (subj) init.subject = subj;
            const body = sp.get("body"); if (body) init.bodyHtml = `<p>${body.replace(/\n/g, "<br>")}</p>`;
            const cc = sp.get("cc"); if (cc) init.cc = cc.split(",").map(s => s.trim()).filter(Boolean);
            const bcc = sp.get("bcc"); if (bcc) init.bcc = bcc.split(",").map(s => s.trim()).filter(Boolean);
        } catch { /* malformed mailto: — drop the URL into body so user can fix */ init.bodyHtml = `<p>${escapeHtml(detail.mailto)}</p>`; }
    } else {
        if (detail.subject) init.subject = detail.subject;
        if (detail.text) init.bodyHtml = `<p>${escapeHtml(String(detail.text)).replace(/\n/g, "<br>")}</p>`;
    }
    // Shared files (screenshots, PDFs, .eml, …) arrive base64-encoded from the
    // Android bridge. Seed them as compose attachments — compose.ts consumes
    // init.attachments[{filename, mimeType, dataBase64}] and builds the chips.
    if (Array.isArray(detail.attachments) && detail.attachments.length) {
        init.attachments = detail.attachments.filter((a: any) => a?.filename && a?.dataBase64);
    }
    sessionStorage.setItem("composeInit", JSON.stringify(init));
    showComposeOverlay(init.subject || "Compose");
}) as EventListener);

// ── Search ──

let searchTimeout: ReturnType<typeof setTimeout>;
const searchInput = document.getElementById("search-input") as HTMLInputElement;
const searchScope = document.getElementById("search-scope") as HTMLSelectElement;

// Recent-searches dropdown — backed by localStorage, populated into the
// existing <datalist id="search-history"> so the browser's native picker
// shows on focus/click. No edit / delete UI; editing is implicit (pick a
// past query, modify, hit Enter — the modified version is recorded).
// Cap at 25 to keep the list usable.
const SEARCH_HISTORY_KEY = "mailx-search-history";
const SEARCH_HISTORY_MAX = 25;
function loadSearchHistory(): string[] {
    try { return JSON.parse(localStorage.getItem(SEARCH_HISTORY_KEY) || "[]"); }
    catch { return []; }
}
function saveSearchHistory(list: string[]): void {
    try { localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(list)); }
    catch { /* private mode */ }
}
function refreshSearchHistoryDatalist(): void {
    const dl = document.getElementById("search-history") as HTMLDataListElement | null;
    if (!dl) return;
    const items = loadSearchHistory();
    dl.innerHTML = items.map(q => `<option value="${q.replace(/"/g, "&quot;")}"></option>`).join("");
}
function recordSearchHistory(query: string): void {
    const trimmed = query.trim();
    if (!trimmed) return;
    const cur = loadSearchHistory();
    // Move-to-front dedup, AND drop any entry that is a prefix of this
    // query — that collapses the in-progress typing ("Hod", "Hodd") into
    // the final "Hoddie", so a live search the user never pressed Enter on
    // still lands in history exactly once.
    const filtered = cur.filter(q => q !== trimmed && !trimmed.startsWith(q));
    filtered.unshift(trimmed);
    if (filtered.length > SEARCH_HISTORY_MAX) filtered.length = SEARCH_HISTORY_MAX;
    saveSearchHistory(filtered);
    refreshSearchHistoryDatalist();
}
refreshSearchHistoryDatalist();

// ── Live search-syntax highlighting ──
// An overlay behind the search input colors recognized qualifiers
// (from:/to:/subject:/date:/after:/before:/has:/is:/folder:), FTS boolean
// operators (OR/NOT/AND), quoted phrases, and /regex/ literals — reparsed
// on every keystroke so the user sees how mailx will interpret the query.
// An invalid /regex/ only goes red after a brief settle delay, so it
// doesn't flash red while the pattern is still being typed.
const searchHl = document.getElementById("search-hl");
const SEARCH_QUALIFIERS = new Set(["from", "to", "subject", "date", "after", "before", "has", "is", "folder"]);
const SEARCH_REGEX_SETTLE_MS = 500;
let searchRegexSettleTimer: ReturnType<typeof setTimeout> | null = null;

function escapeHl(s: string): string {
    return s.replace(/[&<>]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c] || c));
}

function renderSearchHighlight(settled: boolean): void {
    if (!searchHl || !searchInput) return;
    const text = searchInput.value;
    // Tokenize keeping whitespace runs so the overlay lines up glyph-for-
    // glyph with the input's own text.
    const tokens = text.match(/\s+|"[^"]*"?|\S+/g) || [];
    let html = "";
    for (const tok of tokens) {
        if (/^\s+$/.test(tok)) { html += escapeHl(tok); continue; }
        // /regex/ literal — leading slash, body, optional closing slash.
        if (tok.startsWith("/") && tok.length > 1) {
            let bad = false;
            if (tok.endsWith("/")) {
                try { new RegExp(tok.slice(1, -1), "i"); } catch { bad = true; }
            }
            const cls = (bad && settled) ? "sh-regex-bad" : "sh-regex";
            html += `<span class="${cls}">${escapeHl(tok)}</span>`;
            continue;
        }
        // qualifier:value — color the keyword: prefix when recognized.
        const qm = tok.match(/^([a-z]+):(.*)$/i);
        if (qm && SEARCH_QUALIFIERS.has(qm[1].toLowerCase())) {
            html += `<span class="sh-key">${escapeHl(qm[1] + ":")}</span>${escapeHl(qm[2])}`;
            continue;
        }
        // FTS boolean operator.
        if (/^(OR|NOT|AND)$/.test(tok)) {
            html += `<span class="sh-op">${escapeHl(tok)}</span>`;
            continue;
        }
        // Quoted phrase.
        if (tok.startsWith("\"")) {
            html += `<span class="sh-phrase">${escapeHl(tok)}</span>`;
            continue;
        }
        html += escapeHl(tok);
    }
    searchHl.innerHTML = html;
    searchHl.scrollLeft = searchInput.scrollLeft;
}

function updateSearchHighlight(): void {
    renderSearchHighlight(false);
    if (searchRegexSettleTimer) clearTimeout(searchRegexSettleTimer);
    searchRegexSettleTimer = setTimeout(() => renderSearchHighlight(true), SEARCH_REGEX_SETTLE_MS);
}

// Keep the overlay scroll-aligned when the input scrolls horizontally,
// and seed it once at startup.
searchInput?.addEventListener("scroll", () => {
    if (searchHl) searchHl.scrollLeft = searchInput.scrollLeft;
});
searchInput?.addEventListener("change", () => updateSearchHighlight());
updateSearchHighlight();

// Server search is slow (per-folder IMAP SEARCH across ~90 folders). It must
// never gate the local result display, and it should only fire once typing
// has settled. Local search runs immediately on every doSearch; the server
// pass is scheduled `SERVER_SEARCH_DALLY_MS` later and any new keystroke
// clears+reschedules it. message-list's loadGen counter discards a stale
// server pass's results, so a restart is effectively an abort at the UI.
const SERVER_SEARCH_DALLY_MS = 700;
let serverSearchTimer: ReturnType<typeof setTimeout> | null = null;

function doSearch(immediate = false): void {
    const query = searchInput.value.trim();
    if (query.length === 0) {
        // Leaving search — ABORT any in-flight server sweep. A "Server" search is
        // a 90-folder IMAP run on the daemon; if it's still going when you clear
        // the box, it keeps churning the sync worker + connections and starves
        // the read worker, so the folder reload's getUnifiedInbox ballooned from
        // ~200ms to 4.7s and the IPC timed out (Bob 2026-06-19). cancelServerSearch
        // bumps the daemon's serverSearchGen so the sweep loop bails immediately.
        if (serverSearchTimer) { clearTimeout(serverSearchTimer); serverSearchTimer = null; }
        cancelServerSearch();
        reloadCurrentFolder();
        return;
    }
    if (query.length < 2 && !immediate) return;

    // P20: orthogonal "Server" checkbox. When checked, the search ALSO spans
    // every folder on the server — but as a deferred second phase, never a
    // replacement for the instant local pass.
    const serverCheck = document.getElementById("search-server-too") as HTMLInputElement | null;
    const trashCheck = document.getElementById("search-include-trash") as HTMLInputElement | null;
    const localScope = searchScope?.value || "all";
    const includeTrash = !!trashCheck?.checked;
    const serverOn = !!serverCheck?.checked;

    // Any re-run aborts a pending server pass — it'll be rescheduled below
    // if still wanted. This is the "editing the search aborts + restarts"
    // behavior: each keystroke cancels the prior server search. clearTimeout
    // kills a not-yet-fired pass; cancelServerSearch() aborts one that's
    // already mid-sweep on the daemon (generation bump → loop bails).
    if (serverSearchTimer) { clearTimeout(serverSearchTimer); serverSearchTimer = null; }
    cancelServerSearch();

    // "This folder" scope used to be an instant client-side filter of the
    // VISIBLE rows. That was doubly wrong (Bob 2026-06-12): it only saw the
    // loaded page (so a match deeper in a big folder like Trash never showed),
    // and it did a dumb substring match that didn't understand qualifiers
    // (`from:amazon` matched the literal text and so found nothing). "This
    // folder" now runs the REAL DB search below (scope="current"), which the
    // service scopes to the current folder AND forces includeTrashSpam=true —
    // so searching inside Trash finds Trash messages, and `from:`/`subject:`
    // qualifiers work. The DB FTS pass is sub-second, so we don't lose the
    // instant feel. (Server checkbox still adds the cross-folder IMAP sweep.)

    // ── Phase 1: LOCAL search — always, immediately ──
    // The local SQLite store is fast; results paint as the user types.
    const localScopeEff = localScope === "current" ? "current" : "all";
    loadSearchResults(query, localScopeEff, currentAccountId, currentFolderId, includeTrash);
    setTitle(`${APP_NAME} - Search: ${query}`);
    setActiveTabView(
        { kind: "search", query, scope: serverOn ? "server" : localScopeEff, accountId: currentAccountId, folderId: currentFolderId, includeTrash },
        `Search: ${query}`,
    );
    // Record every executed search — Enter AND a settled debounced search
    // alike. The prefix-pruning in recordSearchHistory() collapses the
    // keystroke progression, so a search the user typed without pressing
    // Enter ("Hoddie") still shows up in history.
    recordSearchHistory(query);

    // ── Phase 2: SERVER search — deferred, augments phase 1 ──
    // Fires after the dally so a burst of keystrokes only triggers one IMAP
    // sweep. The local rows from phase 1 stay on screen the whole time
    // (loadSearchResults won't blank a list that already has rows), so the
    // server pass never makes the user stare at an empty list.
    if (serverOn) {
        serverSearchTimer = setTimeout(() => {
            serverSearchTimer = null;
            loadSearchResults(query, "server", currentAccountId, currentFolderId, includeTrash);
        }, SERVER_SEARCH_DALLY_MS);
    }
}

// Track current folder for scoped search
let currentAccountId = "";
let currentFolderId = 0;
let reloadDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let lastListReloadAt = 0;

// Search-as-find: closing search returns the user to the message they had
// selected among the results, in its own folder — not back to the pre-search
// view scrolled "who knows where" (Bob 2026-06-25). Capture the focused result
// BEFORE clearing search mode, then navigate to that message's folder and
// reselect it. With no selected result (or no folderId) fall back to the old
// reloadCurrentFolder behavior.
async function closeSearchAndReveal(): Promise<void> {
    const focused = getCurrentFocused();
    clearSearchMode();
    const body = document.getElementById("ml-body");
    if (body) body.querySelectorAll(".filter-hidden").forEach(r => r.classList.remove("filter-hidden"));
    if (focused && focused.folderId) {
        await revealMessage(focused.accountId, focused.folderId, focused.uid);
    } else {
        reloadCurrentFolder();
    }
    // Leaving search must leave search STATE behind too. Before this, the
    // active tab kept its "Search: …" view (re-running the dead query on any
    // tab re-activation) and the folder tree kept the pre-search highlight —
    // so when the reveal landed somewhere unexpected (a Drafts result, a
    // stale folderId after a server-side rebind), every indicator still
    // claimed search-on-All-Inboxes and the list looked broken (Bob
    // 2026-07-04 "cleared the search but it isn't restoring the list").
    // Sync tab, tree highlight and titles to what the list actually shows.
    syncChromeToListView();
}

/** Mirror tab, folder-tree highlight and titles to whatever view the message
 *  list is actually showing. Needed after any list-driven navigation
 *  (revealMessage from search-close or undo) — the list module can land on a
 *  different folder than the surrounding chrome still claims. */
function syncChromeToListView(): void {
    const vw = getCurrentView();
    if (vw.unified) {
        highlightFolder(null, -1);
        setActiveTabView({ kind: "unified" }, "All Inboxes");
        setTitle(`${APP_NAME} - All Inboxes`);
        setNarrowFolderTitle("All Inboxes");
    } else {
        currentAccountId = vw.accountId;
        currentFolderId = vw.folderId;
        currentFolderSpecialUse = vw.specialUse;
        const name = highlightFolder(vw.accountId, vw.folderId) || "Mail";
        setActiveTabView({ kind: "folder", accountId: vw.accountId, folderId: vw.folderId, specialUse: vw.specialUse }, name);
        setTitle(`${APP_NAME} - ${name}`);
        setNarrowFolderTitle(name);
    }
}

searchInput?.addEventListener("input", () => {
    clearTimeout(searchTimeout);
    if (serverSearchTimer) { clearTimeout(serverSearchTimer); serverSearchTimer = null; cancelServerSearch(); }
    updateSearchHighlight();
    if (searchInput.value.trim() === "") {
        // Cleared — reset immediately, no debounce. Must exit search mode
        // first; otherwise reloadCurrentFolder() sees searchMode=true and
        // re-runs the stale query (user-reported regression 2026-04-24).
        closeSearchAndReveal();
    } else {
        // Short debounce — just enough to coalesce a keystroke burst. The
        // local pass is cheap; the server pass has its own longer dally
        // inside doSearch, so this stays snappy.
        searchTimeout = setTimeout(() => doSearch(false), 180);
    }
});
searchInput?.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
        clearTimeout(searchTimeout);
        doSearch(true);
    }
    if (e.key === "Escape") {
        searchInput.value = "";
        if (serverSearchTimer) { clearTimeout(serverSearchTimer); serverSearchTimer = null; }
        cancelServerSearch();
        updateSearchHighlight();
        closeSearchAndReveal();
    }
});

// Re-run the active search when the scope dropdown or "server too" checkbox
// flips. Without this, switching all/current/server after typing the query
// left the old result set on screen — the controls looked like they did
// nothing. Treat the change as `immediate=true` so the user sees the new
// scope's results without having to retype Enter; clear any client-side
// filter-hidden flags from the prior "current folder" path so the row set
// resets cleanly.
function rerunActiveSearch(): void {
    if (!searchInput || searchInput.value.trim() === "") return;
    const body = document.getElementById("ml-body");
    if (body) body.querySelectorAll(".filter-hidden").forEach(r => r.classList.remove("filter-hidden"));
    clearTimeout(searchTimeout);
    doSearch(true);
}
searchScope?.addEventListener("change", rerunActiveSearch);
document.getElementById("search-server-too")?.addEventListener("change", rerunActiveSearch);
document.getElementById("search-include-trash")?.addEventListener("change", rerunActiveSearch);

// Message state handles move/delete — no manual event listener needed

// ── Folder filter ──
const ftFilterInput = document.getElementById("ft-filter-input") as HTMLInputElement;
if (ftFilterInput) {
    ftFilterInput.addEventListener("input", () => {
        const query = ftFilterInput.value.toLowerCase();
        const tree = document.getElementById("folder-tree");
        if (!tree) return;

        if (!query) {
            // Clear filter — show everything
            tree.querySelectorAll(".ft-filter-hidden").forEach(el => el.classList.remove("ft-filter-hidden"));
            return;
        }

        // Hide all folders first, then show matches + their parent accounts
        const folders = tree.querySelectorAll(".ft-folder");
        const accounts = tree.querySelectorAll(".ft-account");

        for (const acct of accounts) (acct as HTMLElement).classList.add("ft-filter-hidden");
        for (const f of folders) (f as HTMLElement).classList.add("ft-filter-hidden");

        for (const f of folders) {
            const name = f.querySelector(".ft-folder-name")?.textContent?.toLowerCase() || "";
            if (name.includes(query)) {
                (f as HTMLElement).classList.remove("ft-filter-hidden");
                // Show parent account
                const acct = f.closest(".ft-account");
                if (acct) (acct as HTMLElement).classList.remove("ft-filter-hidden");
            }
        }

        // Also show unified inbox if it matches
        const unified = tree.querySelector(".ft-unified");
        if (unified) {
            const text = unified.textContent?.toLowerCase() || "";
            (unified as HTMLElement).classList.toggle("ft-filter-hidden", !text.includes(query));
        }
    });

    ftFilterInput.addEventListener("keydown", (e) => {
        if (e.key === "Escape") {
            ftFilterInput.value = "";
            ftFilterInput.dispatchEvent(new Event("input"));
        }
    });
}

// ── Open links from email body in system browser ──

window.addEventListener("message", (e) => {
    // Relay traces from iframes (compose) to Node via our working bridge.
    // The iframe calls logClientEvent which tries its own bridge first; if
    // that path is broken it also posts here as backup. Tag gets a `via-relay`
    // suffix when the iframe couldn't reach its own bridge — that alone
    // diagnoses whether the iframe bridge works.
    if (e.data?.type === "mailx-trace" && typeof e.data.tag === "string") {
        const relayTag = e.data.bridged ? e.data.tag : `${e.data.tag} (via-relay)`;
        logClientEvent(relayTag, e.data.data);
        return;
    }
    // Compose-send relay: iframe posts the send request here because its own
    // bridge call to sendMessage was failing to reach Node. This window's
    // bridge is proven (getAccounts / getOutboxStatus run every few seconds
    // with no failures), so we do the IPC from here and post the result back
    // to the iframe via its source. `e.source` is the iframe's window; use it
    // so targeting works even if the iframe moves in the DOM.
    // S61 2026-04-24: parent-relay compose close. On Android the
    // window.close() override applied in `frame.onload` doesn't always fire
    // (WebView2 / MAUI WebView dispatches close to the shell in some cases),
    // leaving the compose overlay stuck after Send. postMessage is reliable;
    // compose.ts's closeCompose() posts this, and we find-and-remove the
    // overlay whose iframe window matches e.source.
    if (e.data?.type === "mailx-compose-close") {
        const src = e.source as Window | null;
        document.querySelectorAll<HTMLElement>(".compose-overlay").forEach(el => {
            const iframe = el.querySelector<HTMLIFrameElement>("iframe");
            if (!src || iframe?.contentWindow === src) el.remove();
        });
        return;
    }
    if (e.data?.type === "mailx-compose-send" && e.data.id && e.data.body) {
        const src = e.source as Window | null;
        const id = e.data.id;
        const body = e.data.body;
        logClientEvent("relay-compose-send-received", { id });
        // Drop the draft row from the list immediately on send-click. The
        // daemon-side deleteDraft local-row delete already fires + emits
        // folderCountsChanged, but that reload is debounced 2 s — long
        // enough that Bob saw "the draft is still there, did it send?".
        // Pre-empting the list now keeps the Drafts view honest the
        // moment Send is clicked.
        if (body.from && body.draftUid) {
            removeMessagesAndReconcile([{ accountId: body.from, uid: body.draftUid }]);
        }
        // Visible Sent confirmation in the status bar. The outbox-status
        // pill only shows while queued; for a fast send the pill never
        // appears and the user had nothing to confirm the click did
        // anything (Bob 2026-05-23: "There is no indication that it has
        // been sent").
        const statusSync = document.getElementById("status-sync");
        if (statusSync) {
            statusSync.textContent = `Sent ${new Date().toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false })}`;
        }
        (async () => {
            try {
                await apiSendMessage(body);
                logClientEvent("relay-compose-send-ok", { id });
                src?.postMessage({ type: "mailx-compose-send-result", id, ok: true }, "*" as any);
            } catch (err: any) {
                const msg = err?.message || String(err);
                logClientEvent("relay-compose-send-error", { id, error: msg });
                if (statusSync) statusSync.textContent = `Send failed: ${msg}`;
                src?.postMessage({ type: "mailx-compose-send-result", id, ok: false, error: msg }, "*" as any);
            }
        })();
        return;
    }
    // Generic IPC relay: the iframe's api-client routes every IPC call through
    // postMessage when it's running in a child frame. Same reason as the
    // compose-send relay — sendMessage wasn't the only method the iframe's
    // bridge dropped; saveDraft hit the same wall ("Draft save failed: mailxapi
    // timeout"). This handler invokes the named method on THIS window's
    // mailxapi and posts the result back to the iframe.
    if (e.data?.type === "mailx-ipc" && e.data.id && e.data.method) {
        const src = e.source as Window | null;
        const { id, method, args } = e.data;
        const bridge = (window as any).mailxapi;
        const fn = bridge?.[method];
        if (typeof fn !== "function") {
            src?.postMessage({ type: "mailx-ipc-result", id, ok: false, error: `parent bridge has no method "${method}"` }, "*" as any);
            return;
        }
        try {
            const result = fn.apply(bridge, args || []);
            Promise.resolve(result).then(
                (value) => src?.postMessage({ type: "mailx-ipc-result", id, ok: true, result: value }, "*" as any),
                (err) => src?.postMessage({ type: "mailx-ipc-result", id, ok: false, error: err?.message || String(err) }, "*" as any),
            );
        } catch (err: any) {
            src?.postMessage({ type: "mailx-ipc-result", id, ok: false, error: err?.message || String(err) }, "*" as any);
        }
        return;
    }
    if (e.data?.type === "openLink" && e.data.url) {
        const api = (window as any).mailxapi;
        if (api?.openExternal) api.openExternal(e.data.url);
        else window.open(e.data.url, "_blank", "noopener,noreferrer");
    }
    if (e.data?.type === "linkClick" && e.data.url) {
        const url = e.data.url;
        console.log(`[android-link] parent got linkClick → ${url}`);
        // Route through mailxapi.openExternal — on desktop it spawns the OS
        // browser via the Node process (window.open stayed LOCAL inside msger's
        // WebView2 — Bob 2026-06-13 "old bug back"); on Android it uses the
        // native intent bridge. Falls back to window.open only if the api isn't
        // present (bare browser / --server mode).
        const api = (window as any).mailxapi;
        if (api?.openExternal) api.openExternal(url);
        else window.open(url, "_blank", "noopener,noreferrer");
    }
    if (e.data?.type === "imgLoadFail") {
        // Remote images failed to load inside the preview iframe even though
        // remote content was allowed — silent before; now in the daemon log
        // with the first failing URL (Bob 2026-07-28: Nextdoor digest showed
        // every image broken while curl fetched them fine).
        try {
            logClientEvent("[viewer] remote images failed to load", {
                failed: e.data.count, total: e.data.total, first: e.data.first,
            });
        } catch { /* diagnostic only */ }
        return;
    }
    if (e.data?.type === "previewToggleFullscreen") {
        toggleFullscreenPreview();
        return;
    }
    if (e.data?.type === "previewContextMenu") {
        // Translate iframe-relative coords to viewport. Find the iframe whose
        // contentWindow matches the source so we map correctly even when
        // multiple iframes are present (compose iframe + viewer iframe).
        let iframeRect: DOMRect | null = null;
        for (const f of Array.from(document.querySelectorAll("iframe"))) {
            if ((f as HTMLIFrameElement).contentWindow === e.source) { iframeRect = f.getBoundingClientRect(); break; }
        }
        const x = (iframeRect?.left || 0) + (e.data.x || 0);
        const y = (iframeRect?.top || 0) + (e.data.y || 0);
        showPreviewBodyMenu(
            x, y,
            String(e.data.selectedText || ""),
            e.source as Window | null,
            String(e.data.linkUrl || "") || undefined,
            String(e.data.linkText || "") || undefined,
            String(e.data.imgSrc || "") || undefined,
        );
        return;
    }
    if (e.data?.type === "linkContextMenu") {
        // C29: right-click in body iframe → Open / Save / Copy URL menu.
        // Iframe's clientX/Y is relative to the iframe; translate to viewport.
        let iframeRect: DOMRect | null = null;
        for (const f of Array.from(document.querySelectorAll("iframe"))) {
            if ((f as HTMLIFrameElement).contentWindow === e.source) { iframeRect = f.getBoundingClientRect(); break; }
        }
        const x = (iframeRect?.left || 0) + (e.data.x || 0);
        const y = (iframeRect?.top || 0) + (e.data.y || 0);
        const url: string = e.data.url || "";
        // Find a sensible filename for the Save action.
        const guessName = (() => {
            try {
                const u = new URL(url);
                const last = u.pathname.split("/").pop() || "";
                return last && last.includes(".") ? last : "";
            } catch { return ""; }
        })();
        const items: { label: string; action: () => void }[] = [
            { label: "Open in browser", action: () => {
                window.open(url, "_blank", "noopener,noreferrer");
            }},
            { label: guessName ? `Save "${guessName}"…` : "Save link as…", action: () => {
                // Trigger a download via anchor with download attr.
                const a = document.createElement("a");
                a.href = url;
                if (guessName) a.download = guessName;
                else a.download = "";
                a.style.display = "none";
                document.body.appendChild(a);
                a.click();
                setTimeout(() => a.remove(), 1000);
            }},
            { label: "Copy URL", action: async () => {
                try { await navigator.clipboard.writeText(url); }
                catch { prompt("URL:", url); }
            }},
            { label: "Copy link text", action: async () => {
                try { await navigator.clipboard.writeText(e.data.text || url); }
                catch { prompt("Text:", e.data.text || url); }
            }},
        ];
        // Build a tiny inline menu (showContextMenu would do but it's in components/).
        const menu = document.createElement("div");
        menu.style.cssText = `position:fixed;z-index:2400;background:var(--color-bg);border:1px solid var(--color-border);border-radius:6px;box-shadow:0 4px 16px rgba(0,0,0,0.2);padding:4px 0;font-size:13px;min-width:180px;`;
        menu.style.left = `${Math.min(x, window.innerWidth - 200)}px`;
        menu.style.top = `${Math.min(y, window.innerHeight - 200)}px`;
        // mousedown inside the menu must NOT reach the document-level
        // dismiss handler — otherwise the menu is removed before click
        // fires on the row and the action silently no-ops (user report
        // 2026-04-24). Stop propagation at the menu root covers every row.
        menu.addEventListener("mousedown", (ev) => ev.stopPropagation());
        for (const it of items) {
            const row = document.createElement("div");
            row.textContent = it.label;
            row.style.cssText = `padding:6px 12px;cursor:pointer;`;
            row.addEventListener("mouseenter", () => row.style.background = "var(--color-bg-hover)");
            row.addEventListener("mouseleave", () => row.style.background = "");
            row.addEventListener("click", () => { menu.remove(); it.action(); });
            menu.appendChild(row);
        }
        document.body.appendChild(menu);
        const dismiss = () => { menu.remove(); document.removeEventListener("mousedown", dismiss); document.removeEventListener("keydown", dismiss); };
        setTimeout(() => {
            document.addEventListener("mousedown", dismiss);
            document.addEventListener("keydown", dismiss);
        }, 0);
        return;
    }
    if (e.data?.type === "mailx-send-error") {
        // Send failed AFTER compose closed (fire-and-forget model). Surface in
        // the status bar so the user sees something instead of the silence.
        const statusSync = document.getElementById("status-sync");
        if (statusSync) {
            statusSync.textContent = `Send failed: ${e.data.message}`;
            statusSync.style.color = "oklch(0.65 0.2 25)";
        }
        return;
    }
    if (e.data?.type === "linkHover") {
        // Cancel any pending show — every hoverover/hoverout from the iframe
        // triggers this branch. Without the timer, the popover appears
        // instantly and lingers when the user moves to do anything else,
        // including punching through the compose overlay (which sits at
        // z-index 1000 — popover was at 10000, hence the bug in the
        // screenshot). Now: 1500ms hover delay; suppressed entirely when
        // any overlay (compose, modal) is open; auto-dismissed on click,
        // scroll, blur, or any keypress.
        const w = window as any;
        if (w._linkHoverShowTimer) { clearTimeout(w._linkHoverShowTimer); w._linkHoverShowTimer = null; }
        let pop = document.getElementById("link-hover-popover") as HTMLDivElement | null;
        const hidePop = () => { if (pop) pop.style.display = "none"; };
        if (!e.data.url) { hidePop(); return; }
        // Suppress when compose / modal overlay is up — user shouldn't see
        // a tooltip for a link they can't reach without dismissing first.
        if (document.querySelector(".compose-overlay, .mailx-modal-backdrop")) { hidePop(); return; }
        const data = e.data;
        const source = e.source;
        w._linkHoverShowTimer = setTimeout(() => {
            // Re-check overlay state at fire time — overlay may have appeared
            // during the 1500ms wait.
            if (document.querySelector(".compose-overlay, .mailx-modal-backdrop")) return;
            if (!pop) {
                pop = document.createElement("div");
                pop.id = "link-hover-popover";
                // z-index 500 — above the message body iframe (no z-index)
                // but BELOW the compose overlay (z-index 1000) and modals (2000).
                pop.style.cssText = "position:fixed;z-index:500;max-width:520px;padding:6px 10px;background:var(--color-surface,#fff);color:var(--color-text,#000);border:1px solid var(--color-border,#888);border-radius:6px;box-shadow:0 4px 12px rgba(0,0,0,0.18);font-size:12px;line-height:1.4;word-break:break-all;pointer-events:none;";
                document.body.appendChild(pop);
                // One-time dismissers on the popover lifetime.
                const dismiss = () => hidePop();
                document.addEventListener("mousedown", dismiss, true);
                document.addEventListener("scroll", dismiss, true);
                document.addEventListener("keydown", dismiss, true);
                window.addEventListener("blur", dismiss);
            }
            pop.textContent = data.url;
            pop.style.display = "block";
            let iframeRect: DOMRect | null = null;
            for (const f of Array.from(document.querySelectorAll("iframe"))) {
                if ((f as HTMLIFrameElement).contentWindow === source) { iframeRect = f.getBoundingClientRect(); break; }
            }
            const r = data.rect;
            const m = (data as any).mouse;
            if (iframeRect && m) {
                // Anchor at the cursor (translated from iframe coords to the
                // parent viewport) — just below-right of the pointer, clamped
                // on-screen. Falls back to the link rect if no mouse pos.
                const x = Math.max(4, Math.min(window.innerWidth - 528, iframeRect.left + m.x + 12));
                let y = iframeRect.top + m.y + 18;
                if (y + 60 > window.innerHeight) y = Math.max(4, iframeRect.top + m.y - 60);
                pop.style.left = x + "px";
                pop.style.top = y + "px";
            } else if (iframeRect && r) {
                const x = Math.max(4, Math.min(window.innerWidth - 528, iframeRect.left + r.left));
                let y = iframeRect.top + r.bottom + 4;
                if (y + 60 > window.innerHeight) y = Math.max(4, iframeRect.top + r.top - 60);
                pop.style.left = x + "px";
                pop.style.top = y + "px";
            }
        }, 1500);
    }
    if (e.data?.type === "previewKey" && typeof e.data.key === "string") {
        // Re-dispatch as a real keydown on document so the hotkey handler
        // below runs the same code path as a list-focused keypress. Used
        // when focus is inside the sandboxed preview iframe — works on
        // platforms where parent-side contentDocument listeners don't.
        const ev = new KeyboardEvent("keydown", {
            key: e.data.key, code: e.data.code || "",
            ctrlKey: !!e.data.ctrlKey, shiftKey: !!e.data.shiftKey,
            altKey: !!e.data.altKey, metaKey: !!e.data.metaKey,
            bubbles: true, cancelable: true,
        });
        document.dispatchEvent(ev);
    }
});

// ── Splitter drag ──

const splitter = document.getElementById("splitter-h");
if (splitter) {
    // Restore saved position
    const saved = localStorage.getItem("mailx-split");
    if (saved) document.documentElement.style.setProperty("--list-viewer-split", saved);

    let dragging = false;
    let startX: number;
    let startSplit: number;

    splitter.addEventListener("pointerdown", (e: PointerEvent) => {
        dragging = true;
        startX = e.clientX;
        const mainArea = document.querySelector(".main-area") as HTMLElement;
        startSplit = mainArea.getBoundingClientRect().width * (parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--list-viewer-split")) / 100);
        splitter.setPointerCapture(e.pointerId);
    });

    splitter.addEventListener("pointermove", (e: PointerEvent) => {
        if (!dragging) return;
        const mainArea = document.querySelector(".main-area") as HTMLElement;
        const totalWidth = mainArea.getBoundingClientRect().width;
        const newSplit = ((startSplit + (e.clientX - startX)) / totalWidth) * 100;
        const clamped = Math.max(15, Math.min(85, newSplit));
        const val = `${clamped}%`;
        document.documentElement.style.setProperty("--list-viewer-split", val);
        localStorage.setItem("mailx-split", val);
    });

    splitter.addEventListener("pointerup", () => { dragging = false; });
}

// ── WebSocket for live updates ──

connectWebSocket();

// Priority-sender index — fetch once on load. Refreshed on the contacts
// configChanged event (below) so changes from another device propagate
// without a full reload.
refreshPriorityIndex().catch(() => { /* service may not be up yet */ });

onWsEvent((event) => {
    const statusSync = document.getElementById("status-sync");
    const startupStatus = document.getElementById("startup-status");

    switch (event.type) {
        case "connected":
            if (statusSync) statusSync.textContent = "Connected";
            if (startupStatus) startupStatus.textContent = "Loading accounts...";
            // Don't refresh folder tree on connect — it's already loaded by initFolderTree
            break;
        case "connectProgress":
            handleConnectProgress(event);
            break;
        case "bootstrapStatus": {
            // C156: Android first-boot narration ("Signing into <email>…",
            // "Fetching settings from Google Drive…"). Rendered in the status
            // bar, the pre-UI startup banner, AND the folder tree's loading
            // slot — the tree is the big empty thing a fresh install stares
            // at, so that's where the progress needs to be.
            const msg = String(event.message || "");
            if (msg) {
                if (statusSync) statusSync.textContent = msg;
                if (startupStatus) startupStatus.textContent = msg;
                const slot = document.querySelector(".folder-loading");
                if (slot) slot.textContent = msg;
                // The message-list pane too — on a phone the folder tree is
                // off-canvas and the viewer pane is behind the list, so the
                // tree/viewer slots above are both invisible at boot. This is
                // the pane the user is actually staring at (Bob 2026-08-01:
                // "blank screen and 'syncing' at the bottom"). `.ml-empty`
                // exists only while the list has no rows, so live message
                // rows are never overwritten.
                const mlEmpty = document.querySelector("#ml-body .ml-empty");
                if (mlEmpty) mlEmpty.textContent = msg;
                // Zero-account welcome pane (the visible one on a phone until
                // accounts land) — folder-tree.ts renders this slot.
                const narrationSlot = document.getElementById("bootstrap-narration");
                if (narrationSlot) narrationSlot.textContent = msg;
            }
            break;
        }
        case "syncProgress": {
            // Aggregate folders phases ("folders:<path>" when starting a folder,
            // "folders-done" between folders) print as a proportion so the user
            // can see forward progress instead of a meaningless "47%". Older
            // phase strings ("sync:<path>", "folders") still render raw.
            let label = `${event.phase} ${event.progress || 0}%`;
            if (typeof event.phase === "string" && event.phase.startsWith("folders:")) {
                const folderPath = event.phase.slice("folders:".length);
                label = `folders — ${folderPath} (${event.progress || 0}%)`;
            } else if (event.phase === "folders-done") {
                label = `folders ${event.progress || 0}% done`;
            }
            if (statusSync) statusSync.textContent = `Syncing ${event.accountId}: ${label}`;
            if (startupStatus) startupStatus.textContent = `Syncing ${event.accountId}: ${label}`;
            scheduleSyncedStamp();   // re-arm the settle timer; stamp "Synced" when this quiets
            // Mark syncing folder in tree — bubble up to visible parent if collapsed
            const syncPath = event.phase?.startsWith("sync:") ? event.phase.slice(5) : null;
            // Clear previous syncing markers for this account
            document.querySelectorAll(`.ft-folder.ft-syncing[data-account-id="${event.accountId}"]`).forEach(el => el.classList.remove("ft-syncing"));
            if (syncPath && event.progress < 100) {
                // Try exact match first
                let folderEl = document.querySelector(`.ft-folder[data-account-id="${event.accountId}"][data-folder-path="${CSS.escape(syncPath)}"]`);
                if (!folderEl) {
                    // Folder not visible (parent collapsed) — find nearest visible ancestor
                    const parts = syncPath.split(/[./]/);
                    for (let i = parts.length - 1; i >= 1; i--) {
                        const parentPath = parts.slice(0, i).join(".");
                        folderEl = document.querySelector(`.ft-folder[data-account-id="${event.accountId}"][data-folder-path="${CSS.escape(parentPath)}"]`);
                        if (folderEl) break;
                    }
                }
                if (folderEl) folderEl.classList.add("ft-syncing");
            }
            break;
        }
        case "syncComplete":
            // After sync completes, refresh the folder tree (critical for first-run on Android
            // where folders don't exist until sync fetches them from Gmail API)
            refreshFolderTree();
            // Q53: track per-account last-sync timestamp for the status-bar hover.
            recordAccountSync(event.accountId);
            scheduleSyncedStamp();   // settle → "Synced HH:MM" once activity stops
            // Earlier I added reloadCurrentFolder() here to fix the
            // "phone INBOX stays on placeholder" report. That broke desktop
            // because syncComplete fires repeatedly on the desktop sync
            // loop, and each reload rebuilds the message-list DOM
            // mid-read — the focusedRow Row object becomes a stale
            // pointer to a detached element, and the viewer's "Select a
            // message to read" placeholder stays up while the new row
            // shows .selected. Removed: folderCountsChanged already
            // triggers a debounced reload when new messages actually
            // arrive (which is what the phone case really needs anyway).
            break;
        case "folderSynced":
            // Per-folder timestamps — drives the tooltip + freshness dot.
            for (const entry of event.entries || []) {
                setFolderSynced(event.accountId, entry.folderId, entry.syncedAt);
                if (currentFolderId === entry.folderId && currentAccountId === event.accountId) {
                    currentFolderSyncedAt = entry.syncedAt;
                    renderNarrowFolderTitle();
                }
            }
            break;
        case "folderCountsChanged": {
            // Incremental update only — updateFolderCounts patches badge counts
            // in-place and falls back to a full refreshFolderTree() when the
            // folder structure has actually changed. Calling both was doing a
            // 300 ms debounced rebuild on every sync tick even when just the
            // unread count moved — visible as folder-tree flicker on Dovecot
            // accounts where STATUS polls fire frequently.
            updateFolderCounts();
            updateNewMessageCount();
            // LEADING-EDGE + trailing debounce. The old pure-trailing 2 s debounce
            // meant new INBOX mail didn't appear in the list for 2 s after sync
            // said "done" (Bob 2026-06-18: "long delay before I see the new
            // messages ... I should see new inbox entries immediately"). The reload
            // is SILENT (preserves scroll/selection/viewer), so reloading on the
            // leading edge is non-disruptive: show new mail NOW if it's been >1.5 s
            // since the last reload, then a trailing reload coalesces the rest of a
            // sync burst into one final settle.
            const nowMs = Date.now();
            if (nowMs - lastListReloadAt > 1500) {
                lastListReloadAt = nowMs;
                reloadCurrentFolder();
            }
            if (reloadDebounceTimer) clearTimeout(reloadDebounceTimer);
            reloadDebounceTimer = setTimeout(() => {
                reloadDebounceTimer = null;
                lastListReloadAt = Date.now();
                reloadCurrentFolder();
            }, 1500);
            // Sync succeeded — clear any transient error banner and re-enable sync button
            hideAlert();
            const syncBtn = document.getElementById("btn-sync") as HTMLButtonElement;
            if (syncBtn) { syncBtn.disabled = false; syncBtn.classList.remove("syncing"); }
            // Honest indicator: a count change means activity, not necessarily
            // "done". Show "Syncing…" and let the settle timer stamp the time
            // once the backfill goes quiet (see scheduleSyncedStamp).
            if (statusSync && !(statusSync.textContent || "").startsWith("Sync error")) {
                statusSync.textContent = "Syncing…";
            }
            scheduleSyncedStamp();
            break;
        }
        case "updateAvailable": {
            const banner = document.getElementById("alert-banner");
            const text = document.getElementById("alert-text");
            if (banner && text) {
                banner.hidden = false;
                // Mark as the sticky update banner so hideAlert() (called
                // after every successful sync) leaves it alone.
                banner.dataset.key = "update-available";
                // Green update banner — matches the Android update bar Bob
                // likes (was blue, oklch hue 250).
                banner.style.background = "oklch(0.52 0.14 150)";
                // Stash the update banner contents so updateFailed can restore
                // it (offering a retry) instead of leaving "Updating..." pinned.
                // `local:true` means a newer build is ALREADY installed on disk
                // (npmglobalize finished) — we just need to restart onto it, no
                // npm install. That path is network-free and does a clean
                // daemon handoff (the fresh daemon kills this one). The remote
                // path (a published version not yet installed) still runs
                // performUpdate (npm install + restart).
                const isLocal = !!(event as any).local;
                const verb = isLocal ? "installed" : "available";
                const label = isLocal ? "Restart to apply" : "Update now";
                const restoreHtml = `${APP_NAME} ${event.latest} ${verb} (you have ${event.current}) — <button id="btn-do-update" style="background:none;border:1px solid #fff;color:#fff;padding:0.15em 0.5em;border-radius:3px;cursor:pointer;margin-left:0.5em">${label}</button>`;
                (window as any).__mailxUpdateBannerHtml = restoreHtml;
                text.innerHTML = restoreHtml;
                document.getElementById("btn-do-update")?.addEventListener("click", () => {
                    const ipc = (window as any).mailxapi || (window as any).opener?.mailxapi;
                    if (isLocal && ipc?.restartDaemon) {
                        text.textContent = "Restarting onto the new version...";
                        ipc.restartDaemon();
                    } else {
                        text.textContent = "Updating... mailx will restart when done";
                        if (ipc?.performUpdate) ipc.performUpdate();
                    }
                });
            }
            break;
        }
        case "updateFailed": {
            // Service tried to install but failed (typically offline). Restore
            // the "Update now" banner so the user can retry — and prefix it
            // with a short status so they know why the previous tap silently
            // came back. mailx itself keeps running on the current version.
            const banner = document.getElementById("alert-banner");
            const text = document.getElementById("alert-text");
            if (banner && text) {
                const restoreHtml = (window as any).__mailxUpdateBannerHtml as string | undefined;
                const prefix = event.offline ? "No connection — update postponed. " : "Update failed — ";
                banner.hidden = false;
                banner.dataset.key = "update-available";  // sticky — survives sync
                banner.style.background = event.offline ? "oklch(0.42 0.06 70)" : "oklch(0.45 0.12 25)";
                text.innerHTML = `${prefix}${restoreHtml ?? ""}`;
                document.getElementById("btn-do-update")?.addEventListener("click", () => {
                    text.textContent = "Updating... mailx will restart when done";
                    const ipc = (window as any).mailxapi || (window as any).opener?.mailxapi;
                    if (ipc?.performUpdate) ipc.performUpdate();
                });
            }
            break;
        }
        case "syncActionFailed": {
            // Surface sync failures (move/delete/flag not applied on server)
            // so the user knows local-first actions haven't propagated yet.
            // Status-bar hint always; banner only when the service is telling
            // us it has GIVEN UP (message starts with "Gave up"). A full sync
            // over a flaky link fires syncActionFailed once per attempt, and
            // a banner per attempt floods DOM work on the WebView main thread.
            const action = event.action === "move" ? "Move" : event.action === "delete" ? "Delete" : event.action;
            if (statusSync) statusSync.textContent = `Sync failed: ${action}`;
            if (typeof event.error === "string" && /gave up/i.test(event.error)) {
                showAlert(
                    `Couldn't ${action.toLowerCase()} message on the server after retries: ${event.error}. Local change kept.`,
                    "conflict",
                );
            }
            break;
        }
        case "reload":
            location.reload();
            break;
        case "bodyCached":
            // Prefetch (or on-demand fetch) downloaded a body — flip the
            // "not-downloaded" indicator to the teal dot for any rows in view.
            markBodiesCached(event.items || []);
            break;
        // bodyAvailable and messageRemoved migrated to subscribeStore below.
        case "syncStateChanged": {
            // Drives the bottom-right sync-status pill. Same surface used by
            // syncProgress / folderSynced; reconciler ticks this every few
            // seconds so the pill reflects current queue depth.
            if (!statusSync) break;
            const msgs = event.messageActions ?? 0;
            const bodies = event.bodyFetches ?? 0;
            if (msgs === 0 && bodies === 0) {
                // Don't clobber an in-progress per-account "Syncing accountX…"
                // status if reconciler ticks while a fresh sync is happening.
                if (!/syncing|fetching/i.test(statusSync.textContent || "")) {
                    statusSync.textContent = "Sync OK";
                    statusSync.removeAttribute("data-sync-state");
                }
            } else {
                const parts: string[] = [];
                if (msgs) parts.push(`${msgs} action${msgs === 1 ? "" : "s"}`);
                if (bodies) parts.push(`${bodies} bod${bodies === 1 ? "y" : "ies"}`);
                statusSync.textContent = `Syncing ${parts.join(" + ")}`;
                statusSync.setAttribute("data-sync-state", "syncing");
            }
            break;
        }
        case "configChanged":
            // A watched config file was modified — could be user edit via the
            // JSONC editor, a GDrive sync, or mailx itself saving (e.g.
            // allowlist update on "allow sender").
            //
            // For accounts.jsonc specifically, surface a sticky banner with a
            // Restart button — the file change has no effect on the running
            // daemon (IMAP connections, token caches, sync loops use the old
            // config snapshot), and users shouldn't need `mailx -kill` just
            // to apply an edit. For other files (allowlist / clients /
            // config) the service handles live, a status-bar flash suffices.
            if (statusSync) {
                statusSync.textContent = `${event.filename} updated`;
                setTimeout(() => {
                    if (statusSync.textContent === `${event.filename} updated`) statusSync.textContent = "";
                }, 8000);
            }
            if (event.filename && /accounts\.jsonc/i.test(String(event.filename))) {
                showRestartForConfigBanner();
            }
            break;
        case "cloudError":
            // Cloud read/write failed (Google Drive auth/network/etc.). Show a
            // sticky banner so the user knows the change wasn't synced. When
            // error is null, the next successful op cleared it — hide it.
            if (event.error) {
                const where = event.filename ? ` (${event.op || "sync"} ${event.filename})` : "";
                showAlert(`Cloud sync error${where}: ${event.error}`, "cloud-error");
            } else {
                // Only hide if the visible banner is the cloud-error one
                if (alertBanner && alertBanner.dataset.key === "cloud-error") {
                    alertBanner.hidden = true;
                    dismissedAlerts.delete("cloud-error");
                }
            }
            break;
        case "error":
            if (statusSync) statusSync.textContent = `Error: ${event.message}`;
            showAlert(event.message, "ws-error");
            break;
        case "fatal":
            // Unrecoverable startup failure — sticky red banner, no auto-dismiss.
            // Used for GDrive-folder-missing and similar conditions where the
            // app cannot function. console.warn would be invisible in the GUI.
            if (alertBanner) alertBanner.style.background = "oklch(0.45 0.18 25)";
            showAlert(event.message || "Fatal error", event.key || "fatal", { sticky: true });
            if (statusSync) statusSync.textContent = `FATAL: ${event.message}`;
            break;
        case "outboxStatus":
            renderOutboxStatus(event);
            break;
        case "calendarUpdated":
        case "tasksUpdated":
            // Reauth succeeded (or was never broken): clear any lingering
            // scope banner for this feature. Handled here (not just in the
            // sidebar) because the global fallback banner isn't tied to the
            // sidebar's lifecycle.
            if (alertBanner && /^scope-(calendar|tasks|google)$/.test(alertBanner.dataset.key || "")) {
                alertBanner.hidden = true;
                alertBanner.dataset.key = "";
                alertBanner.querySelector(".status-action")?.remove();
            }
            break;
        case "authScopeError": {
            // Fallback banner: calendar-sidebar.ts already shows this inline
            // when the sidebar is visible, but if the user has the sidebar
            // off or is on a narrow tier where it's hidden, the error would
            // otherwise be invisible. Global banner with the same button.
            const feat = event.feature || "google";
            const key = `scope-${feat}`;
            const msg = event.message || `Google ${feat} access needs re-consent.`;
            showAlert(msg, key, { sticky: true });
            const bannerText = document.getElementById("alert-text");
            if (bannerText && bannerText.textContent === msg) {
                const existing = bannerText.parentElement?.querySelector(".status-action");
                if (!existing) {
                    const btn = document.createElement("button");
                    btn.className = "status-action";
                    btn.textContent = "Re-authenticate";
                    btn.addEventListener("click", async () => {
                        btn.disabled = true;
                        btn.textContent = "Opening browser…";
                        try {
                            const { reauthGoogleScopes } = await import("./lib/api-client.js");
                            await reauthGoogleScopes();
                            btn.textContent = "Consent opened — finish in browser";
                        } catch (err: any) {
                            btn.disabled = false;
                            btn.textContent = `Failed: ${err?.message || err}`;
                        }
                    });
                    bannerText.parentElement?.insertBefore(btn, document.getElementById("alert-dismiss"));
                }
            }
            break;
        }
        case "accountError": {
            // Show actual error + hint in banner
            const msg = `${event.accountId}: ${event.error}`;
            showAlert(msg, `acct-${event.accountId}`);
            // Add action button: Re-authenticate for OAuth, Retry for password accounts
            const bannerText = document.getElementById("alert-text");
            if (bannerText && bannerText.textContent === msg) {
                const existing = bannerText.parentElement?.querySelector(".status-action");
                if (!existing) {
                    const btn = document.createElement("button");
                    btn.className = "status-action";
                    if (event.isOAuth) {
                        btn.textContent = "Re-authenticate";
                        btn.addEventListener("click", async () => {
                            btn.disabled = true;
                            btn.textContent = "Authenticating...";
                            try {
                                const data = await reauthenticate(event.accountId);
                                if (data.ok) {
                                    hideAlert();
                                    const acctEl = document.getElementById("status-accounts");
                                    if (acctEl) { acctEl.textContent = `${event.accountId}: reconnected`; acctEl.style.color = ""; }
                                } else {
                                    btn.textContent = "Re-authenticate";
                                    btn.disabled = false;
                                }
                            } catch {
                                btn.textContent = "Re-authenticate";
                                btn.disabled = false;
                            }
                        });
                    } else {
                        btn.textContent = "Retry";
                        btn.addEventListener("click", async () => {
                            btn.disabled = true;
                            btn.textContent = "Syncing...";
                            try {
                                const data = await syncAccount(event.accountId);
                                if (data.ok) {
                                    hideAlert();
                                    const acctEl = document.getElementById("status-accounts");
                                    if (acctEl) { acctEl.textContent = `${event.accountId}: reconnected`; acctEl.style.color = ""; }
                                } else {
                                    btn.textContent = "Retry";
                                    btn.disabled = false;
                                }
                            } catch {
                                btn.textContent = "Retry";
                                btn.disabled = false;
                            }
                        });
                    }
                    bannerText.parentElement?.insertBefore(btn, document.getElementById("alert-dismiss"));
                }
            }
            // Also show in status bar
            const acctEl = document.getElementById("status-accounts");
            if (acctEl) {
                acctEl.textContent = `${event.accountId}: ${event.hint}`;
                acctEl.style.color = "oklch(0.65 0.2 25)";
            }
            break;
        }
        case "openMailto": {
            // P115: OS-level mailto: handler. CLI dropped a pending file; the
            // daemon picked it up and forwarded the parsed fields. Open compose
            // pre-populated. Treat as a fresh New compose, not a reply — the
            // browser doesn't know about a parent message.
            openComposeFromMailto({
                to: Array.isArray(event.to) ? event.to : [],
                cc: Array.isArray(event.cc) ? event.cc : [],
                bcc: Array.isArray(event.bcc) ? event.bcc : [],
                subject: event.subject || "",
                body: event.body || "",
                inReplyTo: event.inReplyTo || "",
            }).catch((e: any) => console.error("openComposeFromMailto failed:", e?.message || e));
            break;
        }
        case "openShare": {
            // C46: Windows Share sheet → rmfmail. rmfshare.exe staged the
            // shared content; the daemon base64'd any files and pushed this
            // event. Open compose with the content attached/inlined.
            openComposeFromShare({
                title: event.title || "",
                text: event.text || "",
                url: event.url || "",
                attachments: Array.isArray(event.attachments) ? event.attachments : [],
                skipped: Array.isArray(event.skipped) ? event.skipped : [],
            }).catch((e: any) => console.error("openComposeFromShare failed:", e?.message || e));
            break;
        }
        case "debugEval": {
            // --debug-server /api/eval: the daemon pushes {id, code}; we
            // evaluate in the live page and answer via debugEvalResult. This
            // is the "drive the app yourself" hook — lets an agent inspect
            // the real DOM (elementFromPoint, computed styles, scroll state)
            // without devtools. Only the daemon can push events on this
            // channel, and only --debug-server exposes the endpoint.
            const { id, code } = event;
            if (!id || typeof code !== "string") break;
            (async () => {
                const { debugEvalResult } = await import("./lib/api-client.js");
                try {
                    const fn = new Function(`return (async () => (${code}))();`);
                    let v: any = await fn();
                    // DOM nodes / circular structures can't ride JSON — fall
                    // back to their string form rather than failing the call.
                    try { JSON.stringify(v); } catch { v = String(v); }
                    await debugEvalResult(id, v);
                } catch (e: any) {
                    await debugEvalResult(id, undefined, e?.message || String(e));
                }
            })().catch((e: any) => console.error(`[debug-eval] reply failed: ${e?.message || e}`));
            break;
        }
        case "popoutAction": {
            // Toolbar click inside a NATIVE popout window (msger, spawned by
            // the daemon). That window has no IPC bridge and can't host
            // compose, so the daemon forwards the action here; we load the
            // message locally and reuse the overlay-popout action path —
            // reply/replyAll/forward/editAsNew open the real editable
            // compose, flag/delete act directly.
            const { action, accountId, uid, folderId } = event;
            if (!action || !accountId || !uid) break;
            (async () => {
                const { getMessage } = await import("./lib/api-client.js");
                const msg = await getMessage(accountId, uid, false, folderId);
                document.dispatchEvent(new CustomEvent("mailx-popout-action", { detail: { action, msg, accountId } }));
            })().catch((e: any) => console.error(`[popout-window] ${action} failed: ${e?.message || e}`));
            break;
        }
    }
});

// Store-bus consumers (post-refactor). The list's mailx-remove-stale
// listener handles row-removal + focus-advance atomically; we just
// translate bus events into that one signal so there's a single
// owner of "row is no longer here."
subscribeStore("*", (ev: any) => {
    if (ev.kind === "bodyAvailable" && ev.accountId && ev.uid) {
        // Flip the row's "not-downloaded" dot. Viewer subscribes separately
        // (in message-viewer.ts) to re-render the focused message.
        markBodiesCached([{ accountId: ev.accountId, uid: ev.uid }]);
    } else if (ev.kind === "messageRemoved" && ev.accountId && ev.uid) {
        // Server-side EXPUNGE (or another device deleted).
        document.dispatchEvent(new CustomEvent("mailx-remove-stale", {
            detail: { accountId: ev.accountId, uid: ev.uid },
        }));
    } else if (ev.kind === "messageMoved" && ev.accountId && ev.uid) {
        // Moved to a different folder — from the current folder's view
        // it's the same as removal. The list's remove-stale handler
        // figures out whether it was actually in the current view; if not,
        // it's a no-op. Local optimistic delete also flows here when the
        // user hits Del on another device — same handler, same advance.
        document.dispatchEvent(new CustomEvent("mailx-remove-stale", {
            detail: { accountId: ev.accountId, uid: ev.uid },
        }));
    }
});

/** Open a compose window pre-filled from a mailto: URL. Same plumbing as
 *  openCompose("new") but the init shape comes from the parsed URL instead
 *  of the currently-selected message. The body arrives as plain text from
 *  the URL — we wrap it in a single <p> so Quill renders it as paragraphs
 *  rather than a single line, escaping `<` so embedded angle brackets in a
 *  signature/template don't get interpreted as tags. */
async function openComposeFromMailto(m: {
    to: string[]; cc: string[]; bcc: string[];
    subject: string; body: string; inReplyTo: string;
}): Promise<void> {
    // Open the iframe immediately and load accounts in parallel — same
    // pattern as openCompose. The mailto handler should never feel like
    // "waiting for the system" to a user who clicked a link.
    const accountsP = getAccounts();
    const frame = showComposeOverlay(m.subject ? `Compose: ${m.subject}` : "Compose");
    const accounts = await accountsP;
    const accountId = accounts[0]?.id || "";
    const escape = (s: string) => s.replace(/[&<>]/g, c =>
        ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]!));
    const bodyHtml = m.body
        ? "<p>" + escape(m.body).replace(/\r?\n/g, "</p><p>") + "</p>"
        : "";
    const init: any = {
        mode: "new",
        accountId,
        to: m.to.map(addr => ({ name: "", address: addr })),
        cc: m.cc.map(addr => ({ name: "", address: addr })),
        bcc: m.bcc.map(addr => ({ name: "", address: addr })),
        subject: m.subject,
        bodyHtml,
        inReplyTo: m.inReplyTo,
        references: m.inReplyTo ? [m.inReplyTo] : [],
        accounts: accounts.map((a: any) => ({ id: a.id, name: a.name, email: a.email, signature: a.signature, sig: a.sig })),
    };
    sessionStorage.setItem("composeInit", JSON.stringify(init));
    try { frame?.contentWindow?.postMessage({ type: "compose-init-ready" }, "*"); } catch { /* */ }
}

/** C46: open compose from a Windows Share sheet drop. Shared files become
 *  attachments; shared text/links become the body; a shared page title (or
 *  the first filename) seeds the subject. Skipped files (oversize or
 *  unreadable) are called out in the body — the user must see that a file
 *  they shared is NOT on the outgoing message. */
async function openComposeFromShare(s: {
    title: string; text: string; url: string;
    attachments: { filename: string; mimeType: string; dataBase64: string }[];
    skipped: { name: string; reason: string }[];
}): Promise<void> {
    const accountsP = getAccounts();
    let subject = s.title;
    if (!subject && s.attachments.length) {
        subject = s.attachments[0].filename +
            (s.attachments.length > 1 ? ` (+${s.attachments.length - 1} more)` : "");
    }
    const frame = showComposeOverlay(subject ? `Compose: ${subject}` : "Compose");
    const accounts = await accountsP;
    const escape = (str: string) => str.replace(/[&<>]/g, c =>
        ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]!));
    const paras: string[] = [];
    if (s.text && s.text !== s.url) {
        paras.push("<p>" + escape(s.text).replace(/\r?\n/g, "</p><p>") + "</p>");
    }
    if (s.url) {
        const u = escape(s.url);
        paras.push(`<p><a href="${u}">${u}</a></p>`);
    }
    for (const sk of s.skipped) {
        paras.push(`<p>[Attachment "${escape(sk.name)}" was not added — ${escape(sk.reason)}]</p>`);
    }
    const init: any = {
        mode: "new",
        accountId: accounts[0]?.id || "",
        to: [], cc: [], bcc: [],
        subject,
        bodyHtml: paras.join(""),
        attachments: s.attachments,
        accounts: accounts.map((a: any) => ({ id: a.id, name: a.name, email: a.email, signature: a.signature, sig: a.sig })),
    };
    sessionStorage.setItem("composeInit", JSON.stringify(init));
    try { frame?.contentWindow?.postMessage({ type: "compose-init-ready" }, "*"); } catch { /* */ }
}

// ── Keyboard shortcuts ──

// Capture-phase pre-handler: intercept WebView accelerator keys that would
// otherwise trigger a browser action (Ctrl+R / Ctrl+Shift+R = reload). Call
// the action directly here — earlier version re-dispatched a synthetic
// keydown to document, which bubbled back up to this window listener and
// fired again, breaking unrelated shortcuts (user-reported: Ctrl+N
// stopped composing). Direct dispatch avoids the recursion.
//
// Whether preventDefault actually suppresses WebView2's native reload
// depends on AreBrowserAcceleratorKeysEnabled — if it still reloads,
// the msger Rust side needs the flag flipped (option 1 in the original
// design notes).
window.addEventListener("keydown", (e) => {
    if (!e.ctrlKey || e.altKey || e.metaKey) return;
    if (e.key !== "r" && e.key !== "R") return;
    // Skip when the user is typing in an input (compose textarea etc.) so
    // we don't hijack their typing to fire reply-all.
    const t = e.target as HTMLElement | null;
    const tag = t?.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t?.isContentEditable) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (e.shiftKey) openCompose("replyAll");
    else openCompose("reply");
}, { capture: true });

document.addEventListener("keydown", (e) => {
    // Ctrl+N or Ctrl+Shift+M = Compose
    if ((e.ctrlKey && e.key === "n") || (e.ctrlKey && e.shiftKey && e.key === "M")) {
        e.preventDefault();
        openCompose("new");
    }
    // Ctrl+P = Print the focused message. preventDefault stops the host
    // browser from printing the whole app window instead of the letter —
    // UNCONDITIONALLY, even with focus in a text field: the WebView's
    // whole-app print dialog is never what the user wants, and ^P should
    // print the current letter regardless of where the caret sits.
    if (e.ctrlKey && (e.key === "p" || e.key === "P") && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        if (getCurrentMessage()) {
            printCurrentMessage();
        } else {
            const s = document.getElementById("status-sync");
            if (s) s.textContent = "Nothing to print — select a message first.";
        }
    }
    // Ctrl+T = new view tab. The strip is hidden with a single tab (no wasted
    // band), so this is how the second tab gets opened; once 2+ exist the
    // strip's "+" is also available.
    if (e.ctrlKey && (e.key === "t" || e.key === "T") && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        openTab({ kind: "unified" }, "All Inboxes", true);
    }
    // Ctrl+R = Reply (without Shift)
    if (e.ctrlKey && e.key === "r" && !e.shiftKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        openCompose("reply");
    }
    // Ctrl+Shift+R = Reply All
    if (e.ctrlKey && e.shiftKey && e.key === "R" && !e.altKey && !e.metaKey) {
        e.preventDefault();
        openCompose("replyAll");
    }
    // Ctrl+F = Forward (without Shift). Use toLowerCase so a Caps-Lock or
    // shifted state doesn't bypass us. Single handler — the previous
    // duplicate fired openCompose twice, which double-loaded the compose
    // iframe and the second copy got an empty sessionStorage (the first
    // had already consumed it), producing an empty Forward form.
    if (e.ctrlKey && !e.shiftKey && !e.altKey && !e.metaKey && (e.key === "f" || e.key === "F")) {
        e.preventDefault();
        openCompose("forward");
    }
    // Ctrl/Cmd +/-/0 = UI zoom in / out / reset. Reads the current font
    // size from the root style and steps by 1px. Skip when the focus is
    // inside an input / contenteditable so the user's text isn't hijacked
    // (compose autocomplete already handles its own zoom in the Quill
    // editor via Ctrl+wheel). The browser's own Ctrl+/Ctrl- is also
    // suppressed because we preventDefault.
    const zoomKey = (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey;
    if (zoomKey && (e.key === "=" || e.key === "+" || e.key === "-" || e.key === "0")) {
        const t = e.target as HTMLElement | null;
        const tag = t?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t?.isContentEditable) return;
        const cur = parseFloat(document.documentElement.style.fontSize) ||
            parseFloat(getComputedStyle(document.documentElement).fontSize) || 15;
        let next = cur;
        if (e.key === "0") next = 15;                            // reset to default
        else if (e.key === "-") next = cur - 1;                  // smaller
        else next = cur + 1;                                     // = or +
        e.preventDefault();
        applyFontSize(next);
    }
    // Escape = leave multi-select mode. Guaranteed escape hatch so the list
    // can never get stuck showing checkbox circles with no way to open a
    // message (Bob 2026-06-26). Only acts when multi-select is actually on, so
    // it doesn't swallow Escape from other handlers otherwise.
    if (e.key === "Escape" && document.getElementById("ml-body")?.classList.contains("multi-select-on")) {
        const t = e.target as HTMLElement | null;
        const tag = t?.tagName;
        if (tag !== "INPUT" && tag !== "TEXTAREA" && tag !== "SELECT" && !t?.isContentEditable) {
            e.preventDefault();
            exitMultiSelect();
            return;
        }
    }

    // Ctrl+A = Select all visible messages
    if (e.ctrlKey && e.key === "a") {
        const t = e.target as HTMLElement | null;
        const tag = t?.tagName;
        // In a text field / editor, Ctrl+A means "select the text" — never
        // hijack it to select-all-messages. The old guard's `.closest(...,
        // body)` always matched (everything is inside <body>), so Ctrl+A in
        // the search box selected every message instead of the box's text,
        // and the field couldn't be selected+cleared (Bob 2026-05-18).
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t?.isContentEditable) return;
        const mlBody = document.getElementById("ml-body");
        if (mlBody) {
            e.preventDefault();
            // selectAllVisible() also sets `multi-select-on` so a following ★
            // click clears the WHOLE selection, not just the clicked row
            // (Bob 2026-06-26). Plain add-.selected was the bug.
            selectAllVisible();
        }
    }
    // Ctrl+D or Delete = Delete selected messages.
    // Focus is the only signal. Text input has focus → Delete is a native
    // character edit, ALWAYS. List has focus (a row, the list container,
    // anything outside a text editor) → Delete acts on the list selection,
    // single or multi. The 2026-05-09 "multi-select while typing" carve-out
    // is gone — it cost Bob real messages 2026-05-20 by hard-deleting the
    // auto-selected row every time he hit Delete to backspace in the search
    // input.
    if ((e.ctrlKey && e.key === "d") || e.key === "Delete") {
        const t = e.target as HTMLElement | null;
        const tag = t?.tagName;
        const editable = t?.isContentEditable;
        const inEditable = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || editable;
        if (inEditable) return;
        e.preventDefault();
        deleteSelection();
    }
    // Ctrl+Z = pop the top of the undo stack (delete / move / flag). Guard the
    // SAME way Delete does (the block above): never hijack Ctrl+Z when focus is
    // in a text field / contenteditable, and never while a compose overlay is
    // open — the editor (or the input) owns undo there. Without this guard the
    // mail-action undo could fire instead of an editor/text undo.
    if (e.ctrlKey && e.key === "z") {
        const t = e.target as HTMLElement | null;
        const tag = t?.tagName;
        const inEditable = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || !!t?.isContentEditable;
        const composeOpen = !!document.querySelector(".compose-overlay");
        if (!inEditable && !composeOpen && undoStack.length > 0) {
            e.preventDefault();
            performUndo();
        }
    }
    // F5 = Sync
    if (e.key === "F5") {
        e.preventDefault();
        document.getElementById("btn-sync")?.click();
    }
    // R (no modifiers) or Ctrl+Q = Toggle read/unread on the focused row.
    // Ctrl+Q mirrors the Outlook-style "mark read/unread" combo so it works
    // even when focus is in a text input (Ctrl-modifier guarantees no
    // collision with typing). Bare R defers when typing.
    const isToggleSeen =
        (e.key.toLowerCase() === "r" && !e.ctrlKey && !e.metaKey && !e.altKey) ||
        (e.ctrlKey && !e.metaKey && !e.altKey && e.key.toLowerCase() === "q");
    if (isToggleSeen) {
        const active = document.activeElement;
        const inText = active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA" || active.tagName === "SELECT" || (active as HTMLElement).isContentEditable);
        // Bare R yields to text inputs; Ctrl+Q overrides them so it's reachable
        // from the search box or compose draft list.
        if (!e.ctrlKey && inText) return;
        e.preventDefault();
        const applySeen = (m: NonNullable<ReturnType<typeof getCurrentFocused>>, seen: boolean): void => {
            setSeen(m, seen);
            updateFlags(m.accountId, m.uid, m.flags, (m as any).folderId).then(() => {
                messageState.updateMessageFlags(m.accountId, m.uid, m.flags);
                const row = document.querySelector(`.ml-row[data-uid="${m.uid}"][data-account-id="${CSS.escape(m.accountId)}"]`) as HTMLElement | null;
                if (row) row.classList.toggle("unread", !seen);
            }).catch(() => { setSeen(m, !seen); });
        };
        // getSelectedMessages returns only id tuples — resolve to the full
        // message objects (with flags) from the list state.
        const selectedIds = getSelectedMessages();
        if (selectedIds.length > 1) {
            // Multi-select → mark every selected message READ (not a toggle):
            // a triage sweep is "I've dealt with these," not "flip each one"
            // (Bob 2026-06-15). Skip those already read.
            const all = messageState.getMessages();
            for (const id of selectedIds) {
                const m = all.find(x => x.accountId === id.accountId && x.uid === id.uid && x.folderId === id.folderId);
                if (m && !seenOf(m)) applySeen(m, true);
            }
            return;
        }
        // Single → toggle read/unread.
        const sel = getCurrentFocused();
        if (!sel) return;
        applySeen(sel, !seenOf(sel));
    }
    // Z = locate the focused row in the list (scroll-to-selected). After
    // scrolling the list out of sync with the preview, this snaps back.
    if (e.key.toLowerCase() === "z" && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const active = document.activeElement;
        if (active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA" || active.tagName === "SELECT")) return;
        const editable = (active as HTMLElement | null)?.isContentEditable;
        if (editable) return;
        e.preventDefault();
        scrollFocusedIntoView();
    }
    // F6 / Shift+F6 — standard pane-switch shortcut. Cycles focus among
    // folder tree → message list → message viewer.
    if (e.key === "F6") {
        e.preventDefault();
        cyclePaneFocus(e.shiftKey);
    }
    // ? = show keyboard shortcuts help. Skip when typing in inputs so a
    // literal "?" in search/compose doesn't pop the dialog.
    if (e.key === "?" && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const active = document.activeElement;
        if (active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA" || active.tagName === "SELECT")) return;
        const editable = (active as HTMLElement | null)?.isContentEditable;
        if (editable) return;
        e.preventDefault();
        openShortcutsDialog();
    }
    // Arrow keys + Home/End/PgUp/PgDn — navigate message list (Q58).
    if (["ArrowDown", "ArrowUp", "Home", "End", "PageDown", "PageUp"].includes(e.key)) {
        const active = document.activeElement;
        if (active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA" || active.tagName === "SELECT")) return;
        // Respect focus: if the preview iframe or the folder tree has
        // focus, arrows should scroll/navigate that pane — not hijack to
        // list navigation. The viewer's iframe forwards every keydown to
        // the parent for hotkey unification; here we bail so the iframe's
        // own preventDefault chain leaves the native arrow-scroll alone
        // (Bob 2026-05-22: "arrow keys should work in the window with focus").
        if (active && (active.tagName === "IFRAME"
                       || active.closest?.("#message-viewer")
                       || active.closest?.("#folder-tree"))) return;
        const body = document.getElementById("ml-body");
        if (!body) return;
        const rows = Array.from(body.querySelectorAll<HTMLElement>(".ml-row"));
        if (rows.length === 0) return;
        const selected = body.querySelector<HTMLElement>(".ml-row.selected");
        const idx = selected ? rows.indexOf(selected) : -1;
        let target: HTMLElement | undefined;
        if (e.key === "ArrowDown") target = rows[idx + 1] || rows[idx];
        else if (e.key === "ArrowUp") target = rows[Math.max(0, idx - 1)];
        else if (e.key === "Home") target = rows[0];
        else if (e.key === "End") target = rows[rows.length - 1];
        else if (e.key === "PageDown") target = rows[Math.min(rows.length - 1, idx + 10)];
        else if (e.key === "PageUp") target = rows[Math.max(0, idx - 10)];
        if (target && (!selected || target !== selected)) {
            e.preventDefault();
            target.click();
            target.scrollIntoView({ block: "nearest" });
        }
    }
});

// ── Double-click viewer chrome → fullscreen preview ──
// The iframe's own contentDocument has its own dblclick handler (in
// message-viewer.ts). This catches dblclicks on the headers / from row /
// reply-to row of the viewer that aren't inside the iframe.
document.getElementById("message-viewer")?.addEventListener("dblclick", (e) => {
    const target = e.target as HTMLElement | null;
    // Don't toggle when double-clicking interactive controls (chips, buttons,
    // links, address widgets) — only on the chrome / empty space.
    if (target?.closest("a, button, input, select, textarea, [contenteditable]")) return;
    // Don't toggle on the header (From/To/Subject/Date). Double-clicking an
    // address or subject is a universal text-select gesture; hijacking it for
    // fullscreen collapsed the whole UI to a near-empty viewer and looked
    // broken (Bob 2026-06-05: "double-clicked a from address by mistake and
    // got this screen"). The iframe-level dblclick toggle was ALSO removed
    // (it hijacked word-selection), so this chrome handler is now the only
    // entry into fullscreen-preview. toggleFullscreenPreview() itself guards
    // against entering with no message loaded.
    if (target?.closest(".mv-header")) return;
    toggleFullscreenPreview();
});

// ── F6 pane cycling ──

/** Focus a specific pane by element id. Same visual hint as cyclePaneFocus
 *  so the user can see which pane took focus. Wired to middle-click. */
function focusPaneById(id: string): void {
    const el = document.getElementById(id);
    if (!el) return;
    if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "-1");
    el.focus();
    el.style.outline = "2px solid var(--color-accent, #3b82f6)";
    el.style.outlineOffset = "-2px";
    setTimeout(() => { el.style.outline = ""; el.style.outlineOffset = ""; }, 600);
}

// Middle-click anywhere in a pane focuses that pane. Doesn't conflict with
// any existing right-click context menus or left-click selection handlers.
// Bob 2026-05-27: F6/Shift+F6 isn't memorable; right-click feels natural.
// We do middle-click (button=1) because right-click is already overloaded
// for row/message context menus throughout the UI.
document.addEventListener("auxclick", (e) => {
    if (e.button !== 1) return; // middle button only
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const ft = target.closest("#folder-tree");
    const ml = target.closest("#ml-body");
    const mv = target.closest("#message-viewer");
    let id: string | null = null;
    if (ft) id = "folder-tree";
    else if (ml) id = "ml-body";
    else if (mv) id = "message-viewer";
    if (!id) return;
    e.preventDefault();
    focusPaneById(id);
});

function cyclePaneFocus(reverse: boolean): void {
    // Major panes in tab order. Skip ones not currently visible (folder
    // tree is hidden in narrow tier when the rail/drawer is closed; message
    // viewer is only meaningful with a message open).
    const panes: { id: string; el: HTMLElement | null }[] = [
        { id: "folder-tree", el: document.getElementById("folder-tree") },
        { id: "ml-body", el: document.getElementById("ml-body") },
        { id: "message-viewer", el: document.getElementById("message-viewer") },
    ].filter(p => {
        if (!p.el) return false;
        const r = p.el.getBoundingClientRect();
        return r.width > 0 && r.height > 0;
    });
    if (panes.length === 0) return;
    const active = document.activeElement as HTMLElement | null;
    const currentIdx = panes.findIndex(p => p.el && (p.el === active || p.el.contains(active!)));
    const step = reverse ? -1 : 1;
    const nextIdx = currentIdx < 0 ? 0 : (currentIdx + step + panes.length) % panes.length;
    const next = panes[nextIdx];
    if (!next.el) return;
    // Make the pane focusable if it isn't already; tabindex=-1 lets us
    // focus it programmatically without inserting it into Tab order.
    if (!next.el.hasAttribute("tabindex")) next.el.setAttribute("tabindex", "-1");
    next.el.focus();
    // Visual hint — brief outline so the user can see which pane took focus.
    next.el.style.outline = "2px solid var(--color-accent, #3b82f6)";
    next.el.style.outlineOffset = "-2px";
    setTimeout(() => { next.el!.style.outline = ""; next.el!.style.outlineOffset = ""; }, 600);
}

// ── Keyboard shortcuts help dialog ──

function openShortcutsDialog(): void {
    if (document.querySelector(".mailx-shortcuts-modal")) return;
    const backdrop = document.createElement("div");
    backdrop.className = "mailx-modal-backdrop mailx-shortcuts-modal";
    const panel = document.createElement("div");
    panel.className = "mailx-modal";
    panel.style.maxWidth = "640px";
    const tabs = [
        {
            id: "app", label: "Application",
            rows: [
                ["Compose", "Ctrl+N"],
                ["Reply", "Ctrl+R"],
                ["Reply all", "Ctrl+Shift+R"],
                ["Forward", "Ctrl+F"],
                ["Sync", "F5"],
                ["Delete selected", "Del or Ctrl+D"],
                ["Undo last delete/move", "Ctrl+Z"],
                ["Toggle read/unread", "R"],
                ["Toggle flag (★)", "(⚑ button in viewer)"],
                ["Select all visible", "Ctrl+A"],
                ["Navigate list", "↑ ↓ Home End PgUp PgDn"],
                ["Scroll-to-focused row", "Z"],
                ["Switch pane", "F6 / Shift+F6"],
                ["Find / search", "(focus the search box)"],
                ["Show this help", "?"],
                ["Close dialog", "Esc"],
            ],
        },
        {
            id: "compose", label: "Compose",
            rows: [
                ["Send", "Ctrl+Enter"],
                ["Insert / edit link", "Ctrl+K"],
                ["Remove link", "Ctrl+Shift+K"],
                ["Bold / italic / underline", "Ctrl+B / Ctrl+I / Ctrl+U"],
                ["Strikethrough", "Ctrl+Shift+X"],
                ["Ordered list", "Ctrl+Shift+7"],
                ["Bulleted list", "Ctrl+Shift+8"],
                ["Indent / outdent", "Ctrl+] / Ctrl+["],
                ["Clear formatting", "Ctrl+\\"],
                ["Color text", "Ctrl+Shift+C"],
                ["New line in To/Cc/Bcc", "(Enter inserts a comma)"],
                ["Editor reference", "(? button on compose toolbar — shows Quill/tiptap differences)"],
            ],
        },
        {
            id: "viewer", label: "Viewer",
            rows: [
                ["Toggle preview fullscreen", "double-click on body"],
                ["Exit fullscreen", "Esc / ✕ button / double-click"],
                ["Zoom in / out / reset", "Ctrl+= / Ctrl+- / Ctrl+0"],
                ["Right-click in body", "Copy / Search / Translate / Zoom"],
                ["Right-click a link", "Open / Save / Copy URL / Copy text"],
            ],
        },
    ];
    panel.innerHTML = `
        <div class="mailx-modal-title">
            <span class="mailx-modal-title-text">Keyboard shortcuts</span>
            <button type="button" class="mailx-modal-close" id="sc-x" title="Close (Esc)" aria-label="Close">&times;</button>
        </div>
        <div class="mailx-shortcuts-tabs" role="tablist" style="display:flex;gap:0;border-bottom:1px solid var(--color-border, #ddd);margin:0 -16px 12px -16px;padding:0 16px;">
            ${tabs.map((t, i) => `<button type="button" role="tab" class="mailx-shortcuts-tab" data-tab="${t.id}" aria-selected="${i === 0}" style="background:none;border:0;padding:8px 14px;cursor:pointer;font:inherit;border-bottom:2px solid ${i === 0 ? "var(--color-accent, #3b82f6)" : "transparent"};color:${i === 0 ? "var(--color-text)" : "var(--color-text-muted, #888)"};">${t.label}</button>`).join("")}
        </div>
        <div class="mailx-about">
            ${tabs.map((t, i) => `<div class="mailx-shortcuts-pane" data-pane="${t.id}" ${i === 0 ? "" : "hidden"}>
                <dl class="mailx-about-dl">
                    ${t.rows.map(([k, v]) => `<dt>${k}</dt><dd>${v}</dd>`).join("")}
                </dl>
            </div>`).join("")}
            <div class="mailx-about-foot">On Android most shortcuts need a Bluetooth/USB keyboard. Touch equivalents: tap a row to view, tap the rail icons for navigation, long-press for context menu, double-tap the message body for fullscreen.</div>
        </div>
        <div class="mailx-modal-buttons">
            <span class="mailx-modal-spacer"></span>
            <button type="button" class="mailx-modal-btn mailx-modal-btn-primary" data-action="close">Close</button>
        </div>`;
    backdrop.appendChild(panel);
    document.body.appendChild(backdrop);
    // Wire tab clicks
    panel.querySelectorAll<HTMLButtonElement>(".mailx-shortcuts-tab").forEach(tab => {
        tab.addEventListener("click", () => {
            const which = tab.dataset.tab;
            panel.querySelectorAll<HTMLButtonElement>(".mailx-shortcuts-tab").forEach(t => {
                const sel = t.dataset.tab === which;
                t.setAttribute("aria-selected", String(sel));
                t.style.borderBottom = `2px solid ${sel ? "var(--color-accent, #3b82f6)" : "transparent"}`;
                t.style.color = sel ? "var(--color-text)" : "var(--color-text-muted, #888)";
            });
            panel.querySelectorAll<HTMLElement>(".mailx-shortcuts-pane").forEach(p => {
                p.hidden = p.dataset.pane !== which;
            });
        });
    });
    const close = () => { backdrop.remove(); document.removeEventListener("keydown", onKey, true); };
    const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") { e.stopPropagation(); e.preventDefault(); close(); }
    };
    document.addEventListener("keydown", onKey, true);
    const closeBtn = panel.querySelector<HTMLButtonElement>("#sc-x");
    closeBtn?.addEventListener("click", close);
    backdrop.addEventListener("mousedown", (e) => { if (e.target === backdrop) close(); });
}

// ── View menu ──

const viewBtn = document.getElementById("btn-view");
const viewDropdown = document.getElementById("view-dropdown");

// View → New window: another full mailx window as its own OS window
// (side-by-side folder views). Daemon spawns it via the popout server;
// works from secondary windows too (each click = one more window).
document.getElementById("opt-new-window")?.addEventListener("click", async () => {
    if (viewDropdown) viewDropdown.hidden = true;
    try {
        const { popoutMainWindow } = await import("./lib/api-client.js");
        const r = await popoutMainWindow();
        if (!r?.ok) showAlert(`New window failed: ${r?.reason || "unknown"}`);
    } catch (e: any) {
        showAlert(`New window failed: ${e?.message || e}`);
    }
});

const optTwoLine = document.getElementById("opt-two-line") as HTMLInputElement;
const optEleanor = document.getElementById("opt-eleanor") as HTMLInputElement;
const optPreview = document.getElementById("opt-preview") as HTMLInputElement;
const optSnippet = document.getElementById("opt-snippet") as HTMLInputElement;
const optFlagStars = document.getElementById("opt-flag-stars") as HTMLInputElement;
const optThreaded = document.getElementById("opt-threaded") as HTMLInputElement;
const optFlagged = document.getElementById("opt-flagged") as HTMLInputElement;
const optFolderCounts = document.getElementById("opt-folder-counts") as HTMLInputElement;
const optCalendarSidebar = document.getElementById("opt-calendar-sidebar") as HTMLInputElement;
const optThreadFilter = document.getElementById("opt-thread-filter") as HTMLInputElement;

// Toggle dropdown — also close any other open toolbar menu so they can't
// overlap. Without this, opening View while Settings was already open left
// both visible at once (user-reported screenshot).
viewBtn?.addEventListener("click", (e) => {
    e.stopPropagation();
    const settingsDd = document.getElementById("settings-dropdown");
    if (settingsDd) settingsDd.hidden = true;
    const restartDd = document.getElementById("restart-dropdown");
    if (restartDd) restartDd.hidden = true;
    // If the dropdown was previously opened via the rail (re-parented to
    // body with inline modal styles), restore it to its toolbar parent so
    // it renders in the expected position-absolute location.
    restoreToolbarDropdown("view-dropdown", "view-menu");
    if (viewDropdown) viewDropdown.hidden = !viewDropdown.hidden;
    refreshToolbarOverlayShield();
});
// Capture-phase pointerdown so we run BEFORE any handler that calls
// stopPropagation. The earlier bubble-phase click listener missed clicks
// when an upstream handler swallowed propagation, and the menu stayed open.
// pointerdown also feels snappier than waiting for click (which fires on
// pointerup). The closest() checks let inside-clicks (radio buttons,
// checkboxes, the input) keep the menu open.
document.addEventListener("pointerdown", (e) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    // Don't close if the click is on the toolbar button that toggles us —
    // its own handler will run after this and toggle, otherwise we'd
    // close-then-reopen-then-close.
    if (target.closest("#btn-view, #btn-settings, #rail-view, #rail-settings, #rail-menu-backdrop")) return;
    // Re-query the dropdown elements FRESH on every pointerdown rather than
    // using the module-load-time references. If anything re-rendered the
    // toolbar (theme change, update banner, settings-applied repaint) the
    // cached node is detached and `.hidden = true` on it does nothing while
    // the live element stays visible — the "menu won't close until I restart
    // the app" bug (Bob 2026-05-28). Querying live makes it robust against
    // element replacement.
    const viewDd = document.getElementById("view-dropdown");
    const settingsDd2 = document.getElementById("settings-dropdown");
    if (viewDd && !viewDd.hidden && !target.closest("#view-menu") && !target.closest("#view-dropdown")) {
        hideDropdownHard(viewDd);
    }
    if (settingsDd2 && !settingsDd2.hidden && !target.closest("#settings-menu") && !target.closest("#settings-dropdown")) {
        hideDropdownHard(settingsDd2);
    }
    document.getElementById("rail-menu-backdrop")?.remove();
    refreshToolbarOverlayShield();
}, true);

/** Hide a toolbar/rail dropdown so it STAYS hidden regardless of how it was
 *  opened. `.hidden = true` alone isn't enough when the rail path left inline
 *  `position:fixed; display/visibility` styles on the element — an inline
 *  `display` would override the `[hidden]` UA rule and the menu would stay
 *  visible until an app restart cleared the element (Bob 2026-05-28 "click
 *  outside the settings menu but it does not go away"). Clear the inline
 *  style block too so nothing can keep it on screen. */
function hideDropdownHard(dd: HTMLElement): void {
    dd.hidden = true;
    // Wipe rail-modal inline styles (position/top/left/transform/zIndex/…).
    // Safe for the toolbar path too — its position comes from CSS classes,
    // not inline styles, so clearing inline leaves it correct.
    dd.style.cssText = "";
}

// Click-to-dismiss for the toolbar dropdowns relies on `pointerdown` firing
// on the parent document. Clicks inside the compose iframe stay in the
// iframe's document and never bubble out, so a Settings/View menu opened
// over compose used to stay stuck until the user hit Escape or the toolbar
// button again. Shield the overlay with a transparent fullscreen catcher
// while any toolbar dropdown is open — its pointerdown lands here in the
// parent doc, triggering the same outside-click close. The shield sits
// above compose (z 1600) but below the dropdown itself (z 2000).
// Cleanup for the in-iframe Escape forwarder (see below). Null when no
// forwarder is attached.
let tbEscapeCleanup: (() => void) | null = null;
function closeToolbarDropdowns(): void {
    for (const id of ["settings-dropdown", "view-dropdown", "restart-dropdown"]) {
        const dd = document.getElementById(id);
        if (dd && !dd.hidden) hideDropdownHard(dd);
    }
}
function refreshToolbarOverlayShield(): void {
    const dropdownOpen = ["settings-dropdown", "view-dropdown", "restart-dropdown"]
        .some(id => {
            const el = document.getElementById(id);
            return el && !el.hidden;
        });
    const hasOverlay = !!document.querySelector(".compose-overlay, .popout-overlay");
    let shield = document.getElementById("tb-overlay-shield");
    if (dropdownOpen && hasOverlay) {
        if (!shield) {
            shield = document.createElement("div");
            shield.id = "tb-overlay-shield";
            shield.style.cssText = "position:fixed;inset:0;z-index:1999;background:transparent;";
            shield.addEventListener("pointerdown", (ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                closeToolbarDropdowns();
                refreshToolbarOverlayShield();
            });
            document.body.appendChild(shield);
        }
        // Escape with the cursor inside the compose editor never reaches the
        // parent's Escape handler — the same-origin iframe captures the
        // keystroke. While a toolbar menu is open over compose, attach a
        // capture-phase keydown to each compose/popout iframe document that
        // closes the menu and swallows the event, so compose's OWN Escape
        // (the save/discard prompt) doesn't also fire. Only active while a
        // menu is open; detached when it closes (Bob 2026-06-21 "fix esc").
        if (!tbEscapeCleanup) {
            const docs: Document[] = [];
            document.querySelectorAll<HTMLIFrameElement>(".compose-overlay iframe, .popout-overlay iframe")
                .forEach(ifr => { try { if (ifr.contentDocument) docs.push(ifr.contentDocument); } catch { /* cross-origin */ } });
            if (docs.length) {
                const onEsc = (e: KeyboardEvent): void => {
                    if (e.key !== "Escape") return;
                    if (!["settings-dropdown", "view-dropdown", "restart-dropdown"]
                        .some(id => { const el = document.getElementById(id); return el && !el.hidden; })) return;
                    e.preventDefault();
                    e.stopPropagation();
                    closeToolbarDropdowns();
                    refreshToolbarOverlayShield();
                };
                for (const d of docs) d.addEventListener("keydown", onEsc, true);
                tbEscapeCleanup = () => {
                    for (const d of docs) { try { d.removeEventListener("keydown", onEsc, true); } catch { /* */ } }
                    tbEscapeCleanup = null;
                };
            }
        }
    } else {
        if (shield) shield.remove();
        if (tbEscapeCleanup) tbEscapeCleanup();
    }
}
// Escape always closes any open dropdown/backdrop, regardless of how it was
// opened or whether an outside-click handler missed firing. Last-resort
// escape hatch for the "stuck modal" case.
document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    let closed = false;
    for (const id of ["settings-dropdown", "view-dropdown", "restart-dropdown"]) {
        const dd = document.getElementById(id);
        if (dd && !dd.hidden) { dd.hidden = true; closed = true; }
    }
    const bd = document.getElementById("rail-menu-backdrop");
    if (bd) { bd.remove(); closed = true; }
    const themeSub = document.getElementById("theme-submenu");
    if (themeSub && !themeSub.hidden) { themeSub.hidden = true; closed = true; }
    if (closed) {
        e.preventDefault();
        refreshToolbarOverlayShield();
    }
});

// Restore saved view settings
const savedTwoLine = localStorage.getItem("mailx-two-line") === "true";
const savedEleanor = localStorage.getItem("mailx-eleanor-view") === "true";
const savedPreview = localStorage.getItem("mailx-preview") !== "false"; // default true
const savedSnippet = localStorage.getItem("mailx-snippet") !== "false"; // default true
const savedFlagStars = localStorage.getItem("mailx-flag-stars") !== "false"; // default true (show)
const savedThreaded = localStorage.getItem("mailx-threaded") === "true";
// Flagged-only ("★ only") is EPHEMERAL — it must NOT persist across restarts.
// Restoring it on boot left the user stuck every launch in a gold-washed,
// all-starred list with no obvious way out, and the ★ toggle felt like a no-op
// because the state kept coming back (Bob 2026-06-27). Always start OFF and
// clear any stale stored value; the in-session toggle still works normally.
const savedFlagged = false;
try { localStorage.removeItem("mailx-flagged"); } catch { /* private mode */ }
const savedFolderCounts = localStorage.getItem("mailx-folder-counts") === "true";
if (optTwoLine) optTwoLine.checked = savedTwoLine;
if (optEleanor) optEleanor.checked = savedEleanor;
if (optPreview) optPreview.checked = savedPreview;
if (optSnippet) optSnippet.checked = savedSnippet;
if (optFlagStars) optFlagStars.checked = savedFlagStars;
if (optThreaded) optThreaded.checked = savedThreaded;
if (optFlagged) optFlagged.checked = savedFlagged;
if (optFolderCounts) optFolderCounts.checked = savedFolderCounts;
if (savedTwoLine) document.getElementById("message-list")?.classList.add("two-line");
if (savedEleanor) document.getElementById("message-list")?.classList.add("eleanor-view");
if (!savedPreview) document.querySelector(".main-area")?.classList.add("no-preview");
if (!savedSnippet) document.getElementById("message-list")?.classList.add("no-snippets");
if (!savedFlagStars) document.getElementById("message-list")?.classList.add("no-empty-stars");
if (savedThreaded) document.getElementById("ml-body")?.classList.add("threaded");
// (flagged-only intentionally NOT restored — ephemeral, see savedFlagged above)
if (savedFolderCounts) document.getElementById("folder-tree")?.classList.add("show-folder-counts");

// "Only this conversation" toggle — hides rows whose threadId differs from
// the currently-selected message's threadId. Client-side only (no server
// round-trip); toggling off restores the full list. Persisted per-session
// but not across reloads (thread context is tied to current selection).
optThreadFilter?.addEventListener("change", () => {
    const body = document.getElementById("ml-body");
    if (!body) return;
    body.classList.toggle("thread-filter-on", optThreadFilter.checked);
    applyThreadFilter();
});
// Re-apply thread filter whenever the list contents change (load,
// search, paged append, removal). The filter also depends on the
// currently-focused row's threadId, so wire mailx-focus-changed too —
// a focus change without a list-content change still needs the filter
// to recompute which sibling rows to hide/show.
messageState.subscribe(() => applyThreadFilter());
document.addEventListener("mailx-focus-changed", () => applyThreadFilter());

function applyThreadFilter(): void {
    const body = document.getElementById("ml-body");
    if (!body) return;
    if (!optThreadFilter?.checked) {
        body.querySelectorAll<HTMLElement>(".ml-row.thread-filter-hidden")
            .forEach(r => r.classList.remove("thread-filter-hidden"));
        return;
    }
    const sel = getCurrentFocused() as any;
    const tid = sel?.threadId;
    if (!tid) return;
    body.querySelectorAll<HTMLElement>(".ml-row").forEach(r => {
        const rowTid = r.dataset.threadId;
        if (rowTid === tid || r.classList.contains("selected")) {
            r.classList.remove("thread-filter-hidden");
        } else {
            r.classList.add("thread-filter-hidden");
        }
    });
}

// S51 — Calendar sidebar: View-menu toggle, restore from localStorage,
// hide auto-magically on narrow screens (CSS handles that).
(async () => {
    const { initCalendarSidebar, isCalendarSidebarOn, showCalendarSidebar, hideCalendarSidebar } =
        await import("./components/calendar-sidebar.js");
    initCalendarSidebar();
    const on = isCalendarSidebarOn();
    if (optCalendarSidebar) optCalendarSidebar.checked = on;
    if (on) await showCalendarSidebar();
    optCalendarSidebar?.addEventListener("change", () => {
        if (optCalendarSidebar.checked) showCalendarSidebar();
        else hideCalendarSidebar();
    });
})();

// P17 / Q104: alarm subsystem — Thunderbird/Outlook-style popup with
// snooze + dismiss. Covers calendar events + tasks today; mail reminders
// will slot in here when the mail-reminder feature lands.
(async () => {
    try {
        const { startAlarmPoller } = await import("./components/alarms.js");
        startAlarmPoller();
    } catch (e: any) {
        console.error("alarm poller init failed:", e?.message || e);
    }
})();

// Pull-to-refresh: touch-drag down at the top of the message list fires a
// sync, same as F5 / btn-sync. Standard Android / Thunderbird / mobile
// pattern. The indicator slides down with the drag and rotates the chevron
// once the user crosses the trigger threshold; releasing past the threshold
// fires triggerSync(). Below threshold = snap back without action.
//
// Touch-only by design — desktop has the F5 / sync button. Pointer events
// would also catch mouse-drag, which is more annoying than useful (people
// drag the scrollbar). The `e.touches.length === 1` check skips two-finger
// gestures (pinch-zoom, two-finger scroll).
(() => {
    const PTR_THRESHOLD_PX = 80;          // pull this far to arm the trigger
    const PTR_MAX_PX = 120;                // cap the visual translate
    const mlBody = document.getElementById("ml-body");
    if (!mlBody) return;
    const indicator = document.createElement("div");
    indicator.className = "ptr-indicator";
    indicator.innerHTML = `<span class="ptr-chev"></span><span class="ptr-label">Pull to refresh</span>`;
    mlBody.parentElement?.insertBefore(indicator, mlBody);
    const labelEl = indicator.querySelector(".ptr-label") as HTMLElement;

    let startY = 0;
    let pullY = 0;
    let active = false;
    let refreshing = false;

    const setPull = (px: number, armed: boolean): void => {
        const clamped = Math.min(PTR_MAX_PX, Math.max(0, px));
        indicator.classList.toggle("armed", armed);
        indicator.style.transform = `translateY(calc(-100% + ${clamped}px))`;
    };
    const reset = (): void => {
        indicator.classList.remove("dragging", "armed");
        indicator.style.transform = "";
    };

    mlBody.addEventListener("touchstart", (e: TouchEvent) => {
        if (refreshing) return;
        if (e.touches.length !== 1) return;
        if (mlBody.scrollTop > 0) return;
        startY = e.touches[0].clientY;
        pullY = 0;
        active = true;
        indicator.classList.add("dragging");
        labelEl.textContent = "Pull to refresh";
    }, { passive: true });

    mlBody.addEventListener("touchmove", (e: TouchEvent) => {
        if (!active || refreshing) return;
        const dy = e.touches[0].clientY - startY;
        if (dy <= 0) {
            // User scrolled up past the start — abandon, let normal scroll
            // take over without reverting (the touchend handler will reset).
            pullY = 0;
            setPull(0, false);
            return;
        }
        // Resist past 0 — pull "stretches" rather than scrolls. preventDefault
        // is necessary to keep the browser from interpreting the drag as
        // overscroll / browser-level pull-to-refresh.
        if (e.cancelable) e.preventDefault();
        pullY = dy * 0.5;                  // 0.5 = rubber-band damping
        const armed = pullY >= PTR_THRESHOLD_PX;
        labelEl.textContent = armed ? "Release to refresh" : "Pull to refresh";
        setPull(pullY, armed);
    }, { passive: false });

    const finish = async (): Promise<void> => {
        if (!active) return;
        active = false;
        if (pullY < PTR_THRESHOLD_PX) { reset(); return; }
        // Armed — fire the sync and hide the indicator immediately. No
        // "Refreshing..." banner: the bottom-bar `#status-sync` already
        // shows sync progress ("Syncing gmail: sync:INBOX 0/0"), so a
        // second top-banner is duplicate UI taking space (Bob 2026-05-26
        // "drop refreshing since there is already syncing"). Briefly
        // tint the status text so the user knows their gesture fired.
        refreshing = true;
        reset();
        const statusSync = document.getElementById("status-sync");
        const prevColor = statusSync?.style.color || "";
        if (statusSync) statusSync.style.color = "var(--color-brand-dark, #2563eb)";
        try {
            const { triggerSync } = await import("./lib/api-client.js");
            await triggerSync();
        } catch (e: any) {
            console.error("pull-to-refresh failed:", e?.message || e);
        }
        if (statusSync) statusSync.style.color = prevColor;
        refreshing = false;
    };
    mlBody.addEventListener("touchend", () => { finish(); }, { passive: true });
    mlBody.addEventListener("touchcancel", () => { active = false; reset(); }, { passive: true });
})();

// P115: pick up a pending mailto: drop file dropped by the CLI invocation
// that spawned us (or by an earlier --mailto run that the user hasn't yet
// acknowledged). The IPC call deletes the file as part of reading it, so a
// race with the daemon's fs.watch is harmless — whichever fires first wins
// and the other gets null. Subsequent live clicks during this session
// arrive via the `openMailto` event handled in onEvent above.
(async () => {
    try {
        const { consumePendingMailto } = await import("./lib/api-client.js");
        const data = await consumePendingMailto();
        if (data && (data.to.length || data.subject || data.body)) {
            await openComposeFromMailto(data);
        }
    } catch (e: any) {
        console.error("pending mailto pickup failed:", e?.message || e);
    }
})();

// C46: same startup pickup for a pending Windows-share drop (rmfshare.exe
// spawned the daemon before its fs.watch armed). Live shares during this
// session arrive via the `openShare` event handled in onEvent above.
(async () => {
    try {
        const { consumePendingShare } = await import("./lib/api-client.js");
        const data = await consumePendingShare();
        if (data && (data.attachments.length || data.text || data.url || data.title || data.skipped.length)) {
            await openComposeFromShare(data);
        }
    } catch (e: any) {
        console.error("pending share pickup failed:", e?.message || e);
    }
})();

// Two-line toggle
optTwoLine?.addEventListener("change", () => {
    const list = document.getElementById("message-list");
    if (optTwoLine.checked) {
        list?.classList.add("two-line");
    } else {
        list?.classList.remove("two-line");
    }
    localStorage.setItem("mailx-two-line", String(optTwoLine.checked));
});

// Eleanor view toggle — Thunderbird-classic two-line card list at every
// width (From … Date / Subject, outlined entries, airy, monochrome).
// Pure CSS class; the row markup is unchanged. TODO.md: generalize into
// per-column view control instead of a named preset.
function applyEleanorView(on: boolean): void {
    document.getElementById("message-list")?.classList.toggle("eleanor-view", on);
    if (optEleanor) optEleanor.checked = on;
    try { localStorage.setItem("mailx-eleanor-view", String(on)); } catch { /* private mode */ }
}
optEleanor?.addEventListener("change", () => {
    applyEleanorView(optEleanor.checked);
    // Mirror to preferences.jsonc (ui.eleanorView) so the choice follows the
    // shared config across machines; localStorage stays the synchronous
    // first-paint cache, same split as the editor setting.
    getSettings().then((settings: any) => {
        settings.ui = { ...settings.ui, eleanorView: optEleanor.checked };
        saveSettings(settings);
    }).catch(() => {});
});
// A ui.eleanorView value in preferences.jsonc wins over the local cache once
// it arrives — so the view can be enabled by editing the shared config file
// directly, without touching the View menu on that machine.
getSettings().then((s: any) => {
    const pref = s.ui?.eleanorView;
    if (typeof pref === "boolean" && pref !== (localStorage.getItem("mailx-eleanor-view") === "true")) {
        applyEleanorView(pref);
    }
}).catch(() => { /* offline boot — keep the localStorage value */ });

// Preview pane toggle
optPreview?.addEventListener("change", () => {
    const main = document.querySelector(".main-area");
    if (optPreview.checked) {
        main?.classList.remove("no-preview");
    } else {
        main?.classList.add("no-preview");
    }
    localStorage.setItem("mailx-preview", String(optPreview.checked));
});

// Preview snippet toggle
optSnippet?.addEventListener("change", () => {
    const list = document.getElementById("message-list");
    if (optSnippet.checked) {
        list?.classList.remove("no-snippets");
    } else {
        list?.classList.add("no-snippets");
    }
    localStorage.setItem("mailx-snippet", String(optSnippet.checked));
});

optFlagStars?.addEventListener("change", () => {
    const list = document.getElementById("message-list");
    // checked = show empty stars (no class); unchecked = hide them.
    list?.classList.toggle("no-empty-stars", !optFlagStars.checked);
    localStorage.setItem("mailx-flag-stars", String(optFlagStars.checked));
});

// ── Search help button (?) ──
// Toggles a NON-modal panel with the search-syntax reference so it stays
// visible while the user types into the search box — build a query
// against it, then dismiss. The panel is anchored directly below the
// search bar (no dark backdrop, no centered modal).
//
// The help content is HTML compiled into the app (client/help/search-help.ts),
// NOT a docs/*.md file. Feature help is application content; the docs/*.md
// files are only a stand-in for proper settings UI and stay out of this path.
// The module is dynamically imported so its markup isn't in the cold-start
// bundle.
document.getElementById("search-help")?.addEventListener("click", async () => {
    const btn = document.getElementById("search-help") as HTMLButtonElement | null;
    if (!btn) return;

    // Toggle: a second click on ? closes the open panel.
    const existing = document.getElementById("search-help-panel");
    if (existing) { existing.remove(); btn.setAttribute("aria-expanded", "false"); return; }

    const { SEARCH_HELP_HTML } = await import("./help/search-help.js");

    const searchBar = document.querySelector("search.ml-search") as HTMLElement | null;

    const panel = document.createElement("div");
    panel.id = "search-help-panel";
    panel.className = "search-help-panel";
    panel.innerHTML = `
        <div class="search-help-head">
            <span>Search syntax</span>
            <button type="button" id="search-help-close" title="Close (Esc)" aria-label="Close">&times;</button>
        </div>
        <div class="search-help-body">${SEARCH_HELP_HTML}</div>
    `;
    document.body.appendChild(panel);
    btn.setAttribute("aria-expanded", "true");

    // Anchor below the search bar, clamped to the viewport. Re-runs on
    // resize so the panel tracks the bar if the layout reflows.
    const position = (): void => {
        const anchor = searchBar || btn;
        const rect = anchor.getBoundingClientRect();
        const margin = 8;
        const top = rect.bottom + 2;
        const width = Math.min(640, Math.max(320, rect.width));
        let left = rect.left;
        if (left + width > window.innerWidth - margin) left = window.innerWidth - margin - width;
        if (left < margin) left = margin;
        panel.style.top = `${top}px`;
        panel.style.left = `${left}px`;
        panel.style.width = `${width}px`;
        panel.style.maxHeight = `${Math.max(160, window.innerHeight - top - margin)}px`;
    };
    position();

    const close = (): void => {
        panel.remove();
        btn.setAttribute("aria-expanded", "false");
        window.removeEventListener("resize", position);
        document.removeEventListener("keydown", onKey, true);
        document.removeEventListener("mousedown", onOutside);
    };
    // Esc always closes the help panel while it's open — including when
    // focus is in the search box. Capture phase + stopPropagation so the
    // search input doesn't also clear/blur on the same keystroke.
    const onKey = (e: KeyboardEvent): void => {
        if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            close();
        }
    };
    // Click outside dismisses — except clicks on the search bar itself
    // (typing a query must not close the reference) or the ? button
    // (its own handler toggles).
    const onOutside = (e: MouseEvent): void => {
        const t = e.target as Node;
        if (panel.contains(t) || btn.contains(t) || searchBar?.contains(t)) return;
        close();
    };
    panel.querySelector("#search-help-close")?.addEventListener("click", close);
    window.addEventListener("resize", position);
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("mousedown", onOutside);
});

// ── JSONC config file editor ──
document.getElementById("btn-edit-jsonc")?.addEventListener("click", async () => {
    const settingsDropdown = document.getElementById("settings-dropdown");
    if (settingsDropdown) settingsDropdown.hidden = true;
    await openJsoncEditor("accounts.jsonc");
});
// Settings → "Make default email app...". Registers rmfmail as the OS
// mailto: handler (same work as `rmfmail -register-mailto`), then walks the
// user through the one step Windows refuses to automate: picking rmfmail in
// Settings → Default apps (UserChoice is hash-protected). Discoverability
// fix — the CLI flag existed but nothing in the app pointed at it (Bob
// 2026-07-20 "couldn't figure out how to make rmfmail the responder").
document.getElementById("btn-register-mailto")?.addEventListener("click", async () => {
    const settingsDropdown = document.getElementById("settings-dropdown");
    if (settingsDropdown) settingsDropdown.hidden = true;
    const { registerMailto } = await import("./lib/api-client.js");
    const r = await registerMailto();

    const backdrop = document.createElement("div");
    backdrop.className = "mailx-modal-backdrop";
    backdrop.style.cssText = "position:fixed;inset:0;z-index:2000;background:rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;";
    const panel = document.createElement("div");
    panel.style.cssText = "background:var(--color-bg,#fff);color:var(--color-text,#000);border-radius:8px;box-shadow:0 8px 32px rgba(0,0,0,0.3);width:min(520px,90vw);padding:18px;display:flex;flex-direction:column;gap:12px;";
    const heading = document.createElement("div");
    heading.style.cssText = "font-weight:600;font-size:1.05em;";
    heading.textContent = r.ok ? "rmfmail registered as an email handler" : "Registration failed";
    const body = document.createElement("div");
    body.style.cssText = "font-size:0.92em;line-height:1.45;white-space:pre-wrap;";
    body.textContent = r.ok
        ? "One manual step remains — Windows only lets YOU flip the default:\n\nSettings → Apps → Default apps → search \"rmfmail\" → set it for MAILTO.\n\nIf a picker warns the app must be from the Store, choose \"More options\" / \"Choose an app on this PC\"."
        : r.message;
    const btnRow = document.createElement("div");
    btnRow.style.cssText = "display:flex;gap:8px;justify-content:flex-end;";
    const closeBtn = document.createElement("button");
    closeBtn.textContent = "Close";
    closeBtn.style.cssText = "padding:6px 14px;";
    closeBtn.addEventListener("click", () => backdrop.remove());
    btnRow.append(closeBtn);
    if (r.ok) {
        const openBtn = document.createElement("button");
        openBtn.textContent = "Open Default-apps settings";
        openBtn.style.cssText = "padding:6px 14px;font-weight:500;";
        openBtn.addEventListener("click", () => {
            const api = (window as any).mailxapi;
            if (api?.openExternal) api.openExternal("ms-settings:defaultapps");
            backdrop.remove();
        });
        btnRow.append(openBtn);
    }
    panel.append(heading, body, btnRow);
    backdrop.append(panel);
    document.body.append(backdrop);
});
// Allow other components (remote-content banner, etc.) to open the editor
// pre-selected to a specific file.
document.addEventListener("mailx-open-jsonc-editor", async (ev: Event) => {
    const file = ((ev as CustomEvent).detail?.file as string) || "accounts.jsonc";
    await openJsoncEditor(file);
});
// Q61: open ~/.mailx in OS file explorer.
document.getElementById("btn-open-mailx-dir")?.addEventListener("click", async () => {
    const settingsDropdown = document.getElementById("settings-dropdown");
    if (settingsDropdown) settingsDropdown.hidden = true;
    try {
        const { openLocalPath } = await import("./lib/api-client.js");
        await openLocalPath("config");
    } catch (e: any) {
        alert(`Couldn't open folder: ${e?.message || e}`);
    }
});
// Q62: open today's log file.
document.getElementById("btn-open-log")?.addEventListener("click", async () => {
    const settingsDropdown = document.getElementById("settings-dropdown");
    if (settingsDropdown) settingsDropdown.hidden = true;
    try {
        const { openLocalPath } = await import("./lib/api-client.js");
        await openLocalPath("log");
    } catch (e: any) {
        alert(`Couldn't open log: ${e?.message || e}`);
    }
});

async function openJsoncEditor(initialFile: string): Promise<void> {
    const { readJsoncFile, writeJsoncFile, readConfigHelp, formatJsonc, openLocalPath } = await import("./lib/api-client.js");

    const backdrop = document.createElement("div");
    backdrop.className = "mailx-modal-backdrop";
    const panel = document.createElement("div");
    panel.className = "mailx-modal mailx-modal-wide";
    panel.innerHTML = `
        <div class="mailx-modal-title">
            <span class="mailx-modal-title-text">Edit config file</span>
            <button type="button" class="mailx-modal-close" id="jsonc-close" title="Close (Esc)" aria-label="Close">&times;</button>
        </div>
        <label class="mailx-modal-label">File
            <select class="mailx-modal-input" id="jsonc-file">
                <option value="accounts.jsonc">accounts.jsonc — accounts (shared via Google Drive)</option>
                <option value="contacts.jsonc">contacts.jsonc — preferred + denylist + discovered (shared)</option>
                <option value="allowlist.jsonc">allowlist.jsonc — remote-content allowlist (shared)</option>
                <option value="clients.jsonc">clients.jsonc — per-device registrations (shared)</option>
                <option value="config.jsonc">config.jsonc — local per-machine overrides (not synced)</option>
            </select>
        </label>
        <div class="mailx-modal-split">
            <label class="mailx-modal-label mailx-modal-split-left">Contents (JSONC — comments and trailing commas allowed)
                <div class="jsonc-editor-wrap">
                    <div class="jsonc-gutter" id="jsonc-gutter" aria-hidden="true"></div>
                    <textarea class="mailx-modal-input mailx-modal-textarea jsonc-textarea" id="jsonc-content" spellcheck="false"></textarea>
                </div>
            </label>
            <div class="mailx-modal-split-right mailx-help-panel">
                <div class="mailx-help-title">
                    <button type="button" class="mailx-help-toggle" id="jsonc-help-toggle" aria-expanded="true" title="Hide/show help">▾ Help</button>
                </div>
                <div class="mailx-help-body" id="jsonc-help-body"></div>
            </div>
        </div>
        <div class="mailx-modal-error" id="jsonc-error" hidden></div>
        <div class="mailx-modal-buttons">
            <button type="button" class="mailx-modal-btn" data-action="format" title="Reformat indentation while preserving comments and trailing commas">Format</button>
            <button type="button" class="mailx-modal-btn" data-action="opensource" id="jsonc-opensource" title="Open the folder containing config.jsonc so you can edit it in a full editor" hidden>Open source folder</button>
            <span class="mailx-modal-spacer"></span>
            <button type="button" class="mailx-modal-btn" data-action="cancel">Cancel</button>
            <button type="button" class="mailx-modal-btn mailx-modal-btn-primary" data-action="save">Save</button>
        </div>`;
    backdrop.appendChild(panel);
    document.body.appendChild(backdrop);

    const fileSelect = panel.querySelector<HTMLSelectElement>("#jsonc-file")!;
    const textarea = panel.querySelector<HTMLTextAreaElement>("#jsonc-content")!;
    const gutter = panel.querySelector<HTMLElement>("#jsonc-gutter")!;
    const errorEl = panel.querySelector<HTMLElement>("#jsonc-error")!;
    const saveBtn = panel.querySelector<HTMLButtonElement>('[data-action="save"]')!;
    const helpBody = panel.querySelector<HTMLElement>("#jsonc-help-body")!;
    const helpToggle = panel.querySelector<HTMLButtonElement>("#jsonc-help-toggle")!;
    const helpPanel = panel.querySelector<HTMLElement>(".mailx-help-panel")!;
    fileSelect.value = initialFile;

    // Line-number gutter — recomputed whenever the textarea content changes,
    // scroll-synced so numbers stay aligned. errorLine (1-based) is highlighted
    // red so the "Line N, col M" error message in the status bar points at a
    // visible marker in the gutter.
    let errorLine = 0;
    const renderGutter = () => {
        const lines = textarea.value.split("\n").length;
        let html = "";
        for (let i = 1; i <= lines; i++) {
            html += i === errorLine
                ? `<div class="jsonc-gutter-line jsonc-gutter-error">${i}</div>`
                : `<div class="jsonc-gutter-line">${i}</div>`;
        }
        gutter.innerHTML = html;
    };
    const syncScroll = () => { gutter.scrollTop = textarea.scrollTop; };
    textarea.addEventListener("scroll", syncScroll);
    textarea.addEventListener("input", renderGutter);

    helpToggle.addEventListener("click", () => {
        const open = helpPanel.classList.toggle("mailx-help-collapsed");
        helpToggle.textContent = open ? "▸ Help" : "▾ Help";
        helpToggle.setAttribute("aria-expanded", open ? "false" : "true");
    });

    const loadHelp = async () => {
        helpBody.textContent = "Loading help…";
        try {
            const r = await readConfigHelp(fileSelect.value);
            const md = (r?.content || "").trim();
            helpBody.innerHTML = md ? renderMarkdown(md) : "<em>No help available for this file.</em>";
        } catch (e: any) {
            helpBody.textContent = `Help unavailable: ${e.message}`;
        }
    };

    const clearValidation = () => {
        errorEl.hidden = true;
        errorEl.textContent = "";
        textarea.classList.remove("mailx-modal-input-error");
        saveBtn.disabled = false;
        errorLine = 0;
        renderGutter();
    };
    const showValidation = (err: { message: string; pos: number; line: number; col: number }) => {
        // CRITICAL: do NOT move the cursor here. Validation fires every 600ms
        // while the user types; auto-selecting the error position yanked the
        // cursor mid-edit and made fixing the error impossible (the user
        // reported this as a fatal bug — the very mechanism preventing a save
        // was preventing the fix). Location is shown via the gutter highlight
        // + the "Line N, col M" message, and the user can click "Jump" to
        // explicitly navigate.
        errorEl.innerHTML = "";
        const text = document.createElement("span");
        text.textContent = `Line ${err.line}, col ${err.col}: ${err.message}  `;
        const jumpBtn = document.createElement("button");
        jumpBtn.type = "button";
        jumpBtn.className = "mailx-modal-btn mailx-modal-btn-link";
        jumpBtn.textContent = "Jump to error";
        jumpBtn.addEventListener("click", () => {
            textarea.focus();
            try { textarea.setSelectionRange(err.pos, err.pos + 1); } catch { /* */ }
        });
        errorEl.appendChild(text);
        errorEl.appendChild(jumpBtn);
        errorEl.hidden = false;
        textarea.classList.add("mailx-modal-input-error");
        saveBtn.disabled = true;
        errorLine = err.line;
        renderGutter();
    };

    let validateTimer: number | undefined;
    const scheduleValidate = () => {
        if (validateTimer) window.clearTimeout(validateTimer);
        validateTimer = window.setTimeout(() => {
            const err = validateJsonc(textarea.value);
            if (err) showValidation(err); else clearValidation();
        }, 600);
    };
    textarea.addEventListener("input", scheduleValidate);

    const loadFile = async () => {
        textarea.value = "Loading...";
        clearValidation();
        renderGutter();
        try {
            const r = await readJsoncFile(fileSelect.value);
            textarea.value = r?.content || "";
            renderGutter();
            scheduleValidate();
        } catch (e: any) {
            textarea.value = "";
            renderGutter();
            errorEl.textContent = `Failed to load: ${e.message}`;
            errorEl.hidden = false;
        }
    };
    // "Open source folder" — only meaningful for config.jsonc, the one
    // file that lives on the local disk (~/.rmfmail/config.jsonc). The
    // others are Google Drive objects with no local path, so the button
    // is hidden for them. Lets the user edit config.jsonc in a real editor
    // instead of the modal textarea.
    const openSourceBtn = panel.querySelector<HTMLButtonElement>("#jsonc-opensource")!;
    const updateOpenSource = () => { openSourceBtn.hidden = fileSelect.value !== "config.jsonc"; };
    updateOpenSource();

    await Promise.all([loadFile(), loadHelp()]);
    fileSelect.addEventListener("change", () => { loadFile(); loadHelp(); updateOpenSource(); });

    const close = () => {
        if (validateTimer) window.clearTimeout(validateTimer);
        backdrop.remove();
        document.removeEventListener("keydown", onKey, true);
    };
    const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") { e.stopPropagation(); e.preventDefault(); close(); }
    };
    document.addEventListener("keydown", onKey, true);
    panel.querySelector<HTMLButtonElement>("#jsonc-close")!.addEventListener("click", close);

    panel.querySelectorAll<HTMLButtonElement>(".mailx-modal-btn").forEach(btn => {
        btn.addEventListener("click", async () => {
            const action = btn.dataset.action;
            if (action === "cancel") { close(); return; }
            if (action === "opensource") {
                // Reveal ~/.rmfmail/ in the OS file manager — config.jsonc
                // sits there; the user can then open it in a full editor.
                try { await openLocalPath("config"); }
                catch (e: any) { errorEl.textContent = `Couldn't open folder: ${e?.message || e}`; errorEl.hidden = false; }
                return;
            }
            if (action === "format") {
                // Reformat via the service-side jsonc-parser format() — the
                // edits are whitespace-only, so `//` and `/* */` comments
                // survive intact (which JSON.stringify(parse(...)) does not).
                btn.disabled = true;
                const orig = btn.textContent;
                btn.textContent = "Formatting…";
                try {
                    const r = await formatJsonc(textarea.value);
                    if (r?.content !== undefined) {
                        textarea.value = r.content;
                        renderGutter();
                        scheduleValidate();
                    }
                } catch (e: any) {
                    errorEl.textContent = `Format failed: ${e.message}`;
                    errorEl.hidden = false;
                } finally {
                    btn.disabled = false;
                    btn.textContent = orig || "Format";
                }
                return;
            }
            if (action === "save") {
                // Final sync-check; refuse to save if it doesn't parse
                const err = validateJsonc(textarea.value);
                if (err) { showValidation(err); return; }
                errorEl.hidden = true;
                btn.disabled = true;
                btn.textContent = "Saving...";
                // Auto-format before save: the service-side formatter does
                // whitespace-only edits via jsonc-parser, so // and /* */
                // comments survive. If the format call fails (network /
                // parse) we fall back to the raw text — better to save
                // something than lose the user's edit.
                let toWrite = textarea.value;
                try {
                    const r = await formatJsonc(textarea.value);
                    if (r?.content !== undefined) {
                        toWrite = r.content;
                        textarea.value = r.content;
                    }
                } catch { /* keep raw text */ }
                try {
                    await writeJsoncFile(fileSelect.value, toWrite);
                    close();
                    const statusSync = document.getElementById("status-sync");
                    if (statusSync) statusSync.textContent = `Saved ${fileSelect.value} — restart mailx to apply`;
                } catch (e: any) {
                    errorEl.textContent = `${e.message}`;
                    errorEl.hidden = false;
                    btn.disabled = false;
                    btn.textContent = "Save";
                }
            }
        });
    });
    backdrop.addEventListener("mousedown", (e) => { if (e.target === backdrop) close(); });
}

// JSONC validator — strips comments + trailing commas (preserving source positions
// by replacing stripped chars with spaces/newlines) and runs JSON.parse. Reports
// only the *first* error; cascading errors are suppressed.
function validateJsonc(src: string): { message: string; pos: number; line: number; col: number } | null {
    const stripped = stripJsoncPreservingPositions(src);
    if (stripped.error) {
        const { pos, line, col } = offsetToLineCol(src, stripped.error.pos);
        return { message: stripped.error.message, pos, line, col };
    }
    if (stripped.text.trim() === "") return null; // empty file: treat as valid (settings code handles)
    try {
        JSON.parse(stripped.text);
        return null;
    } catch (e: any) {
        const msg = String(e?.message || "parse error");
        const m = msg.match(/at position (\d+)/i);
        const pos = m ? Math.min(parseInt(m[1], 10), src.length - 1) : 0;
        const lc = offsetToLineCol(src, pos);
        return { message: msg.replace(/\s*at position \d+/i, ""), pos: lc.pos, line: lc.line, col: lc.col };
    }
}

function stripJsoncPreservingPositions(src: string): { text: string; error?: { message: string; pos: number } } {
    const out: string[] = new Array(src.length);
    let i = 0;
    const n = src.length;
    while (i < n) {
        const c = src[i];
        const next = src[i + 1];
        if (c === '"') {
            out[i] = c; i++;
            while (i < n) {
                const ch = src[i];
                out[i] = ch; i++;
                if (ch === "\\" && i < n) { out[i] = src[i]; i++; continue; }
                if (ch === '"') break;
                if (ch === "\n") return { text: out.join(""), error: { message: "unterminated string", pos: i - 1 } };
            }
        } else if (c === "/" && next === "/") {
            while (i < n && src[i] !== "\n") { out[i] = " "; i++; }
        } else if (c === "/" && next === "*") {
            const start = i;
            out[i] = " "; out[i + 1] = " "; i += 2;
            let closed = false;
            while (i < n) {
                if (src[i] === "*" && src[i + 1] === "/") { out[i] = " "; out[i + 1] = " "; i += 2; closed = true; break; }
                out[i] = src[i] === "\n" ? "\n" : " "; i++;
            }
            if (!closed) return { text: out.join(""), error: { message: "unterminated block comment", pos: start } };
        } else if (c === ",") {
            // trailing comma before } or ] → replace with space
            let j = i + 1;
            while (j < n && /\s/.test(src[j])) j++;
            if (j < n && (src[j] === "}" || src[j] === "]")) { out[i] = " "; i++; }
            else { out[i] = c; i++; }
        } else {
            out[i] = c; i++;
        }
    }
    return { text: out.join("") };
}

function offsetToLineCol(src: string, pos: number): { pos: number; line: number; col: number } {
    pos = Math.max(0, Math.min(pos, src.length));
    let line = 1, col = 1;
    for (let i = 0; i < pos; i++) {
        if (src[i] === "\n") { line++; col = 1; } else col++;
    }
    return { pos, line, col };
}

// Minimal markdown renderer for the help panel. Supports: headings, fenced code blocks,
// inline code, bold, italic, links, bullet lists, paragraphs. HTML is escaped first.
function renderMarkdown(md: string): string {
    const esc = (s: string) => s.replace(/[&<>"']/g, c =>
        ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);

    // Pull fenced code blocks out first so their contents aren't processed as markdown.
    const blocks: string[] = [];
    let src = md.replace(/```(\w*)\n([\s\S]*?)```/g, (_m, _lang, code) => {
        const i = blocks.length;
        blocks.push(`<pre class="mailx-help-code"><code>${esc(code)}</code></pre>`);
        return `\u0000BLOCK${i}\u0000`;
    });

    const lines = src.split(/\r?\n/);
    const out: string[] = [];
    let inList = false;
    let para: string[] = [];
    const flushPara = () => {
        if (para.length) { out.push(`<p>${inline(para.join(" "))}</p>`); para = []; }
    };
    const closeList = () => { if (inList) { out.push("</ul>"); inList = false; } };

    function inline(s: string): string {
        s = esc(s);
        s = s.replace(/`([^`]+)`/g, (_m, c) => `<code>${c}</code>`);
        s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
        s = s.replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>");
        s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
        return s;
    }

    for (const raw of lines) {
        const blockMatch = /^\u0000BLOCK(\d+)\u0000$/.exec(raw);
        if (blockMatch) { flushPara(); closeList(); out.push(blocks[parseInt(blockMatch[1], 10)]); continue; }
        const h = /^(#{1,6})\s+(.+)$/.exec(raw);
        if (h) { flushPara(); closeList(); const lvl = h[1].length; out.push(`<h${lvl}>${inline(h[2])}</h${lvl}>`); continue; }
        const bullet = /^\s*[-*]\s+(.+)$/.exec(raw);
        if (bullet) {
            flushPara();
            if (!inList) { out.push("<ul>"); inList = true; }
            out.push(`<li>${inline(bullet[1])}</li>`);
            continue;
        }
        if (raw.trim() === "") { flushPara(); closeList(); continue; }
        para.push(raw);
    }
    flushPara();
    closeList();
    return out.join("\n");
}

// ── Keyboard shortcuts (Settings menu item) ──
document.getElementById("btn-shortcuts")?.addEventListener("click", () => {
    const settingsDropdown = document.getElementById("settings-dropdown");
    if (settingsDropdown) settingsDropdown.hidden = true;
    openShortcutsDialog();
});

// ── Check for updates (Settings menu item) ──
// On phone narrow tier the toolbar Update menu is collapsed away — give the
// user an explicit, non-reloading path to trigger the update poll. The
// AppUpdater banner appears at the bottom of the WebView if a newer version
// is available; otherwise the status bar reports "up to date".
document.getElementById("btn-settings-checkupdate")?.addEventListener("click", () => {
    const settingsDropdown = document.getElementById("settings-dropdown");
    if (settingsDropdown) settingsDropdown.hidden = true;
    const statusSync = document.getElementById("status-sync");
    if (statusSync) statusSync.textContent = "Checking for updates…";
    const isAndroid = (window as any).mailxapi?.platform === "android";
    if (isAndroid) {
        // Bridge fires the C# AppUpdater.CheckForUpdate, which injects the
        // bottom banner if a newer version is on rmf39.aaz.lt/mailx.
        const f = document.createElement("iframe");
        f.style.display = "none";
        f.src = "mailxapi://checkUpdate";
        document.body.appendChild(f);
        setTimeout(() => f.remove(), 100);
        // Status hint resets after a few seconds in case nothing happens
        // (already up to date, or fetch failed silently).
        setTimeout(() => {
            if (statusSync && statusSync.textContent === "Checking for updates…") {
                statusSync.textContent = "Up to date or check pending — see banner if available";
                setTimeout(() => { if (statusSync.textContent?.startsWith("Up to date")) statusSync.textContent = ""; }, 6000);
            }
        }, 4000);
    } else {
        // Desktop: same path as the toolbar btn-update.
        const ipc = (window as any).mailxapi || (window as any).opener?.mailxapi;
        if (ipc?.performUpdate) {
            if (statusSync) statusSync.textContent = "Updating… mailx will restart when done";
            ipc.performUpdate();
        } else if (statusSync) {
            statusSync.textContent = "Update not available in this mode";
        }
    }
});

// ── About dialog ──
document.getElementById("btn-about")?.addEventListener("click", () => {
    const settingsDropdown = document.getElementById("settings-dropdown");
    if (settingsDropdown) settingsDropdown.hidden = true;
    openAboutDialog();
});
// Clicking the version string (toolbar in wide mode, status bar in narrow mode) also opens About
document.querySelectorAll<HTMLElement>(".app-version").forEach(el => {
    el.style.cursor = "pointer";
    el.addEventListener("click", openAboutDialog);
});

async function openAboutDialog(): Promise<void> {
    const backdrop = document.createElement("div");
    backdrop.className = "mailx-modal-backdrop";
    const panel = document.createElement("div");
    panel.className = "mailx-modal";
    panel.innerHTML = `
        <div class="mailx-modal-title">
            <span class="mailx-modal-title-text">About ${APP_NAME}</span>
            <button type="button" class="mailx-modal-close" id="about-x" title="Close (Esc)" aria-label="Close">&times;</button>
        </div>
        <div class="mailx-about" id="about-body">Loading...</div>
        <div class="mailx-modal-buttons">
            <span class="mailx-modal-spacer"></span>
            <button type="button" class="mailx-modal-btn mailx-modal-btn-primary" data-action="close">Close</button>
        </div>`;
    backdrop.appendChild(panel);
    document.body.appendChild(panel.parentElement!);

    const body = panel.querySelector<HTMLElement>("#about-body")!;
    const close = () => {
        backdrop.remove();
        document.removeEventListener("keydown", onKey, true);
    };
    const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") { e.stopPropagation(); e.preventDefault(); close(); }
    };
    document.addEventListener("keydown", onKey, true);
    panel.querySelector<HTMLButtonElement>('[data-action="close"]')!
        .addEventListener("click", close);
    panel.querySelector<HTMLButtonElement>("#about-x")!
        .addEventListener("click", close);
    backdrop.addEventListener("mousedown", (e) => { if (e.target === backdrop) close(); });

    try {
        const [v, accounts] = await Promise.all([
            getVersion().catch(() => ({} as any)),
            getAccounts().catch(() => [] as any[]),
        ]);
        const storage = v.storage || {};
        const isApp = typeof mailxapi !== "undefined" && mailxapi?.isApp;
        const platform = isApp ? (mailxapi?.platform || "app") : "browser";
        const versionText = v.version ? `v${v.version}` : "unknown";
        const versionHtml = v.version
            ? `<a href="https://github.com/BobFrankston/mailx/releases/tag/v${v.version}" target="_blank" rel="noopener">${versionText}</a>`
            : versionText;
        const rows: [string, string][] = [
            ["Version", versionHtml],
            ["Platform", platform],
            ["Storage", storage.provider || "local"],
        ];
        if (storage.cloudPath) rows.push(["Cloud path", `My Drive/${storage.cloudPath}/`]);
        if (storage.mode) rows.push(["Storage mode", storage.mode]);
        if (storage.folderPath) rows.push(["Drive path", storage.folderPath]);
        else if (storage.folderName) rows.push(["Drive folder", storage.folderName]);
        if (storage.folderId) rows.push(["Drive folderId", storage.folderId]);
        if (storage.folderOwner) rows.push(["Drive owner", storage.folderOwner]);
        if (storage.configDir) rows.push(["Config dir", storage.configDir]);
        rows.push(["Accounts", String((accounts || []).length)]);
        rows.push(["User agent", navigator.userAgent]);
        rows.push(["Screen", `${screen.width}×${screen.height}`]);
        rows.push(["Window", `${window.innerWidth}×${window.innerHeight}`]);

        // Version row contains an anchor tag; all other rows are plain text
        // and must be escaped. Treat row[0]==="Version" as pre-formatted HTML.
        body.innerHTML = `
            <dl class="mailx-about-dl">
              ${rows.map(([k, val]) => `<dt>${k}</dt><dd>${k === "Version" ? val : escapeHtml(val)}</dd>`).join("")}
            </dl>
            ${(accounts || []).length ? `
            <div class="mailx-about-accounts">
              <div class="mailx-about-section">Accounts</div>
              <ul>
                ${(accounts as any[]).map(a => `<li>${escapeHtml(a.email || a.id)}${a.name ? ` — ${escapeHtml(a.name)}` : ""}</li>`).join("")}
              </ul>
            </div>` : ""}
            <div class="mailx-about-foot">${APP_NAME} — local-first mail client</div>`;
    } catch (e: any) {
        body.textContent = `Failed to load: ${e.message}`;
    }
}

function escapeHtml(s: string): string {
    return String(s).replace(/[&<>"']/g, c =>
        ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

// Threaded view toggle
optThreaded?.addEventListener("change", () => {
    const body = document.getElementById("ml-body");
    if (optThreaded.checked) {
        body?.classList.add("threaded");
    } else {
        body?.classList.remove("threaded");
    }
    localStorage.setItem("mailx-threaded", String(optThreaded.checked));
    reloadCurrentFolder();
});

// Date basis (Sent / Received). message-list owns the state + persistence +
// reload; app.ts just reflects the current value into the radios and forwards
// changes. Initialize the radio to match the stored basis.
const optDateSent = document.getElementById("opt-date-sent") as HTMLInputElement | null;
const optDateReceived = document.getElementById("opt-date-received") as HTMLInputElement | null;
(function initDateBasisRadios() {
    const basis = getDateBasis();
    if (optDateSent) optDateSent.checked = basis === "sent";
    if (optDateReceived) optDateReceived.checked = basis === "received";
})();
optDateSent?.addEventListener("change", () => { if (optDateSent.checked) setDateBasis("sent"); });
optDateReceived?.addEventListener("change", () => { if (optDateReceived.checked) setDateBasis("received"); });

// Flagged-only filter — keeps the CSS-level hiding for instant feedback on
// the current page AND re-queries the folder so flagged messages that live
// outside the currently-loaded page show up.
/** Sync the search-bar ★ button's pressed state to the current
 *  `flagged-only` filter setting. Visible UI parallel to the menu
 *  checkbox so the filter is reachable without diving into the View
 *  dropdown. */
function syncFilterFlaggedButton(): void {
    const btn = document.getElementById("btn-filter-flagged");
    if (!btn) return;
    btn.classList.toggle("active", !!optFlagged?.checked);
    btn.setAttribute("aria-pressed", optFlagged?.checked ? "true" : "false");
}

function applyFlaggedFilter(on: boolean): void {
    try { logClientEvent("applyFlaggedFilter", { on, caller: new Error().stack?.split("\n").slice(2, 5).join(" | ") }); } catch { /* */ }
    if (optFlagged) optFlagged.checked = on;
    const body = document.getElementById("ml-body");
    if (on) body?.classList.add("flagged-only");
    else body?.classList.remove("flagged-only");
    localStorage.setItem("mailx-flagged", String(on));
    syncFilterFlaggedButton();
    reloadCurrentFolder();
}

optFlagged?.addEventListener("change", () => {
    applyFlaggedFilter(!!optFlagged.checked);
});

document.getElementById("btn-filter-flagged")?.addEventListener("click", () => {
    applyFlaggedFilter(!(optFlagged?.checked));
});

// Initial paint of the button's pressed state (covers the localStorage-restored
// value loaded earlier in this file).
syncFilterFlaggedButton();

// Priority-senders-only filter — same pattern as flagged-only. Adds
// .priority-only to the list body; CSS hides any row without .priority.
const optPriorityOnly = document.getElementById("opt-priority-only") as HTMLInputElement | null;
if (optPriorityOnly) {
    optPriorityOnly.checked = localStorage.getItem("mailx-priority-only") === "true";
    const body0 = document.getElementById("ml-body");
    if (optPriorityOnly.checked) body0?.classList.add("priority-only");
    optPriorityOnly.addEventListener("change", () => {
        const body = document.getElementById("ml-body");
        if (optPriorityOnly.checked) body?.classList.add("priority-only");
        else body?.classList.remove("priority-only");
        localStorage.setItem("mailx-priority-only", String(optPriorityOnly.checked));
    });
}

// "Show all messages" escape from a list that's empty only because a
// flagged-only / priority-only filter is hiding every row. The message-list
// renders an overlay with this button when a filter hides everything; here
// we honor it by clearing BOTH filters (whichever was on) and reloading.
document.addEventListener("mailx-clear-list-filters", () => {
    applyFlaggedFilter(false);
    if (optPriorityOnly?.checked) {
        optPriorityOnly.checked = false;
        document.getElementById("ml-body")?.classList.remove("priority-only");
        localStorage.setItem("mailx-priority-only", "false");
    }
    reloadCurrentFolder();
});

// Folder counts toggle
optFolderCounts?.addEventListener("change", () => {
    const tree = document.getElementById("folder-tree");
    if (optFolderCounts.checked) {
        tree?.classList.add("show-folder-counts");
    } else {
        tree?.classList.remove("show-folder-counts");
    }
    localStorage.setItem("mailx-folder-counts", String(optFolderCounts.checked));
});

// Q52: Reset column widths — clears persisted list/viewer splitter and
// restores the default CSS-var value. Currently only the list/viewer split
// is user-resizable; if per-column drag-resize lands later, add its keys to
// the cleanup list below.
document.getElementById("btn-reset-widths")?.addEventListener("click", () => {
    localStorage.removeItem("mailx-split");
    document.documentElement.style.removeProperty("--list-viewer-split");
    if (viewDropdown) viewDropdown.hidden = true;
});

// ── Settings menu ──

const settingsBtn = document.getElementById("btn-settings");
const settingsDropdown = document.getElementById("settings-dropdown");
const optEditorQuill = document.getElementById("opt-editor-quill") as HTMLInputElement;
const optEditorTiptap = document.getElementById("opt-editor-tiptap") as HTMLInputElement;
const optEditorTinymce = document.getElementById("opt-editor-tinymce") as HTMLInputElement | null;
const optTinymceCdn = document.getElementById("opt-tinymce-cdn") as HTMLInputElement | null;
// Restore TinyMCE CDN URL (Android-only path) from localStorage; the
// adapter reads it on demand. Persisted client-side because it's a
// per-device setting (Android needs CDN; desktop has npm install).
try {
    if (optTinymceCdn) optTinymceCdn.value = localStorage.getItem("mailx-tinymce-cdn") || "";
} catch { /* */ }
optTinymceCdn?.addEventListener("change", () => {
    try { localStorage.setItem("mailx-tinymce-cdn", optTinymceCdn.value.trim()); } catch { /* */ }
});

settingsBtn?.addEventListener("click", (e) => {
    e.stopPropagation();
    if (viewDropdown) viewDropdown.hidden = true;
    const restartDd = document.getElementById("restart-dropdown");
    if (restartDd) restartDd.hidden = true;
    restoreToolbarDropdown("settings-dropdown", "settings-menu");
    if (settingsDropdown) settingsDropdown.hidden = !settingsDropdown.hidden;
    refreshToolbarOverlayShield();
});
// Close handled by the shared document click handler above

// Load current editor setting from server
getSettings().then((s: any) => {
    const ed = s.ui?.editor || "quill";
    if (optEditorQuill) optEditorQuill.checked = ed === "quill";
    if (optEditorTiptap) optEditorTiptap.checked = ed === "tiptap";
    if (optEditorTinymce) optEditorTinymce.checked = ed === "tinymce";
    const cwEl = document.getElementById("opt-compose-window") as HTMLInputElement | null;
    if (cwEl) {
        const inWindow = s.ui?.composeInWindow !== false;
        cwEl.checked = inWindow;
        try { localStorage.setItem("mailx-compose-in-window", inWindow ? "1" : "0"); } catch { /* */ }
    }
    const fontEl = document.getElementById("opt-compose-font-size") as HTMLInputElement | null;
    if (fontEl) {
        const px = Number(s.ui?.composeFontSize);
        fontEl.value = String(Number.isFinite(px) && px >= 8 && px <= 32 ? px : 15);
    }
    const gcEl = document.getElementById("opt-grammar-check") as HTMLInputElement | null;
    if (gcEl) {
        const on = s.ui?.grammarCheck !== false;   // default ON
        gcEl.checked = on;
        try { localStorage.setItem("mailx-grammar-check", on ? "1" : "0"); } catch { /* */ }
    }
}).catch(() => {});

document.getElementById("opt-compose-window")?.addEventListener("change", (e) => {
    const on = (e.target as HTMLInputElement).checked;
    try { localStorage.setItem("mailx-compose-in-window", on ? "1" : "0"); } catch { /* */ }
    getSettings().then((settings: any) => {
        settings.ui = { ...settings.ui, composeInWindow: on };
        saveSettings(settings);
    }).catch(() => {});
});

// Harper grammar squiggles in compose — same dual persistence as the
// compose-window toggle: localStorage for the in-window compose iframe
// (same origin), settings.ui for the popout's boot snapshot.
document.getElementById("opt-grammar-check")?.addEventListener("change", (e) => {
    const on = (e.target as HTMLInputElement).checked;
    try { localStorage.setItem("mailx-grammar-check", on ? "1" : "0"); } catch { /* */ }
    getSettings().then((settings: any) => {
        settings.ui = { ...settings.ui, grammarCheck: on };
        saveSettings(settings);
    }).catch(() => {});
});

// Unified compose editing font size (px). Same sync-cache-then-persist shape
// as saveEditorSetting: compose.ts reads `mailx-compose-font-size` from
// localStorage at module load, so the very next compose-open is correct.
document.getElementById("opt-compose-font-size")?.addEventListener("change", (e) => {
    const el = e.target as HTMLInputElement;
    const px = Number(el.value);
    if (!Number.isFinite(px) || px < 8 || px > 32) return;
    try { localStorage.setItem("mailx-compose-font-size", String(px)); } catch { /* */ }
    getSettings().then((settings: any) => {
        settings.ui = { ...settings.ui, composeFontSize: px };
        saveSettings(settings);
    }).catch(() => {});
});

// Save editor choice to server settings
function saveEditorSetting(editor: string): void {
    // Update the localStorage cache SYNCHRONOUSLY. compose.ts reads
    // `mailx-editor-type` from localStorage at module-load to pick the
    // editor — its async getSettings() refresh only runs when a compose
    // window opens, and reads localStorage FIRST. Without this write the
    // cache stays stale and the next compose keeps the old editor until a
    // full app restart (Bob 2026-05-21: "changed to quill but got tinymce
    // until I restarted"). With it, the very next compose-open is correct.
    try { localStorage.setItem("mailx-editor-type", editor); } catch { /* private mode */ }
    getSettings().then((settings: any) => {
        settings.ui = { ...settings.ui, editor };
        saveSettings(settings);
    }).catch(() => {});
}

optEditorQuill?.addEventListener("change", () => {
    if (optEditorQuill.checked) saveEditorSetting("quill");
});
optEditorTiptap?.addEventListener("change", () => {
    if (optEditorTiptap.checked) saveEditorSetting("tiptap");
});
optEditorTinymce?.addEventListener("change", () => {
    if (!optEditorTinymce.checked) return;
    saveEditorSetting("tinymce");
    // Q133 + 2026-05-21 — warm the TinyMCE bundle into the HTTP cache so the
    // first compose-open doesn't stall downloading it. The old code used a
    // silent <link rel=prefetch> — no completion signal, so picking TinyMCE
    // looked like a frozen UI ("hang while fetching"). Now we do an explicit
    // fetch() (I/O, already off the UI thread — a Worker buys nothing for a
    // download) and surface a pending → ready indicator on the menu item.
    const cdnUrl = localStorage.getItem("mailx-tinymce-cdn") || "lib/tinymce/tinymce.min.js";
    const label = optEditorTinymce.closest("label");
    let status = document.getElementById("opt-editor-tinymce-status");
    if (label && !status) {
        status = document.createElement("span");
        status.id = "opt-editor-tinymce-status";
        status.className = "tb-menu-status";
        label.appendChild(status);
    }
    const setStatus = (text: string, state: string): void => {
        if (!status) return;
        status.textContent = text;
        status.dataset.state = state;
    };
    setStatus(" loading…", "pending");  // CSS ::before draws the spinner
    fetch(cdnUrl, { cache: "force-cache" })
        .then(r => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(`HTTP ${r.status}`))))
        .then(() => {
            setStatus(" ✓ ready", "ready");
            // Clear the badge after a few seconds — but only if still "ready"
            // (don't wipe a later pending/error state from a re-pick).
            setTimeout(() => { if (status?.dataset.state === "ready") setStatus("", "idle"); }, 4000);
        })
        .catch(e => {
            setStatus(" ⚠ load failed", "error");
            console.error("[tinymce] pre-warm fetch failed:", e?.message || e);
        });
});

// External editor preference (Edit-in-Word handoff target). Stored under
// settings.externalEditor so the service can read it via loadSettings().
// "auto" tries Word → LibreOffice → OS default; explicit values force
// that editor (still falling back to OS default if it isn't installed).
const optExtEditAuto = document.getElementById("opt-extedit-auto") as HTMLInputElement | null;
const optExtEditWord = document.getElementById("opt-extedit-word") as HTMLInputElement | null;
const optExtEditLibre = document.getElementById("opt-extedit-libre") as HTMLInputElement | null;
getSettings().then((s: any) => {
    const v = s.externalEditor || "auto";
    if (optExtEditAuto) optExtEditAuto.checked = v === "auto";
    if (optExtEditWord) optExtEditWord.checked = v === "word";
    if (optExtEditLibre) optExtEditLibre.checked = v === "libreoffice";
}).catch(() => {});
function saveExtEditor(v: "auto" | "word" | "libreoffice"): void {
    getSettings().then((settings: any) => {
        settings.externalEditor = v;
        saveSettings(settings);
    }).catch(() => {});
}
optExtEditAuto?.addEventListener("change", () => { if (optExtEditAuto.checked) saveExtEditor("auto"); });
optExtEditWord?.addEventListener("change", () => { if (optExtEditWord.checked) saveExtEditor("word"); });
optExtEditLibre?.addEventListener("change", () => { if (optExtEditLibre.checked) saveExtEditor("libreoffice"); });

// ── AI feature toggles ──
// One umbrella settings record (AutocompleteSettings) holds the provider config
// + per-feature on/off flags. All features default OFF — user must opt into
// each AI behavior individually. Per user preference (2026-04-21).
const optAutocomplete = document.getElementById("opt-autocomplete") as HTMLInputElement | null;
const optAiTranslate = document.getElementById("opt-ai-translate") as HTMLInputElement | null;
const optAiProofread = document.getElementById("opt-ai-proofread") as HTMLInputElement | null;

getAutocompleteSettings().then((ac: any) => {
    if (optAutocomplete) optAutocomplete.checked = !!ac.enabled;
    if (optAiTranslate) optAiTranslate.checked = !!ac.translateEnabled;
    if (optAiProofread) optAiProofread.checked = !!ac.proofreadEnabled;
}).catch(() => {});

function persistAi(mutator: (ac: any) => void): void {
    getAutocompleteSettings().then((ac: any) => {
        mutator(ac);
        saveAutocompleteSettings(ac);
    }).catch(() => {});
}
optAutocomplete?.addEventListener("change", () => persistAi((ac) => { ac.enabled = optAutocomplete.checked; }));
optAiTranslate?.addEventListener("change", () => persistAi((ac) => { ac.translateEnabled = optAiTranslate.checked; }));
optAiProofread?.addEventListener("change", () => {
    persistAi((ac) => { ac.proofreadEnabled = optAiProofread.checked; });
    // Mirror to localStorage so the compose editor (separate page/iframe with
    // its own getSettings cycle) can read it synchronously.
    try { localStorage.setItem("mailx-ai-proofread-enabled", String(optAiProofread.checked)); } catch { /* */ }
});

// Sender reputation check (Spamhaus DBL). Stored at top-level settings so
// the service can read it cheaply without going through autocomplete config.
// Off by default — enabling it leaks read-recipient domains to Spamhaus's
// DNS infra, which the user should opt into knowingly.
const optCheckReputation = document.getElementById("opt-check-reputation") as HTMLInputElement | null;
getSettings().then((s: any) => {
    if (optCheckReputation) optCheckReputation.checked = !!s.checkDomainReputation;
}).catch(() => {});
optCheckReputation?.addEventListener("change", () => {
    getSettings().then((settings: any) => {
        settings.checkDomainReputation = !!optCheckReputation.checked;
        saveSettings(settings);
    }).catch(() => {});
});

// Auto mark-as-read settings (per-device localStorage; the viewer reads
// these directly when showing a message). Default on with a 2s delay so
// scrolling through a folder doesn't mark every glanced-at message as
// read, but a deliberate read still gets recorded.
const optAutomarkRead = document.getElementById("opt-automark-read") as HTMLInputElement | null;
const optAutomarkDelay = document.getElementById("opt-automark-delay") as HTMLInputElement | null;
try {
    if (optAutomarkRead) optAutomarkRead.checked = localStorage.getItem("mailx-automark-read") !== "false";
    if (optAutomarkDelay) optAutomarkDelay.value = localStorage.getItem("mailx-automark-delay") || "2";
} catch { /* private mode */ }
optAutomarkRead?.addEventListener("change", () => {
    try { localStorage.setItem("mailx-automark-read", String(optAutomarkRead.checked)); } catch { /* */ }
});
optAutomarkDelay?.addEventListener("change", () => {
    const v = parseFloat(optAutomarkDelay.value);
    if (Number.isFinite(v) && v >= 0) {
        try { localStorage.setItem("mailx-automark-delay", String(v)); } catch { /* */ }
    }
});

// ── Version display ──
declare const mailxapi: { isApp: boolean; platform: string; ensureServer: () => Promise<boolean>; getVersion: () => Promise<any> } | undefined;
const isApp = typeof mailxapi !== "undefined" && mailxapi?.isApp;

// Wait for server ready signal, then fetch version. getVersion is idempotent
// and trivial; msger occasionally drops the startup IPC (see api-client note),
// so retry with backoff rather than leaving "(??)" + a "service error" banner
// up forever for a cosmetic version read (Bob 2026-06-02). A short per-action
// timeout (mailxapi.js) makes each failed attempt give up in 5s.
async function getVersionWithRetry(attempts = 5): Promise<any> {
    for (let i = 0; i < attempts; i++) {
        try { return await getVersion(); }
        catch (e) {
            if (i === attempts - 1) { console.warn("getVersion failed after retries:", e); return {}; }
            await new Promise(r => setTimeout(r, 1500));
        }
    }
    return {};
}
const versionPromise = getVersionWithRetry();
versionPromise.then((d: any) => {
    if (!d || !d.version) return;   // all retries dropped — leave the prior text, no scary banner
    const els = document.querySelectorAll<HTMLElement>(".app-version");
    const storage = d.storage || {};
    const storageLabel = storage.provider && storage.provider !== "local"
        ? ` [${storage.provider}]`
        : "";
    // Toolbar real estate is tight; the app icon already conveys identity.
    // Show just the version + storage tag (and "[browser]" tag when running
    // in a plain browser tab vs. msger / MAUI shell).
    const text = `v${d.version}${storageLabel}${isApp ? "" : " [browser]"}`;
    const tip = storage.provider && storage.provider !== "local"
        ? (storage.cloudPath ? `My Drive/${storage.cloudPath}/` : storage.provider)
        : "";
    for (const el of els) {
        el.textContent = text;
        if (tip) el.title = tip;
    }
    if (d.settingsError) {
        showAlert(d.settingsError, "settings-error");
        // Add repair button to the banner
        const banner = document.getElementById("alert-banner");
        if (banner && !banner.querySelector(".repair-btn")) {
            const btn = document.createElement("button");
            btn.className = "repair-btn status-action";
            btn.textContent = "Repair: restore accounts from cache";
            btn.style.cssText = "margin-left:1rem;padding:0.25rem 0.75rem;background:#a6e3a1;color:#1e1e2e;border:none;border-radius:4px;cursor:pointer;font-weight:bold";
            btn.onclick = async () => {
                btn.textContent = "Restoring...";
                btn.disabled = true;
                try {
                    const data = await repairAccounts();
                    if (data.ok) {
                        hideAlert();
                        setTimeout(() => location.reload(), 1000);
                    } else {
                        btn.textContent = `Failed: ${data.error}`;
                    }
                } catch (e: any) {
                    btn.textContent = `Error: ${e.message}`;
                }
            };
            banner.querySelector("#alert-text")?.after(btn);
        }
    } else if (storage.cloudError) {
        showAlert(`Cloud storage error: ${storage.cloudError}`, "cloud-error");
    }
}).catch((e: any) => {
    // Version fetch failed
    const els = document.querySelectorAll<HTMLElement>(".app-version");
    const text = isApp ? `[version error: ${e.message}]` : `[server offline]`;
    for (const el of els) el.textContent = text;
});

// ── Sync pending indicator + server health check (HTTP mode only) ──
let serverDown = false;
if (isApp) {
    // IPC mode: events come via push, no polling needed
} else
setInterval(async () => {
    try {
        const data = await getSyncPending();
        const el = document.getElementById("status-pending");
        if (el) {
            el.textContent = data.pending > 0 ? `↻ ${data.pending} pending` : "";
            el.style.color = data.pending > 0 ? "oklch(0.75 0.15 60)" : "";
        }
        // Server is back — reload if it was down
        if (serverDown) {
            serverDown = false;
            const statusEl = document.getElementById("status-sync");
            if (statusEl) statusEl.textContent = "Server reconnected";
            location.reload();
        }
    } catch {
        if (!serverDown) {
            serverDown = true;
            const statusEl = document.getElementById("status-sync");
            if (statusEl) {
                statusEl.textContent = "SERVER OFFLINE";
                statusEl.style.color = "oklch(0.65 0.2 25)";
            }
        }
    }
}, 5000);

// ── Outbox queue indicator (status-queue span) ──
// Event-driven in IPC mode (service pushes outboxStatus on every mutation).
// Plus a 15s poll safety net for both modes so a missed event doesn't leave
// the user staring at stale numbers. Idempotent — renderOutboxStatus just
// overwrites the text.
function renderOutboxStatus(s: any): void {
    // Feed the folder-tree synthesized "Send-pending" row. Idempotent —
    // it no-ops when the presence state and count haven't changed.
    setOutboxTotal(s?.total || 0);
    const el = document.getElementById("status-queue");
    if (!el) return;
    if (!s || !s.total || s.total === 0) {
        el.textContent = "";
        el.title = "";
        el.style.color = "";
        return;
    }
    const parts: string[] = [`✉ ${s.total} queued`];
    if (s.claimed > 0) parts.push(`${s.claimed} sending`);
    if (s.retrying > 0) parts.push(`${s.retrying} retrying (×${s.maxAttempts})`);
    if (s.oldestAgeSec >= 60) {
        const age = s.oldestAgeSec >= 3600
            ? `${Math.floor(s.oldestAgeSec / 3600)}h`
            : `${Math.floor(s.oldestAgeSec / 60)}m`;
        parts.push(`oldest ${age}`);
    }
    el.textContent = parts.join(" · ");
    const perAcct = s.perAccount || {};
    const detail = Object.keys(perAcct).sort().map(a =>
        `${a}: ${perAcct[a].total} total, ${perAcct[a].claimed} sending, ${perAcct[a].retrying} retrying`
    ).join("\n");
    el.title = detail || "";
    // Orange when retrying, red when stuck >5min, else muted.
    el.style.color = s.oldestAgeSec > 300 ? "oklch(0.65 0.2 25)"
        : s.retrying > 0 ? "oklch(0.75 0.15 60)"
        : "";
}

setInterval(async () => {
    try {
        const { getOutboxStatus, getDiagnostics } = await import("./lib/api-client.js");
        // Run in parallel — neither call depends on the other and each is a
        // separate IPC round-trip. Earlier code awaited them serially.
        const [outbox, diag] = await Promise.all([getOutboxStatus(), getDiagnostics()]);
        renderOutboxStatus(outbox);
        renderDiagnosticsBadge(diag);
    } catch { /* service unreachable */ }
}, 15000);
// First read on startup so the bar isn't blank.
(async () => {
    try {
        const { getOutboxStatus, getDiagnostics } = await import("./lib/api-client.js");
        const [outbox, diag] = await Promise.all([getOutboxStatus(), getDiagnostics()]);
        renderOutboxStatus(outbox);
        renderDiagnosticsBadge(diag);
    } catch { /* */ }
})();

/** Render the ⚠ "something's wrong" badge next to status-sync. Shown when
 *  any account has non-zero diagnostic counters (inactivity timeouts,
 *  connection-cap hits, rate-limit waits). Tooltip breaks down per-account. */
function renderDiagnosticsBadge(snapshot: any[]): void {
    const host = document.getElementById("status-diag");
    if (!host) return;
    const issues = (snapshot || []).filter(d => d.inactivityTimeouts > 0 || d.connCapHits > 0 || d.rateLimitWaits > 0);
    if (issues.length === 0) {
        host.hidden = true;
        host.textContent = "";
        host.title = "";
        return;
    }
    host.hidden = false;
    host.textContent = "⚠";
    const totalTimeouts = issues.reduce((a, d) => a + d.inactivityTimeouts, 0);
    const totalCapHits = issues.reduce((a, d) => a + d.connCapHits, 0);
    const totalRateLimits = issues.reduce((a, d) => a + d.rateLimitWaits, 0);
    const summary = [
        totalTimeouts > 0 ? `${totalTimeouts} IMAP inactivity timeout${totalTimeouts === 1 ? "" : "s"}` : null,
        totalCapHits > 0 ? `${totalCapHits} conn-cap rejection${totalCapHits === 1 ? "" : "s"}` : null,
        totalRateLimits > 0 ? `${totalRateLimits} rate-limit wait${totalRateLimits === 1 ? "" : "s"}` : null,
    ].filter(Boolean).join("; ");
    const detail = issues.map(d => {
        const parts = [
            d.inactivityTimeouts > 0 ? `${d.inactivityTimeouts} timeout${d.inactivityTimeouts === 1 ? "" : "s"}` : null,
            d.connCapHits > 0 ? `${d.connCapHits} conn-cap` : null,
            d.rateLimitWaits > 0 ? `${d.rateLimitWaits} rate-limit` : null,
        ].filter(Boolean).join(", ");
        const last = d.lastCommand ? `\n  last: ${d.lastCommand}` : "";
        return `${d.accountId}: ${parts}${last}`;
    }).join("\n");
    host.title = `Connection issues — ${summary}\n\n${detail}`;
}
// Q64/C44: pop-out message actions. Fired by the floating-overlay popout's
// toolbar (message-viewer.ts can't import openCompose directly) AND by the
// native popout window via the `popoutAction` daemon event above. Reply /
// Reply All / Forward act on the popped-out message specifically (not the
// main viewer's), Delete removes it.
document.addEventListener("mailx-popout-action", ((e: any) => {
    const { action, msg, accountId } = e.detail || {};
    if (!msg) return;
    if (action === "reply" || action === "replyAll" || action === "forward" || action === "editAsNew") {
        openCompose(action as ComposeMode, msg, accountId);
    } else if (action === "delete") {
        deleteMessage(accountId, msg.uid).catch((err: any) =>
            console.error(`[popout] delete failed: ${err?.message || err}`));
    } else if (action === "flag") {
        const wasFlagged = flaggedOf(msg);
        setFlagged(msg, !wasFlagged);
        updateFlags(accountId, msg.uid, msg.flags, msg.folderId).catch((err: any) =>
            console.error(`[popout] flag failed: ${err?.message || err}`));
        setRowFlagged(accountId, msg.uid, !wasFlagged);
    }
}) as EventListener);

document.addEventListener("mailx-popout-message", (async (e: any) => {
    const { accountId, uid, folderId, subject } = e.detail || {};
    if (!accountId || !uid) return;
    const { getMessage } = await import("./lib/api-client.js");
    let msg: any;
    try {
        msg = await getMessage(accountId, uid, false, folderId);
    } catch (err: any) {
        alert(`Couldn't load message: ${err?.message || err}`);
        return;
    }
    // Drafts pop out into a COMPOSE window, not a read-only viewer popout.
    // Bob 2026-05-12: "popping out a draft should default to that" (edit
    // mode). Flag detection: server-side \Draft flag, or the message is
    // currently sitting in the user's Drafts folder. The read-only popout
    // surface is missing every action button (Reply, Forward, Edit Draft,
    // …) so dumping a draft into it is a worst-case dead-end.
    const isDraft = !!msg && draftOf(msg);
    if (isDraft) {
        const accts = await getAccounts();
        const init = {
            mode: "draft",
            accountId,
            to: msg.to || [],
            cc: msg.cc || [],
            bcc: msg.bcc || [],
            subject: msg.subject || subject || "",
            bodyHtml: msg.bodyHtml || "",
            inReplyTo: msg.inReplyTo || "",
            references: msg.references || [],
            accounts: accts.map((a: any) => ({ id: a.id, name: a.name, email: a.email, signature: a.signature, sig: a.sig })),
            draftUid: msg.uid,
            draftFolderId: msg.folderId,
            draftId: msg.mailxDraftId || "",
        };
        sessionStorage.setItem("composeInit", JSON.stringify(init));
        showComposeOverlay(msg.subject ? `Edit: ${msg.subject}` : "Edit draft");
        return;
    }
    const wrapper = document.createElement("div");
    wrapper.className = "popout-overlay";
    wrapper.style.cssText = "position:fixed;top:5vh;right:5vw;width:min(900px,60vw);height:min(800px,80vh);z-index:1500;background:var(--color-bg);border:1px solid var(--color-border);border-radius:6px;box-shadow:0 8px 32px rgba(0,0,0,0.3);display:flex;flex-direction:column;resize:both;overflow:hidden;";
    const header = document.createElement("div");
    header.style.cssText = "display:flex;align-items:center;gap:8px;padding:8px 12px;background:var(--color-bg-surface);border-bottom:1px solid var(--color-border);font-weight:600;cursor:move;";
    const title = document.createElement("span");
    title.textContent = subject || "(no subject)";
    title.style.cssText = "flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;";
    const closeBtn = document.createElement("button");
    closeBtn.textContent = "×";
    closeBtn.style.cssText = "background:none;border:none;font-size:1.4rem;cursor:pointer;padding:0 8px;";
    closeBtn.addEventListener("click", () => wrapper.remove());
    header.appendChild(title);
    header.appendChild(closeBtn);
    const meta = document.createElement("div");
    meta.style.cssText = "padding:8px 12px;border-bottom:1px solid var(--color-border);font-size:0.9rem;color:var(--color-text-muted);";
    meta.innerHTML = `<div><b>From:</b> ${escapeHtmlBasic(msg.from?.name || "")} &lt;${escapeHtmlBasic(msg.from?.address || "")}&gt;</div>
                      <div><b>To:</b> ${(msg.to || []).map((a: any) => escapeHtmlBasic(`${a.name||""} <${a.address}>`)).join(", ")}</div>
                      ${msg.cc?.length ? `<div><b>Cc:</b> ${msg.cc.map((a:any) => escapeHtmlBasic(`${a.name||""} <${a.address}>`)).join(", ")}</div>` : ""}
                      <div><b>Date:</b> ${new Date(msg.date).toLocaleString()}</div>`;
    const body = document.createElement("iframe");
    body.style.cssText = "flex:1;border:none;width:100%;background:#fff;";
    body.sandbox.add("allow-same-origin");
    wrapper.appendChild(header);
    wrapper.appendChild(meta);
    wrapper.appendChild(body);
    document.body.appendChild(wrapper);
    // Use the same wrapHtmlBody styling as the inline preview pane so the
    // popout's typography matches the preview's typography. Earlier the
    // popout fed bodyHtml DIRECTLY into srcdoc with no CSS wrapping, so
    // the browser used its default font (Times serif) while the preview
    // pane used system-ui sans-serif (Bob 2026-05-12: "double clicking on
    // the summary shows in a different font than in preview").
    body.srcdoc = msg.bodyHtml
        ? wrapHtmlBody(msg.bodyHtml, !!msg.remoteAllowed)
        : `<!DOCTYPE html><html><head><style>body{font-family:system-ui,sans-serif;font-size:17.5px;line-height:1.5;color:#1a1a2e;padding:1rem;margin:0;}pre{white-space:pre-wrap;word-break:break-word;font-family:inherit;font-size:inherit;margin:0;}</style></head><body><pre>${escapeHtmlBasic(msg.bodyText || "(no body)")}</pre></body></html>`;
    // Drag-to-move.
    let dragX = 0, dragY = 0, dragging = false;
    header.addEventListener("mousedown", (de: MouseEvent) => {
        if ((de.target as HTMLElement).tagName === "BUTTON") return;
        dragging = true;
        const rect = wrapper.getBoundingClientRect();
        dragX = de.clientX - rect.left;
        dragY = de.clientY - rect.top;
        de.preventDefault();
    });
    document.addEventListener("mousemove", (de) => {
        if (!dragging) return;
        wrapper.style.left = `${de.clientX - dragX}px`;
        wrapper.style.top = `${de.clientY - dragY}px`;
        wrapper.style.right = "auto";
    });
    document.addEventListener("mouseup", () => { dragging = false; });
}) as EventListener);
function escapeHtmlBasic(s: string): string {
    return (s || "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;" }[c]!));
}

// Click the status-queue pill to open the outbox view (pink-row list).
document.getElementById("status-queue")?.addEventListener("click", async () => {
    try {
        const { openOutboxView } = await import("./components/outbox-view.js");
        openOutboxView();
    } catch (e: any) {
        console.error("Outbox view failed:", e);
    }
});
// Make it look clickable.
(() => {
    const el = document.getElementById("status-queue");
    if (el) { el.style.cursor = "pointer"; el.title = "Click to view queued messages"; }
})();

console.log("mailx client initialized, location:", location.href);
updateNewMessageCount();

// Offline indicator — show/hide based on navigator.onLine. Doesn't gate any
// functionality (the store is local-first; edits queue and replay on
// reconnect regardless) but tells the user their queued actions are stacking
// up for a later push rather than hitting the server now.
const offlineEl = document.getElementById("status-offline");
function refreshOfflineIndicator(): void {
    if (!offlineEl) return;
    offlineEl.hidden = navigator.onLine;
}
window.addEventListener("online", refreshOfflineIndicator);
window.addEventListener("offline", refreshOfflineIndicator);
refreshOfflineIndicator();

// ── Midnight refresh — update date display when day changes ──
function scheduleMiddnightRefresh(): void {
    const now = new Date();
    const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const ms = midnight.getTime() - now.getTime();
    setTimeout(() => {
        reloadCurrentFolder();
        scheduleMiddnightRefresh();
    }, ms + 1000); // 1s after midnight
}
scheduleMiddnightRefresh();

// ── Apply theme from settings ──
versionPromise.then((d: any) => {
    if (d.theme === "dark") document.documentElement.classList.add("theme-dark");
    else if (d.theme === "light") document.documentElement.classList.add("theme-light");
}).catch(() => {});

// ── Save window geometry on close (IPC mode only) ──
// Sends window position and size so the next launch restores them.
if (isApp) {
    const ipcApi = (window as unknown as Record<string, unknown>).mailxapi as
        { saveWindowGeometry?: (g: { x: number; y: number; width: number; height: number }) => Promise<unknown> } | undefined;

    function sendGeometry(): void {
        if (!ipcApi?.saveWindowGeometry) return;
        ipcApi.saveWindowGeometry({
            x: window.screenX,
            y: window.screenY,
            width: window.outerWidth,
            height: window.outerHeight,
        }).catch(() => { /* fire-and-forget */ });
    }

    // Save on unload (window close) and periodically as a safety net
    window.addEventListener("beforeunload", sendGeometry);
    setInterval(sendGeometry, 60_000);
}

// Boot-snapshot writer — captures key DOM regions to localStorage so the
// next cold start hydrates instantly (see index.html's hydrateFromBootSnapshot).
// Saves every 30 s and on beforeunload; pages with no inbox open just save
// empty strings, which the loader treats as "skip this region."
function saveBootSnapshot(): void {
    try {
        const folderTree = document.getElementById("folder-tree");
        const messageList = document.getElementById("ml-body");
        const folderTitle = document.getElementById("ml-folder-title");
        // Only save when at least one region has content. An in-flight render
        // (e.g. user clicked a folder, list is empty for a tick) would
        // otherwise clobber the previous good snapshot.
        const ftHtml = folderTree?.innerHTML?.trim() || "";
        // Search is ephemeral: never snapshot the list while a search is
        // showing, or a cold start would repaint stale search results before
        // the real folder/inbox loads (Bob 2026-06-26). Empty string = the
        // loader skips this region and the normal boot load fills it.
        const searching = (searchInput?.value.trim() || "") !== "";
        const mlHtml = searching ? "" : (messageList?.innerHTML?.trim() || "");
        if (!ftHtml && !mlHtml) return;
        const snap = {
            folderTree:  ftHtml,
            messageList: mlHtml,
            folderTitle: searching ? "" : (folderTitle?.innerHTML?.trim() || ""),
            savedAt: Date.now(),
        };
        localStorage.setItem("mailx-boot-snapshot", JSON.stringify(snap));
    } catch { /* best-effort */ }
}
window.addEventListener("beforeunload", saveBootSnapshot);
// Also save when the user tabs away — a Windows kill of an inactive
// app would otherwise lose the snapshot up to 30 s old.
document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") saveBootSnapshot();
});
setInterval(saveBootSnapshot, 30_000);
